import { filter, takeUntil } from 'rxjs/operators';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    NgForm,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AddInvitation, IAddInvitation } from '@routes/events/models/invitationlist-model';
import {
    AddCustomField,
    AddCustomFieldFile,
    AddCustomFieldOption,
    Guest,
    IAddCustomField,
    IAddCustomFieldFile,
    IAddCustomFieldOption,
    ICustomField,
    ICustomFieldFile,
    IGuest,
    IRegistration,
    Registration,
} from '@routes/events/models/registrationpage-model';
import { ErrorInfo, IErrorInfo } from '@shared/components/modal-form-host/error-info';
import { ModalActionStatus } from '@shared/components/modal-form-host/modal-action-status';
import { ModalButtonSet } from '@shared/components/modal-form-host/modal-buttons';
import { ModalFormHostStateService } from '@shared/components/modal-form-host/modal-form-host-state.service';
import {
    ModalFormHostService,
} from '@shared/components/model-form-host-ex/modal-form-host-service/modal-form-host.service';
import {
    CustomFieldType,
    RegistrationPageType,
    StandardFieldType,
    TypeOfTimeSlot,
} from '@shared/enums/CustomFieldType';
import { FreeTextFormat } from '@shared/enums/FreeTextFormat';
import { DropdownModel } from '@shared/models/dropdown-model';
import { DropdownVenueModel, DropdownVenueModelSegments } from '@shared/models/dropdown-venue-model';
import {
    IAttendeeDetails,
    ICustomFieldEdit,
    ICustomFieldFiles,
    IGuestDetails,
    IInvitationContactDetails,
    IInvitationRegistrationPage,
    IInvitationStatus,
} from '@shared/services/api/legacy/invitations-service/invitations-model';
import { InvitedeskApiService } from '@shared/services/api/legacy/invitedesk-api.service';
import { ILanguage } from '@shared/services/api/legacy/languages-service/languages-model';
import { IPermission } from '@shared/services/api/legacy/settings-service/setting-model';
import { IAppState } from '@state/reducers/app/app.reducer';
import { IState } from '@state/state.module';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CheckboxFormat } from '@shared/enums/CheckboxFormat';
import {
    CalendarLocales,
    Countries,
    DefaultLanguage,
    MaxUploadedFileSizeRegistrationPage,
    SetDateBasedOnTimeString,
    SetStringTimeValueBasedOnTimePickerValue,
    SetUtcDateValueBasedOnDatePickerValue,
    UploadOptionsRegistrationPage,
} from '@shared/constants/constants';
import { ICalendarLocales } from '@shared/models/calender-locales-model';
import { TranslationService } from '@shared/services/translation/translation.service';
import { DateTimeRange, IFieldConfiguration } from '@routes/events/models/eventpage-model';
import * as moment from 'moment';
import * as countries from 'i18n-iso-countries';
import { IEvent } from '@shared/services/api/dashboard/events-service/events-model';
import { SelectFormat } from '@shared/enums/SelectFormat';
import { INgxSelectOptions } from 'ngx-select-ex';
import { UploadFile, UploadInput, UploadOutput, UploadStatus } from 'ngx-uploader';
import { FileService } from '@shared/services/api/dashboard/file-service/file.service';
import { Guid } from '../guid/guid';
import { MultiVenueType } from '@shared/enums/MultiVenueType';
import { MaxVenueType } from '@shared/enums/MaxVenueType';
import { v4 as uuid } from 'uuid';

interface UploadFieldFileMap { [fieldId: string]: UploadFile[] }
interface UploadFieldDragOverMap { [fieldId: string]: boolean }
interface UploadFieldInputMap { [fieldId: string]: EventEmitter<UploadInput> }
interface UploadFieldValidMap { [fieldId: string]: boolean }
interface ExistingFieldFileMap { [fieldId: string]: ICustomFieldFiles[] }

interface GuestUploadFieldFileMap { [guestId: string]: UploadFieldFileMap }
interface GuestUploadFieldDragOverMap { [guestId: string]: UploadFieldDragOverMap }
interface GuestUploadFieldInputMap { [guestId: string]: UploadFieldInputMap }
interface GuestUploadFieldValidMap { [guestId: string]: UploadFieldValidMap }
interface GuestExistingFieldFileMap { [guestId: string]: ExistingFieldFileMap }

@Component({
  selector: 'app-edit-registration-form',
  templateUrl: './edit-registration-form.component.html',
  styleUrls: ['./edit-registration-form.component.scss']
})
export class EditRegistrationFormComponent implements OnInit, OnDestroy {
  constructor(
    private state: ModalFormHostStateService,
    private modalService: ModalFormHostService,
    private modalStateService: ModalFormHostStateService,
    private invitedeskApi: InvitedeskApiService,
    private fb: UntypedFormBuilder,
    private translation: TranslateService,
    private store: Store<IState>,
    private translationService: TranslationService,
    private fileService: FileService
  ) { }

  public registrationForm: UntypedFormGroup;

  private modalRef: BsModalRef;
  private state$: Observable<IAppState>;
  private destroy$ = new Subject();
  public buttonSets = ModalButtonSet;
  public actionProgress = ModalActionStatus.Iddle;

  private userLanguageId: string;
  private currentLang: string;
  public tenantSettings: IPermission;

  public showVenueDropdown = false;
  public showGuestDropdown = false;

  private isSubmittingAccept = false;
  private isSubmittingEdit = false;
  private isSubmittingDecline = false;

  private customFieldsInvitee = 0;
  private customFieldsGuest = 0;

  private locationDateTimeId = '';

  public soldOut = false;
  public isClosed = false;

  // Locale
  public calendarLocales: ICalendarLocales = CalendarLocales(DefaultLanguage);

  // Current amount of guests, including the invitee, only applicable when there is at least one guest input field
  public currentGuests = 1;

  // Max amount of guests, including the invitee, used in add guest and guest dropdown
  public maxGuests = 1;

  // Index of the dropdown to select an amount of guests when there are no input fields for guests
  private selectedNumberOfGuests = 0;

  // Check when guests have no name input field
  private guestsAnonymous = false;

  // Multivenue configuration
  public isMultiVenue: boolean;
  public multiVenueType: MultiVenueType;
  public maxVenues: number;
  public maxVenueType: MaxVenueType;

  // Stored multivenue locationDateTimeIds per registration flow
  public locationDateTimeIdsPerRegistration: string[] = [];
  // Stored multivenue locationDateTimeIds per attendee flow
  public multiVenueSelectionPerAttendee: {[key: string]: string[]} = {};

  public previousSelectedOption = [];
  public previousVenueSelectionTicketAvailable = null;
  public previousSelectedVenueAvailableTickets = null;
  public previousSelectedVenueIdCustomFieldRouting = null;
  public unvalidatedGuestsList: string[] = [];
  public guestNotValid: boolean;

  public customFieldOptionsMultiVenue = [];
  public customFieldOptionsItems = [];

  // Attendee details copy
  public copiedAttendeeDetails: IAttendeeDetails;

  public defaultVenue: any[];

  // Dropdown items
  private itemsEvents: DropdownVenueModel[] = [];
  private itemsGuests: DropdownModel[] = [];
  public itemsCountries = [];

  public customFieldType = CustomFieldType;
  public freeTextFormats = FreeTextFormat;
  public selectFormat = SelectFormat;

  public sendConfirmationMail = false;

  public fieldsForRegistrationForm = [];

  public hiddenInviteeFields = [];
  // array of arrays for each guest [[1,4], [1,2,3]]
  public hiddenGuestsFields = [];

  public revertHiddenFieldIndex = null;

  public doubleGoTo = [{ }];

  public fieldKeys = ['id', 'firstName', 'lastNamePrefix', 'lastName', 'email', 'mobile', 'jobTitle', 'customerNumber'];

  public addressFields = ['addressLineOne', 'addressLineTwo', 'city', 'postalCode', 'country'];

  // arrays of  disabled option for single select by every quests with  1 default invitee object
  public multiDisabledOptions = [];

  public currentShowedFields = [[ ]];

  public selectedOptionsByVenue = { };

  public previousSingleSelectValues = [{ }];

  public previousSelectedVenueId;
  public hiddenFieldIndexesPerVenue = [];
  public showedFieldIndexesPerVenue = [];

  // Define hidden and showed fields for multivenue per attendee case
  public hiddenFieldIndexesPerGuestMultiVenue = [];
  public showedFieldIndexesPerGuestMultiVenue = [];

  public locationDateTimeField = {
    id: 'locationDateTimeFieldId',
    order: -1,
    customFieldOptions: [],
    optionsGoToQuestion: []
  };

  public selectOptions: INgxSelectOptions = {
      optionValueField: 'id',
      optionTextField: 'text',
      keepSelectedItems: false // The default value
  };

  public uploadOptions = UploadOptionsRegistrationPage();

  private inviteeFiles: UploadFieldFileMap = {};
  private inviteeDragOver: UploadFieldDragOverMap = {};
  private inviteeUploadInputs: UploadFieldInputMap = {};
  private inviteeValidMap: UploadFieldValidMap = {};
  private inviteeExistingFiles: ExistingFieldFileMap = {};

  private guestFiles: GuestUploadFieldFileMap = {};
  private guestDragOver: GuestUploadFieldDragOverMap = {};
  private guestUploadInputs: GuestUploadFieldInputMap = {};
  private guestValidMap: GuestUploadFieldValidMap = {};
  private guestExistingFiles: GuestExistingFieldFileMap = {};

  private uploading: boolean;

  public submitClicked = false;

  @ViewChild(NgForm, { static: true })
  set form(value: NgForm) { this.form$.next(value); }
  get form(): NgForm { return this.form$.value; }
  public form$: BehaviorSubject<NgForm> = new BehaviorSubject<NgForm>(undefined);

  public modal$: BehaviorSubject<BsModalRef> = new BehaviorSubject<BsModalRef>(undefined);
  get modal(): BsModalRef { return this.modal$.value; }
  set modal(value: BsModalRef) { this.modal$.next(value); }

  get model(): IAddInvitation { return this.model$.value; }
  set model(value: IAddInvitation) { this.model$.next(value); }
  public model$: BehaviorSubject<IAddInvitation> = new BehaviorSubject<IAddInvitation>(new AddInvitation(null, null, [], []));

  @Input()
  get status(): ModalActionStatus { return this.status$.value; }
  set status(value: ModalActionStatus) { this.status$.next(value); }
  public status$: BehaviorSubject<ModalActionStatus> = new BehaviorSubject<ModalActionStatus>(ModalActionStatus.Iddle);

  @Input()
  get error(): IErrorInfo { return this.error$.value; }
  set error(value: IErrorInfo) { this.error$.next(value); }
  public error$ = new BehaviorSubject<IErrorInfo>(new ErrorInfo(''));

  @Input()
  get invitationId(): string { return this.invitationId$.value; }
  set invitationId(value: string) { this.invitationId$.next(value); }
  public invitationId$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);

  @Input()
  get invitationStatus(): IInvitationStatus { return this.invitationStatus$.value; }
  set invitationStatus(value: IInvitationStatus) { this.invitationStatus$.next(value); }
  public invitationStatus$: BehaviorSubject<IInvitationStatus> = new BehaviorSubject<IInvitationStatus>(undefined);

  @Input()
  get invitationContact(): IInvitationContactDetails { return this.invitationContact$.value; }
  set invitationContact(value: IInvitationContactDetails) { this.invitationContact$.next(value); }
  public invitationContact$: BehaviorSubject<IInvitationContactDetails> = new BehaviorSubject<IInvitationContactDetails>(undefined);

  @Input()
  get invitationRegistrationPage(): IInvitationRegistrationPage { return this.invitationRegistrationPage$.value; }
  set invitationRegistrationPage(value: IInvitationRegistrationPage) { this.invitationRegistrationPage$.next(value); }
  public invitationRegistrationPage$: BehaviorSubject<IInvitationRegistrationPage> = new BehaviorSubject<IInvitationRegistrationPage>(undefined);

  @Input()
  get invitationAttendees(): IAttendeeDetails { return this.invitationAttendees$.value; }
  set invitationAttendees(value: IAttendeeDetails) { this.invitationAttendees$.next(value); }
  public invitationAttendees$: BehaviorSubject<IAttendeeDetails> = new BehaviorSubject<IAttendeeDetails>(undefined);

  @Input()
  get eventLanguages(): ILanguage[] { return this.eventLanguages$.value; }
  set eventLanguages(value: ILanguage[]) { this.eventLanguages$.next(value); }
  public eventLanguages$: BehaviorSubject<ILanguage[]> = new BehaviorSubject<ILanguage[]>([]);

  @Input()
  get eventDetails(): IEvent { return this.eventDetails$.value; }
  set eventDetails(value: IEvent) { this.eventDetails$.next(value); }
  public eventDetails$: BehaviorSubject<IEvent> = new BehaviorSubject<IEvent>(undefined);

  @Output()
  public formSubmitted = new EventEmitter<IRegistration>();

  @Output()
  public modalActionStatusChanged = new EventEmitter<ModalActionStatus>();

  public ngOnInit() {
    // this.state.form = this.registrationForm;
    this.form$.pipe(
      takeUntil(this.destroy$),
      filter((x) => !!x),)
      .subscribe(x => {
        this.state.form = x;
      });

    this.state$ = this.store.select(x => x.app);
    this.state$.pipe(
      takeUntil(this.destroy$))
      .subscribe(x => {
        this.userLanguageId = x.profile.languageId;
        this.currentLang = x.profile.languageId;
        this.tenantSettings = x.environment.tenantSettings;

        // Set locale
        this.calendarLocales = CalendarLocales(x.profile.localeId);

        if (this.eventLanguages && this.eventLanguages.length && this.userLanguageId) {
          if (this.eventLanguages.findIndex((el) => el.languageId === this.userLanguageId) === -1) {
            this.userLanguageId = this.eventLanguages.find((el) => el.isDefault).languageId;
          }
        }
      });

    // Set multi venue configuration
    if(this.eventDetails && this.eventDetails.multiVenueConfiguration){
      this.isMultiVenue = this.eventDetails.multiVenueConfiguration.isMultiVenue;
      this.multiVenueType = this.eventDetails.multiVenueConfiguration.multiVenueType;
      this.maxVenues = this.eventDetails.multiVenueConfiguration.maxVenues;
      this.maxVenueType = this.eventDetails.multiVenueConfiguration.maxVenueType;
    }

    // Set default locationdatetime to first venue in list
    // This is a fallback, removing this will result in undefined when every venue is soldout and has no overbooking
    if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      this.locationDateTimeId = this.invitationStatus.locationDateTimes[0].id;
      this.previousSelectedVenueId = this.invitationStatus.locationDateTimes[0].id;
    }

    // Set default venue to not soldout or unlimited overbooking and has not surpassed the current date
    if (this.invitationStatus && this.invitationStatus.locationDateTimes) {
      if (this.invitationStatus.locationDateTimes.find((x) => (!x.isSoldOut || x.overBooking === 1) && new Date(x.fromDate) > new Date())) {
        this.locationDateTimeId = this.invitationStatus.locationDateTimes.find((x) => (!x.isSoldOut || x.overBooking === 1) && new Date(x.fromDate) > new Date()).id;
        this.previousSelectedVenueId = this.invitationStatus.locationDateTimes.find((x) => (!x.isSoldOut || x.overBooking === 1) && new Date(x.fromDate) > new Date()).id;
      }
      const validVenues = [];

      this.invitationStatus.locationDateTimes.forEach(venue => {
        // Venue is not soldout or overbooking is unlimited
        if (!venue.isSoldOut || venue.overBooking === 1) {
          // If venue is not closed
            validVenues.push(venue.id);
        }
      });
      const firstValidVenueObject = this.invitationStatus.locationDateTimes.filter(venue => venue.id === validVenues[0]);
        //Assign available tickets to preselected venue
        if(this.isMultiVenue){
          this.previousSelectedVenueAvailableTickets = this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1 ?
          firstValidVenueObject[0].ticketAvailable : this.invitationStatus.maxGuests;
        }else {
          if(firstValidVenueObject[0]){
            this.previousVenueSelectionTicketAvailable = firstValidVenueObject[0].ticketAvailable;
          }
        }
    }

    if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      this.locationDateTimeId = this.invitationAttendees.locationDateTimeId;
      this.previousSelectedVenueId = this.invitationAttendees.locationDateTimeId;
    }
    // init disabled options by venue
    this.multiDisabledOptions = [{ [this.locationDateTimeId]: { } }];

    // Set data
    this.setData();

    // Populate amount of guests dropdown
    this.populateEventsDropdown();

    // Check of er meerdere venues zijn
    this.canShowVenueDropdown(this.invitationStatus);

    // By default, set the max amount of guests to the value specified by the user
    this.maxGuests = this.invitationStatus.maxGuests;

    // Set default number of guests to first element in dropdown
    // This is only applicable when the guest dropdown is shown (no guest input fields)
    // this.selectedNumberOfGuests = 0;

    this.selectedNumberOfGuests = this.invitationAttendees.guests.length;

    // Guest is anonymous
    this.guestsAnonymous = !this.showGuest();

    // Check if guests dropdown can be shown
    this.canShowGuestDropdown(this.invitationStatus);

    if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      // Set max amount of guests based on current venue
      this.setMaxGuests(this.locationDateTimeId);
      this.changeVenueRouting(this.locationDateTimeId);
    }
      this.prepeareFieldsForConfirmRegistration();

    this.setInitFieldsValues(this.currentShowedFields, this.invitationAttendees, null);
    this.invitationAttendees.guests.forEach((element, i) => {
      this.setInitFieldsValues(this.currentShowedFields, this.invitationAttendees, i);
    });
    // remove hidden controls
    this.removeHiddenControls(this.hiddenInviteeFields, null);
    this.hiddenGuestsFields.forEach((hiddenfields, i) => {
      this.removeHiddenControls(hiddenfields, i);
    });

    // Set countries dropdown
    this.itemsCountries = Countries(countries, this.currentLang);

    // Set confirmation mail value from localstorage
    if (localStorage.getItem('sendConfirmationMail') === 'true') {
      this.sendConfirmationMail = true;
    }

    // Populate guests dropdowns for anonymous guests
    this.populateGuestsDropdownMultiVenue();

      this.customFieldOptionsItems['mainInvitee'] = [];
      this.fieldsForRegistrationForm.forEach((field)=>{
        if(field.id && field.options){
          if(!this.customFieldOptionsItems['mainInvitee'][field.id]){
            this.customFieldOptionsItems['mainInvitee'][field.id] = [];
            this.customFieldOptionsItems['mainInvitee'][field.id] = JSON.parse(JSON.stringify(field.options));
          }
          this.invitationAttendees.guests.forEach((guest) => {
            if(!this.customFieldOptionsItems[guest.id]){
              this.customFieldOptionsItems[guest.id] = [];
            }
            if(!this.customFieldOptionsItems[guest.id][field.id]){
              this.customFieldOptionsItems[guest.id][field.id] = [];
              this.customFieldOptionsItems[guest.id][field.id] = JSON.parse(JSON.stringify(field.options));
            }
          });
        }
      });

    // Define previous selected values
    this.definePreviousSelectedValuesCustomFields();

    this.invitationAttendees.guests.forEach((element, i) => {
      this.setInitFieldsValues(this.currentShowedFields, this.invitationAttendees, i);
    });

    if(this.isMultiVenue && !(this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      this.setCustomFieldOptionsMultiVenueUpdateRegistration();
    }

    // Configure fieldOptions for mainInvitee
    this.configureCustomFieldItems('mainInvitee');

    if(this.isMultiVenue && this.multiVenueType === MultiVenueType.Attendee){
      this.refreshFieldOptionsMultiVenue();
    }else{
      this.refreshFieldOptions();
    }
  }

  public ngOnDestroy(): void {
    this.state.clear();

    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public changeAnonymousGuests(model, multipleVenueSelected: boolean){
    const previousSelectedNumberOfGuests = model.guests.length;
    const currentSelectedNumberOfGuests = this.selectedNumberOfGuests;

    if(previousSelectedNumberOfGuests !== currentSelectedNumberOfGuests){
      if(previousSelectedNumberOfGuests > currentSelectedNumberOfGuests){
        model.guests.splice(currentSelectedNumberOfGuests);
        model.guests.forEach(guest => {
          guest.isAnonymous = true;
        });
      }else{
        for(let i = 0; i < (currentSelectedNumberOfGuests - previousSelectedNumberOfGuests); i++){
          const newGuest = {
            id: uuid(),
            parentId: multipleVenueSelected ?  '' : null,
            isAnonymous: true,
            locationDateTimeId: multipleVenueSelected ? '' : model.locationDateTimeId,
            firstName: '',
            lastName: '',
            email: '',
            mobile: '',
            companyName: '',
            addressLineOne: '',
            addressLineTwo: '',
            city: '',
            postalCode: '',
            country: '',
            customFields: [],
            jobTitle: '',
            customerNumber: '',
          };
          model.guests.push(newGuest);
        }
      }
    }
    return model.guests;
  }

  public onSubmit(formData: any) {
    this.submitClicked = true;
    const uploadIsValid = this.validateFileUpload();
    this.validateForm(this.registrationForm);

    if (this.registrationForm.valid && uploadIsValid) {
      const model: IRegistration = this.getFormData(formData);
      model.invitationId = this.invitationId;

      if(!this.isMultiVenue){
        model.overBookingType = this.invitationStatus.locationDateTimes.find((x) => x.id === this.locationDateTimeId).overBooking;
        model.locationDateTimeId = this.locationDateTimeId;
        model.guests.forEach((guest)=>{
          guest.locationDateTimeId = this.locationDateTimeId;
          guest.isAnonymous = this.guestsAnonymous;
          guest.parentId = null;
        });
        if(this.showGuestDropdown && this.guestsAnonymous){
          model.guests = this.changeAnonymousGuests(model, false);
        }
      }

      if(this.isMultiVenue){
          this.validateFormMultiVenue();

          switch(this.multiVenueType){
          case MultiVenueType.Registration:
            if(this.maxVenues === 1){
              // Assign the same locationDateTimeId to all guests
              model.locationDateTimeId = this.locationDateTimeId;
              model.guests.forEach((guest)=>{
                guest.parentId = null;
                guest.isAnonymous = this.guestsAnonymous;
                guest.locationDateTimeId = this.locationDateTimeId;
              });
              if(this.showGuestDropdown && this.guestsAnonymous){
                model.guests = this.changeAnonymousGuests(model, false);
              }
            }else if((this.maxVenues > 1 || this.maxVenues === null) && !this.guestNotValid){
              if(this.locationDateTimeIdsPerRegistration.length === 1){
                model.locationDateTimeId = this.locationDateTimeIdsPerRegistration[0];
                model.guests.forEach((guest)=>{
                  guest.locationDateTimeId = this.locationDateTimeIdsPerRegistration[0];
                  guest.isAnonymous = this.guestsAnonymous;
                  guest.parentId = null;
                });
                if(this.showGuestDropdown && this.guestsAnonymous){
                  model.guests = this.changeAnonymousGuests(model, false);
                }
              }else{
                model.locationDateTimeId = this.locationDateTimeIdsPerRegistration[0];
                if(this.showGuestDropdown && this.guestsAnonymous){
                  model.guests = this.changeAnonymousGuests(model, true);
                }
                model.guests = this.updateAllAttendees(model.guests, false);
                const duplicatedMainInvitees = this.duplicateMainInviteePerRegistration(model);
                duplicatedMainInvitees.forEach((duplicatedMainInvitee)=>{
                  model.guests.push(duplicatedMainInvitee);
                });
              }
            }
            break;
          case MultiVenueType.Attendee:
            if((this.maxVenueType === MaxVenueType.Attendee || this.maxVenueType === MaxVenueType.Registration) && !this.guestNotValid){
              if(this.maxVenues === 1){
                // Assign id and locationDateTimeId to mainInvitee
                model.id = this.copiedAttendeeDetails.id;
                model.locationDateTimeId = this.multiVenueSelectionPerAttendee['mainInvitee'][0];

                // Assign new locationDateTimeIds to guests
                if (model.guests.length !== 0) {
                  model.guests.forEach((guest) => {
                    let locationDateTimeIds;
                    if(this.showGuestDropdown && this.guestsAnonymous){
                      locationDateTimeIds = this.multiVenueSelectionPerAttendee['mainInvitee'];
                    }else{
                      locationDateTimeIds = this.multiVenueSelectionPerAttendee[guest.id];
                    }
                    if (locationDateTimeIds && locationDateTimeIds.length > 0) {
                      guest.locationDateTimeId = locationDateTimeIds[0];
                      guest.parentId = null;
                      guest.isAnonymous = this.guestsAnonymous;
                    }
                  });
                }
                if(this.showGuestDropdown && this.guestsAnonymous){
                  model.guests = this.changeAnonymousGuests(model, false);
                }
              } else if (this.maxVenues > 1 || this.maxVenues === null){
                // Find the main invitee's locationDateTimeIds
                const mainInviteeLocationDateTimes = this.multiVenueSelectionPerAttendee['mainInvitee'];
                if (mainInviteeLocationDateTimes && mainInviteeLocationDateTimes.length > 0) {
                  model.locationDateTimeId = mainInviteeLocationDateTimes[0];
                }
                if(this.showGuestDropdown && this.guestsAnonymous){
                  model.guests = this.changeAnonymousGuests(model, true);
                }
                // Update all guests in the model
                model.guests = this.updateAllAttendees(model.guests, true);
                // Handle duplicated main invitees
                const duplicatedMainInvitees = this.duplicateMainInviteePerAttendee(model);
                duplicatedMainInvitees.forEach((duplicatedMainInvitee) => {
                  model.guests.push(duplicatedMainInvitee);
                });
              }
            }
            break;
          default:
            break;
        }
      }

      // Send confirmation mail
      model.sendConfirmationMail = this.sendConfirmationMail;
      localStorage.setItem('sendConfirmationMail', this.sendConfirmationMail?.toString());

      if(!this.isMultiVenue || (this.isMultiVenue && !this.guestNotValid)){
        this.filterFormDataForSave(model);
        this.formSubmitted.emit(model);
      }else{
        this.modalActionStatusChanged?.next(ModalActionStatus.Iddle);
      }
    } else {
      this.modalActionStatusChanged?.next(ModalActionStatus.Iddle);
    }
  }

  public validateFormMultiVenue(){
    // Multivenue validation
    this.guestNotValid = false;
    if(this.multiVenueType === MultiVenueType.Registration && (this.maxVenues > 1 || this.maxVenues === null)){
      if(this.locationDateTimeIdsPerRegistration.length === 0){
        this.guestNotValid = true;
        this.unvalidatedGuestsList.push('unvalidatedGuestsPerRegistration');
      }
    }else if(this.multiVenueType === MultiVenueType.Attendee && (this.maxVenueType === MaxVenueType.Attendee || this.maxVenueType === MaxVenueType.Registration)){
      // Validate guests in the registration form
      this.registrationForm.value.guests.forEach((registrationFormGuest) => {
        const guestHasLocationDateTime = this.multiVenueSelectionPerAttendee[registrationFormGuest.guestId || registrationFormGuest.id] &&
                                        this.multiVenueSelectionPerAttendee[registrationFormGuest.guestId || registrationFormGuest.id].length > 0;
        if (!guestHasLocationDateTime) {
          if(!this.unvalidatedGuestsList.includes(registrationFormGuest.guestId || registrationFormGuest.id)){
            this.unvalidatedGuestsList.push(registrationFormGuest.guestId || registrationFormGuest.id);
          }
        }
      });
      // Validate main invitee when maxVenues is 1 and maxVenueType is Attendee
      if(this.maxVenues === 1 && (this.maxVenueType === MaxVenueType.Attendee || this.maxVenueType === MaxVenueType.Registration)){
        const mainInviteeHasLocationDateTime = this.multiVenueSelectionPerAttendee['mainInvitee'] &&
                                              this.multiVenueSelectionPerAttendee['mainInvitee'].length > 0;
        if(!mainInviteeHasLocationDateTime){
          if(!this.unvalidatedGuestsList.includes('mainInvitee')){
            this.unvalidatedGuestsList.push('mainInvitee');
          }
        }
      }
      // Validate main invitee when maxVenues is greater than 1 or null
      if(this.maxVenues > 1 || this.maxVenues === null){
        const mainInviteeHasLocationDateTime = this.multiVenueSelectionPerAttendee['mainInvitee'] &&
                                              this.multiVenueSelectionPerAttendee['mainInvitee'].length > 0;
        if(!mainInviteeHasLocationDateTime){
          if(!this.unvalidatedGuestsList.includes('mainInvitee')){
            this.unvalidatedGuestsList.push('mainInvitee');
          }
        }
      }
      // Set guestNotValid flag if there are unvalidated guests
      if(this.unvalidatedGuestsList.length > 0){
        this.guestNotValid = true;
      }
    }
  }

  public onChangeVenueSelectionPerRegistration(){
    //Change venue routing per whole registration page case
    this.changeVenueRoutingMultiVenuePerRegistration(this.locationDateTimeIdsPerRegistration);
    if(this.locationDateTimeIdsPerRegistration.length > 0){
      // Find selecteLocationDateTimes in inviationStatus because of the availableTickets
      const selectedLocationDateTimes = this.invitationStatus.locationDateTimes.filter(locationDateTime =>
        this.locationDateTimeIdsPerRegistration.includes(locationDateTime.id)
      );
      // Find minumum ticketsAvailable of selectedLocationDateTimes
      const minTicketsData = selectedLocationDateTimes.reduce((acc, locationDateTime) => {
        if (locationDateTime.ticketAvailable < acc.minTicketsAvailable) {
            return {
                minTicketsAvailable: locationDateTime.ticketAvailable,
                locationDateTimeId: locationDateTime.id
            };
        }
          return acc;
      }, { minTicketsAvailable: Number.MAX_SAFE_INTEGER, locationDateTimeId: null });
      // If form is shown, change the form
      if(this.currentGuests > 1){
        this.setNewGuestForm(minTicketsData.locationDateTimeId);
      }
      // Set maxGuest based on selected locationDateTimeId with minimum availableTickets
      this.setMaxGuests(minTicketsData.locationDateTimeId);
      // this.unvalidatedGuestsList = [];
      this.previousSelectedVenueAvailableTickets = minTicketsData.minTicketsAvailable > this.invitationStatus.maxGuests ? this.invitationStatus.maxGuests : minTicketsData.minTicketsAvailable;
      this.unvalidatedGuestsList = [];
      this.guestNotValid = false;
    }else{
      this.maxGuests = this.invitationStatus.maxGuests;
      this.previousSelectedVenueAvailableTickets = this.invitationStatus.maxGuests;
    }

    // Refresh fields
    this.refreshFieldOptions();
  }

  public onChangeVenueSelectionPerAttendee(selectedLocationDateTimeIds: string[], guestId: string){
    //If is not array create array
    if (!Array.isArray(selectedLocationDateTimeIds)) {
      selectedLocationDateTimeIds = [selectedLocationDateTimeIds];
    }
    // Ensure selected values are strings
    const processedLocationDateTimeIds = selectedLocationDateTimeIds.map(String);
    // Update the dictionary
    this.multiVenueSelectionPerAttendee[guestId] = [...new Set(processedLocationDateTimeIds)];
    if(this.multiVenueSelectionPerAttendee[guestId].length > 0){
      this.unvalidatedGuestsList = this.unvalidatedGuestsList.filter(id => id !== guestId);
    }
    // Refresh fields
    this.refreshFieldOptionsMultiVenue();
  }

  public configureCustomFieldItems(guestId: string){
      if(!this.customFieldOptionsItems[guestId]){
        this.customFieldOptionsItems[guestId] = [];
      }
      this.fieldsForRegistrationForm.forEach((field)=>{
        if(field.id && field.options){

          if (!this.customFieldOptionsItems[guestId][field.id]) {
            this.customFieldOptionsItems[guestId][field.id] = [];
            this.customFieldOptionsItems[guestId][field.id] = JSON.parse(JSON.stringify(field.options));
          }
          if(!this.customFieldOptionsItems['mainInvitee'][field.id]){
            this.customFieldOptionsItems['mainInvitee'][field.id] = [];
            this.customFieldOptionsItems['mainInvitee'][field.id] = JSON.parse(JSON.stringify(field.options));
          }
        }
      });
  }

  public definePreviousSelectedValuesCustomFields(){
    this.fieldsForRegistrationForm.forEach((field) => {
      if (field.selectFormat === SelectFormat.CheckboxRadio && field.id && field.options) {
        // Define previous selected for mainInvitee
        this.setPreviousSelectedOptions(this.invitationAttendees.customFields, field, null);

        // Define previous selected for guests
        this.invitationAttendees.guests.forEach((guest, guestIndex) => {
          this.setPreviousSelectedOptions(guest.customFields, field, guestIndex);
        });
      }
    });
  }

  public setPreviousSelectedOptions(customFields, field, index){
    const customField = customFields.find(customFieldAttendee => customFieldAttendee.id === field.id);
    if (customField?.customFieldOptions) {
      customField.customFieldOptions.forEach((option) => {
        if (!this.previousSelectedOption[index]) {
          this.previousSelectedOption[index] = [];
        }
        if (!this.previousSelectedOption[index][field.id]) {
          this.previousSelectedOption[index][field.id] = [];
        }
        this.previousSelectedOption[index][field.id] = option.id;
      });
    }
  }

  public checkVenueTicketsAvailability(locationDateTimeId: string, guestId: string){
    let ticketsAvailable;
    let locationDateTimesCount = 0;
    // Find how many tickets are available for specific venue
    this.invitationStatus.locationDateTimes.forEach((locationDateTime)=>{
      if(locationDateTime.id === locationDateTimeId){
        if(locationDateTime.isSoldOut && locationDateTime.overBooking === 1){
          return { isVenueAvailable: false };
        }
        ticketsAvailable = locationDateTime.ticketAvailable;
      }
    });
    // Find how many venues are checked on venue lists (registration page) for specific venue
    Object.entries(this.multiVenueSelectionPerAttendee).forEach(([key, locationDateTimeIds]) => {
      locationDateTimeIds.forEach((id) => {
        if (id === locationDateTimeId) {
          locationDateTimesCount++;
        }
      });
    });
    const isVenueAvailable = locationDateTimesCount >= ticketsAvailable;
    return isVenueAvailable;
  }

  public getVenueItems(guestId?: string){
    if(this.isMultiVenue){
      if(guestId){
        // Venue selection per attendee
        const selectedVenues = this.multiVenueSelectionPerAttendee[guestId] || [];

        // Logic for dynamic segments
        let filteredItems = this.itemsEvents;
        if(this.multiVenueType === MultiVenueType.Attendee){
          filteredItems = this.itemsEvents.filter(item => {
            if (guestId === 'mainInvitee') {
              return item.isVisibleToMainInvitee;
            }else{
              return item.isVisibleToGuests;
            }
          });
        }

        if(this.maxVenueType === MaxVenueType.Attendee){
          if(this.maxVenues === 1 || this.maxVenues === null){
            return filteredItems.map(item => ({
              ...item,
              disabled: selectedVenues.includes(item.id) || this.checkVenueTicketsAvailability(item.id, guestId) ? true : false
            }));
          }else if(this.maxVenues > 1){
            return filteredItems.map(item => ({
              ...item,
              disabled: selectedVenues.length >= this.maxVenues
                        ? true
                        : selectedVenues.includes(item.id)
                          ? true
                          : this.checkVenueTicketsAvailability(item.id, guestId)
            }));
          }
        }else if(this.maxVenueType === MaxVenueType.Registration){
          if(this.maxVenues === 1 || this.maxVenues === null){
            return filteredItems.map(item => ({
              ...item,
              disabled: selectedVenues.includes(item.id) || this.checkVenueTicketsAvailability(item.id, guestId) ? true : false
            }));
          }else if(this.maxVenues > 1){
            // Calculate total selected venues across all guests
            const totalSelectedVenues = Object.values(this.multiVenueSelectionPerAttendee)
              .reduce((acc, curr) => acc + (curr ? curr.length : 0), 0);
            return filteredItems.map(item => ({
              ...item,
              disabled: totalSelectedVenues >= this.maxVenues ||
                        selectedVenues.includes(item.id) || this.checkVenueTicketsAvailability(item.id, guestId)
            }));
          }
        }
      }else{
        // Venue selection per registration
        // If is 1 or no limit disable Full and Closed venues
        const selectedVenues = this.locationDateTimeIdsPerRegistration;
        if(this.maxVenues === 1){
          return this.itemsEvents.map(item =>({
            ...item,
            disabled: this.locationDateTimeId === item.id
          }));
        }else if(this.maxVenues === null){
          return this.itemsEvents.map(item =>({
            ...item,
            disabled: selectedVenues.includes(item.id)
          }));
        }else if(this.maxVenues > 1){
          return this.itemsEvents.map(item => ({
            ...item,
            disabled: this.locationDateTimeIdsPerRegistration.length >= this.maxVenues
                      ? true
                      : this.locationDateTimeIdsPerRegistration.includes(item.id)
          }));
        }
      }
    }else{
      return this.itemsEvents.map(item => ({
        ...item,
        disabled: this.locationDateTimeId === item.id
      }));
    }
  }

  private validateFileUpload(): boolean {
    let isValid = true;

    let fileUploadFields = this.invitationRegistrationPage.customFields.filter(f => f.customFieldType === CustomFieldType.FileUpload && f.required);
    fileUploadFields = this.filterActiveFields(fileUploadFields, this.hiddenInviteeFields);

    if (fileUploadFields) {
        for (const field of fileUploadFields) {
            if (field.show) {
                const files = this.inviteeFiles[field.id];
                this.inviteeValidMap[field.id] = files?.length > 0 || false;
                if (!this.inviteeValidMap[field.id]) {
                isValid = false;
                }
            }
            if (field.showGuest) {
                const guestsFormArray = this.registrationForm.controls['guests'].value;
                guestsFormArray.forEach(guest => {
                const files = (this.guestFiles[guest.guestId] || {})[field.id];
                this.guestValidMap[guest.guestId] = this.guestValidMap[guest.guestId] || {};
                this.guestValidMap[guest.guestId][field.id] = files?.length > 0 || false;
                if (!this.guestValidMap[guest.guestId][field.id]) {
                    isValid = false;
                }
                });
            }
        }
    } else {
        return;
    }
    return isValid;
  }

  private filterActiveFields(fileUploadFields, hiddenInviteeFields) {
    return fileUploadFields.filter((field) => {
      const index = hiddenInviteeFields.findIndex(
        (hiddenField) => hiddenField.id === field.id
      );
      return index === -1;
    });
  }

  public filterFormDataForSave(model: IRegistration) {
    this.updateInviteeDataForSave(model);
    this.updateGuestDataForSave(model);
  }

  public updateInviteeDataForSave(model: IRegistration) {
    this.hiddenInviteeFields.forEach((field) => {
      if (field.id) {
        model.customFields = model.customFields.filter((el) => el.id !== field.id);
      } else {
        if (field.type === 'address') {
          delete model['addressLineOne'];
          delete model['addressLineTwo'];
          delete model['city'];
          delete model['postalCode'];
          delete model['country'];
        } else if (field.type === 'company') {
          delete model['companyName'];
        } else {
          delete model[field.type];
        }
      }
    });

    model.customFields = model.customFields.map(el => {
        if (el.customFieldType === CustomFieldType.FreeText) {
            if (Array.isArray(el.textValue)) {
            el.textValue = el.textValue.length > 0 ? el.textValue[0] : '';
            }
        }
        return el;
    });
  }

  public updateGuestDataForSave(model: IRegistration) {
    if (model.guests) {
      model.guests.forEach((guest, i) => {
        if (this.hiddenGuestsFields[i]) {
          this.hiddenGuestsFields[i].forEach((field) => {
            if (field.id) {
              guest.customFields = guest.customFields.filter((el) => el.id !== field.id);
            } else {
              if (field.type === 'address') {
                delete guest['addressLineOne'];
                delete guest['addressLineTwo'];
                delete guest['city'];
                delete guest['postalCode'];
                delete guest['country'];
              } else if (field.type === 'company') {
                delete guest['companyName'];
              } else {
                delete guest[field.type];
              }
            }
          });
        }
        guest.customFields = guest.customFields.map(el => {
            if (el.customFieldType === CustomFieldType.FreeText) {
                if (Array.isArray(el.textValue)) {
                    el.textValue = el.textValue.length > 0 ? el.textValue[0] : '';
                }
            }
            return el;
        });
      });
    }
  }

  public getFormData(model: any): IRegistration {
    const result: IRegistration = new Registration(null, null, '', '', '', '', '', '', '', '', '', '', '', [], [], '', 0, '', '', false);

    const form = model;
    const formData = form.value;

    this.fieldKeys.forEach((key) => {
      if (formData.hasOwnProperty(key)) {
        result[key] = formData[key];
      }
    });

    if (formData.hasOwnProperty('company')) {
      result.companyName = formData['company'];
    }

    this.addressFields.forEach((key) => {
      if (formData.hasOwnProperty(key)) {
        result[key] = formData[key];
      }
    });

    result.customFields = this.getCustomFields(model);

    for (let i = 0, len = formData.guests.length; i < len; i++) {
      const newGuest = this.getGuestFormData(formData.guests[i]);
      result.guests.push(newGuest);
    }
    return result;
  }

  public getGuestFormData(model: any): IGuest {
    const result: IGuest = new Guest(model?.guestId, '', false, '', '', '', '', '', '', '', '', '', '', '', [], '', '', '');

    this.fieldKeys.forEach((key) => {
      if (model.hasOwnProperty(key)) {
        result[key] = model[key];
      }
    });

    if (model.hasOwnProperty('company')) {
      result.companyName = model['company'];
    }

    this.addressFields.forEach((key) => {
      if (model.hasOwnProperty(key)) {
        result[key] = model[key];
      }
    });

    result.customFields = this.getGuestCustomFields(model);
    return result;
  }

  public getCustomFields(model: any): (IAddCustomFieldOption | IAddCustomField | IAddCustomFieldFile)[] {
    const customFieldsArray: (IAddCustomFieldOption | IAddCustomField | IAddCustomFieldFile)[] = [];

    const form = model;
    const formData = form.value;

    for (const customField of this.invitationRegistrationPage.customFields) {
      // Only include fields that are visible to the invitee on the Dashboard page
      const cfg = customField.customFieldConfigurations
        ? customField.customFieldConfigurations.find(c => c.registrationPageType === RegistrationPageType.Dashboard)
        : null;
      const visibleToInvitee = (!!customField.show) && (cfg ? !!cfg.isVisible : true);

      // Must be visible for invitee and present on the current form
      const controlExists = Object.prototype.hasOwnProperty.call(formData, customField.id);
      if (!visibleToInvitee || !controlExists) {
        continue;
      }

      for (const translation of customField.translations) {
        if (Object.prototype.hasOwnProperty.call(formData, customField.id)) {
          if (customField.customFieldType === CustomFieldType.MultipleSelect || customField.customFieldType === CustomFieldType.SingleSelect) {
            // Check if custom field has dropdown for options
            let arrayDropdownForOptions = formData[customField.id];
            const dropdownForOptions = customField.selectFormat === SelectFormat.Dropdown;
            if (dropdownForOptions) {
              arrayDropdownForOptions = [];
              customField?.customFieldOptions?.forEach(option => {
                let hasData = false;
                if (customField.customFieldType === CustomFieldType.SingleSelect) {
                  hasData = (Array.isArray(formData[customField.id]) ? (option.id === formData[customField.id][0]) : (option.id === formData[customField.id]));
                } else {
                  hasData = Array.isArray(formData[customField.id]) ? formData[customField.id]?.find(x => x === option.id) : false;
                }
                arrayDropdownForOptions.push(!!hasData);
              });
            }

            const newCustomField: IAddCustomFieldOption = new AddCustomFieldOption(
              customField.id, customField.contactCustomFieldId, arrayDropdownForOptions, customField.customFieldType
            );

            if (!customFieldsArray.some((x) => x.id === customField.id)) {
              const ids: string[] = [];
              const contactCustomFieldOptionIds: string[] = [];

              newCustomField.customFieldOptions = newCustomField.customFieldOptions
                .filter((x, i) => {
                  if (!!x) {
                    ids.push(customField.customFieldOptions[i].id);
                    contactCustomFieldOptionIds.push(customField.customFieldOptions[i].contactCustomFieldOptionId);
                  }
                  return !!x;
                });

              // If nothing selected, skip pushing this field entirely
              if (ids.length === 0) {
                continue;
              }

              newCustomField.customFieldOptions = newCustomField.customFieldOptions.map((_x, i) => ({
                id: ids[i],
                contactCustomFieldOptionId: contactCustomFieldOptionIds[i],
                translations: [],
                timeSlot: null
              }));

              customFieldsArray.push(newCustomField);
            }

          } else if (customField.customFieldType === CustomFieldType.Boolean) {
            // Keep booleans as entered (including false) — do not coerce with Boolean()
            const newCustomField: IAddCustomField =
              new AddCustomField(customField.id, customField.contactCustomFieldId, null, null, null,
                !!formData[customField.id], customField.customFieldType);
            if (!customFieldsArray.some((x) => x.id === customField.id)) {
              customFieldsArray.push(newCustomField);
            }

          } else if (customField.customFieldType === CustomFieldType.FileUpload) {
            const customFieldFiles: ICustomFieldFile[] = this.inviteeFiles[customField.id]?.map(f => ({
              fileName: f.nativeFile.name,
              blobName: f.response?.data?.blobName
            }));
            const existingCustomFieldFiles: ICustomFieldFile[] = this.inviteeExistingFiles[customField.id]?.map(f => ({
              fileName: f.fileName,
              blobName: f.blobName
            }));
            const combined = ([] as ICustomFieldFile[]).concat(customFieldFiles || []).concat(existingCustomFieldFiles || []);

            // Only include if there are any files
            if (combined && combined.length > 0) {
              const newCustomField: IAddCustomFieldFile =
                new AddCustomFieldFile(customField.id, customField.contactCustomFieldId, combined, customField.customFieldType);
              if (!customFieldsArray.some((x) => x.id === customField.id)) {
                customFieldsArray.push(newCustomField);
              }
            }

          } else {
            // Free text / number / date / time — include only if there is a value
            let newCustomField: IAddCustomField;

            if (customField.freeTextFormat === FreeTextFormat.Text || customField.freeTextFormat === FreeTextFormat.CountryCode) {
              if (formData[customField.id] === '' || formData[customField.id] === null || formData[customField.id] === undefined) {
                continue;
              }
              newCustomField = new AddCustomField(
                customField.id, customField.contactCustomFieldId, formData[customField.id], null, null, null, customField.customFieldType
              );

            } else if (customField.freeTextFormat === FreeTextFormat.Number) {
              if (formData[customField.id] === '' || formData[customField.id] === null || formData[customField.id] === undefined) {
                continue;
              }
              newCustomField = new AddCustomField(
                customField.id, customField.contactCustomFieldId, null, Number(formData[customField.id]), null, null, customField.customFieldType
              );

            } else if (customField.freeTextFormat === FreeTextFormat.Date) {
              const dateTime = SetUtcDateValueBasedOnDatePickerValue(formData[customField.id]);
              if (!dateTime) {
                continue;
              }
              newCustomField = new AddCustomField(
                customField.id, customField.contactCustomFieldId, null, null, dateTime, null, customField.customFieldType
              );

            } else if (customField.freeTextFormat === FreeTextFormat.Time) {
              const timeValue = SetStringTimeValueBasedOnTimePickerValue(formData[customField.id]);
              if (!timeValue) {
                continue;
              }
              newCustomField = new AddCustomField(
                customField.id, customField.contactCustomFieldId, timeValue, null, null, null, customField.customFieldType
              );
            }

            if (newCustomField && !customFieldsArray.some((x) => x.id === customField.id)) {
              customFieldsArray.push(newCustomField);
            }
          }
        }
      }
    }
    return customFieldsArray;
  }

  public getGuestCustomFields(model: any): (IAddCustomFieldOption | IAddCustomField | IAddCustomFieldFile)[] {
    const customFieldsArray: (IAddCustomFieldOption | IAddCustomField | IAddCustomFieldFile)[] = [];

    for (const customField of this.invitationRegistrationPage.customFields) {
      // Only include fields that are visible to guests on the Dashboard page
      const cfg = customField.customFieldConfigurations
        ? customField.customFieldConfigurations.find(c => c.registrationPageType === RegistrationPageType.Dashboard)
        : null;
      const visibleToGuest = (!!customField.showGuest) && (cfg ? !!cfg.isVisible : true);

      // Must be visible for guest and present on the current guest model
      const controlExists = Object.prototype.hasOwnProperty.call(model, customField.id);
      if (!visibleToGuest || !controlExists) {
        continue;
      }

      for (const translation of customField.translations) {
        if (Object.prototype.hasOwnProperty.call(model, customField.id)) {
          if (customField.customFieldType === CustomFieldType.MultipleSelect || customField.customFieldType === CustomFieldType.SingleSelect) {
            // Check if custom field has dropdown for options
            let arrayDropdownForOptions = model[customField.id];
            const dropdownForOptions = customField.selectFormat === SelectFormat.Dropdown;
            if (dropdownForOptions) {
              arrayDropdownForOptions = [];
              customField?.customFieldOptions?.forEach(option => {
                let hasData = false;
                if (customField.customFieldType === CustomFieldType.SingleSelect) {
                  hasData = (Array.isArray(model[customField.id]) ? (option.id === model[customField.id][0]) : (option.id === model[customField.id]));
                } else {
                  hasData = Array.isArray(model[customField.id]) ? model[customField.id]?.find(x => x === option.id) : false;
                }
                arrayDropdownForOptions.push(!!hasData);
              });
            }

            const newCustomField: IAddCustomFieldOption = new AddCustomFieldOption(
              customField.id, customField.contactCustomFieldId, arrayDropdownForOptions, customField.customFieldType
            );

            if (!customFieldsArray.some((x) => x.id === customField.id)) {
              const ids: string[] = [];
              const contactCustomFieldOptionIds: string[] = [];

              newCustomField.customFieldOptions = newCustomField.customFieldOptions
                .filter((x, i) => {
                  if (!!x) {
                    ids.push(customField.customFieldOptions[i].id);
                    contactCustomFieldOptionIds.push(customField.customFieldOptions[i].contactCustomFieldOptionId);
                  }
                  return !!x;
                });

              // If nothing selected, skip pushing this field entirely
              if (ids.length === 0) {
                continue;
              }

              newCustomField.customFieldOptions = newCustomField.customFieldOptions.map((_x, i) => ({
                id: ids[i],
                contactCustomFieldOptionId: contactCustomFieldOptionIds[i],
                translations: [],
                timeSlot: null
              }));

              customFieldsArray.push(newCustomField);
            }

          } else if (customField.customFieldType === CustomFieldType.Boolean) {
            // Keep booleans as entered (including false)
            const newCustomField: IAddCustomField =
              new AddCustomField(customField.id, customField.contactCustomFieldId, null, null, null,
                !!model[customField.id], customField.customFieldType);
            if (!customFieldsArray.some((x) => x.id === customField.id)) {
              customFieldsArray.push(newCustomField);
            }

          } else if (customField.customFieldType === CustomFieldType.FileUpload) {
            const customFieldFiles: ICustomFieldFile[] = this.guestFiles[model.guestId]
              ? this.guestFiles[model.guestId][customField.id]?.map(f => ({
                  fileName: f.nativeFile.name,
                  blobName: f.response?.data?.blobName
                }))
              : null;

            const existingCustomFieldFiles: ICustomFieldFile[] = this.guestExistingFiles[model.guestId]
              ? this.guestExistingFiles[model.guestId][customField.id]?.map(f => ({
                  fileName: f.fileName,
                  blobName: f.blobName
                }))
              : null;

            const combined = ([] as ICustomFieldFile[]).concat(customFieldFiles || []).concat(existingCustomFieldFiles || []);

            // Only include if there are any files
            if (combined && combined.length > 0) {
              const newCustomField: IAddCustomFieldFile =
                new AddCustomFieldFile(customField.id, customField.contactCustomFieldId, combined, customField.customFieldType);
              if (!customFieldsArray.some((x) => x.id === customField.id)) {
                customFieldsArray.push(newCustomField);
              }
            }

          } else {
            // Free text / number / date / time — include only if there is a value
            let newCustomField: IAddCustomField;

            if (customField.freeTextFormat === FreeTextFormat.Text || customField.freeTextFormat === FreeTextFormat.CountryCode) {
              if (model[customField.id] === '' || model[customField.id] === null || model[customField.id] === undefined) {
                continue;
              }
              newCustomField = new AddCustomField(
                customField.id, customField.contactCustomFieldId, model[customField.id], null, null, null, customField.customFieldType
              );

            } else if (customField.freeTextFormat === FreeTextFormat.Number) {
              if (model[customField.id] === '' || model[customField.id] === null || model[customField.id] === undefined) {
                continue;
              }
              newCustomField = new AddCustomField(
                customField.id, customField.contactCustomFieldId, null, Number(model[customField.id]), null, null, customField.customFieldType
              );

            } else if (customField.freeTextFormat === FreeTextFormat.Date) {
              const dateTime = SetUtcDateValueBasedOnDatePickerValue(model[customField.id]);
              if (!dateTime) {
                continue;
              }
              newCustomField = new AddCustomField(
                customField.id, customField.contactCustomFieldId, null, null, dateTime, null, customField.customFieldType
              );

            } else if (customField.freeTextFormat === FreeTextFormat.Time) {
              const timeValue = SetStringTimeValueBasedOnTimePickerValue(model[customField.id]);
              if (!timeValue) {
                continue;
              }
              newCustomField = new AddCustomField(
                customField.id, customField.contactCustomFieldId, timeValue, null, null, null, customField.customFieldType
              );
            }

            if (newCustomField && !customFieldsArray.some((x) => x.id === customField.id)) {
              customFieldsArray.push(newCustomField);
            }
          }
        }
      }
    }

    return customFieldsArray;
  }

  public containsLanguageValue(customField: ICustomField) {
    return customField.translations.find((x) => x.languageId === this.userLanguageId)?.value !== '';
  }

  public getCustomFieldValue(customField: ICustomField) {
    return customField.translations.find((x) => x.languageId === this.userLanguageId)?.value;
  }

  public getCustomFieldOptionValue(customField: ICustomField, optionIndex: number) {
    let formattedtime = '';
    const option = customField.customFieldOptions[optionIndex];
    const translationValue = this.translationService.getTranslationValueForTimeslot(option?.translations, this.userLanguageId);
    if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      const venue = this.invitationStatus.locationDateTimes.find((x) => x.id === this.locationDateTimeId);
      if (customField.multipleChoiceConfigOptions.typeOfTimeSlot !== TypeOfTimeSlot.Undefined && option?.timeSlot && venue) {
        const venueTime = new DateTimeRange(moment(venue.fromDate).toDate(), moment(venue.toDate).toDate());
        const timeSlot = new DateTimeRange(new Date(option.timeSlot.begin), new Date(option.timeSlot.end));
        formattedtime = timeSlot.format(this.userLanguageId, venueTime);
      }
    }
    if (!formattedtime && customField.multipleChoiceConfigOptions.typeOfTimeSlot !== TypeOfTimeSlot.Undefined && option?.timeSlot) {
      const begin = typeof option.timeSlot.begin === 'string' ? new Date(option.timeSlot.begin) : option.timeSlot.begin;
      const end = typeof option.timeSlot.end === 'string' ? new Date(option.timeSlot.end) : option.timeSlot.end;
      formattedtime = `${moment(begin).locale(this.userLanguageId).format('L LT')} - ${moment(end).locale(this.userLanguageId).format('L LT')}`;
    }
    if (!translationValue && formattedtime) {
      return formattedtime;
    }

    if (!formattedtime && customField.multipleChoiceConfigOptions.typeOfTimeSlot !== TypeOfTimeSlot.Undefined && option?.timeSlot) {
      const begin = typeof option.timeSlot.begin === 'string' ? new Date(option.timeSlot.begin) : option.timeSlot.begin;
      const end = typeof option.timeSlot.end === 'string' ? new Date(option.timeSlot.end) : option.timeSlot.end;
      formattedtime = `${moment(begin).locale(this.userLanguageId).format('L LT')} - ${moment(end).locale(this.userLanguageId).format('L LT')}`;
    }

    if (!translationValue && formattedtime) {
      return formattedtime;
    }

    if (formattedtime !== '') {
      formattedtime = ' -' + formattedtime;
    }

    return translationValue + formattedtime;
  }

  public getCustomFieldId(customField: ICustomField) {
    return customField.id;
  }

  public updateFormData(formData, formGroup) {
    Object.keys(formData).forEach((field) => {
      if (field !== 'guests') {
        if (formGroup.controls[field]) {
          formGroup.controls[field].setValue(formData[field]);
        }
      }
    });
  }

  public prepeareFieldsForConfirmRegistration() {
    this.setLocationDateTimeField();
    this.fieldsForRegistrationForm = [];
    this.invitationRegistrationPage.fieldOrders.forEach((element) => {
      const fieldName = element.fieldName;
      const fieldId = element.customFieldId;
      if (fieldName) {
        const fieldNameInCapitalize: string = this.capitalizeFirstLetter(fieldName);
        this.prepareSimpleField(
          fieldName,
          this.invitationRegistrationPage['show' + fieldNameInCapitalize],
          this.invitationRegistrationPage['showGuest' + fieldNameInCapitalize],
          this.invitationRegistrationPage['required' + fieldNameInCapitalize],
          this.invitationRegistrationPage['requiredGuest' + fieldNameInCapitalize],
          this.invitationRegistrationPage.fieldConfigurations
          .find(config => config.standardFieldType === StandardFieldType[fieldNameInCapitalize] && config.registrationPageType === RegistrationPageType.Dashboard),
                  element.fieldGoToQuestion,
          element.optionsGoToQuestion,
          element.order);
      } else if (fieldId) {
        const customField = this.invitationRegistrationPage.customFields.find((el) => el.id === fieldId);
        customField.fieldGoToQuestion = element.fieldGoToQuestion;
        customField.optionsGoToQuestion = element.optionsGoToQuestion;
        customField['order'] = element.order;
        customField['options'] = this.getCustomFieldOptions(customField, customField.customFieldOptions);

        this.fieldsForRegistrationForm.push({...customField, configuration: customField.customFieldConfigurations.find(config => config.registrationPageType === RegistrationPageType.Dashboard)});
      }
    });
    this.invitationAttendees.guests.forEach(element => {
      this.hiddenGuestsFields.push([]);
    });
    this.setInitFieldsOrder();
    this.setInviteeFieldsForEdit();
    this.setGuestsFieldsForEdit();

    if(!this.isMultiVenue){
      this.changeVenueRouting(this.locationDateTimeId);
    }else if(this.multiVenueType === MultiVenueType.Registration && (this.maxVenues > 1 || this.maxVenues === null)){
      this.changeVenueRoutingMultiVenuePerRegistration(this.locationDateTimeIdsPerRegistration);
    }else if(this.multiVenueType === MultiVenueType.Attendee && (this.maxVenueType === MaxVenueType.Attendee || this.maxVenueType === MaxVenueType.Registration)){
      this.changeVenueRoutingMultiVenuePerAttendee(this.multiVenueSelectionPerAttendee);
    }
  }

  public setLocationDateTimeField() {
    this.invitationRegistrationPage.locationDateTimeEventPageFields.forEach((el) => {
      this.locationDateTimeField.customFieldOptions.push({
        id: el.id
      });
      this.locationDateTimeField.optionsGoToQuestion.push({
        id: el.id,
        locationDateTimeId: el.locationDateTimeId,
        goToQuestions: el.goToQuestion
      });
    });
  }

  public changeVenueRouting(venue) {
    this.hiddenFieldIndexesPerVenue = [];
    this.showedFieldIndexesPerVenue = [];
    this.invitationRegistrationPage.locationDateTimeEventPageFields.forEach((element) => {
      if (element.locationDateTimeId !== venue && element.goToQuestion.length) {
        element.goToQuestion.forEach((goTo) => {
          if (!this.hiddenFieldIndexesPerVenue.includes(goTo)) {
            this.hiddenFieldIndexesPerVenue.push(goTo);
          }
        });
      } else if (element.locationDateTimeId === venue && element.goToQuestion.length) {
        element.goToQuestion.forEach((goTo) => {
          this.showedFieldIndexesPerVenue.push(goTo);
        });
      }
    });
    this.modifyHiddenFields(this.hiddenFieldIndexesPerVenue, this.showedFieldIndexesPerVenue);
  }

   // Change multivenue routing per registration
   public changeVenueRoutingMultiVenuePerRegistration(venues) {
    const allGoToQuestions = new Set();
    const matchedGoToQuestions = new Set();
    // Collect all goToQuestion IDs and matched goToQuestion IDs based on selected venues
    this.invitationRegistrationPage.locationDateTimeEventPageFields.forEach((element) => {
        element.goToQuestion.forEach((goTo) => {
            allGoToQuestions.add(goTo);
            if (venues.includes(element.locationDateTimeId)) {
                matchedGoToQuestions.add(goTo);
            }
        });
    });
    // Determine which fields to show and hide
    const newShowedFieldIndexesPerVenue = Array.from(matchedGoToQuestions);
    const newHiddenFieldIndexesPerVenue = Array.from(allGoToQuestions).filter(goTo => !matchedGoToQuestions.has(goTo));
    // Update showed and hidden fields
    this.showedFieldIndexesPerVenue = newShowedFieldIndexesPerVenue;
    this.hiddenFieldIndexesPerVenue = newHiddenFieldIndexesPerVenue;
    // Ensure there are no duplicates and modify the hidden fields
    this.modifyHiddenFields(this.hiddenFieldIndexesPerVenue, this.showedFieldIndexesPerVenue);
    const guestsArr = this.registrationForm.controls['guests'].value;
    this.resetDoubleGoToForVenueRouting();
    this.changeHiddenFieldsForVenueMultiVenuePerRegistration(this.hiddenFieldIndexesPerVenue, this.showedFieldIndexesPerVenue, null);
    guestsArr.forEach((element, i) => {
        this.changeHiddenFieldsForVenueMultiVenuePerRegistration(this.hiddenFieldIndexesPerVenue, this.showedFieldIndexesPerVenue, i);
    });
  }

   // Change multivenue routing per attendee
   public changeVenueRoutingMultiVenuePerAttendee(locationDateTimesPerAttendee){
    const allGoToQuestionsPerGuest = {};
    const matchedGoToQuestionsPerGuest = {};
    // If mainInvitee don't have venue create hiddenFields and showedFields
    // const hasMainInviteeVenue = locationDateTimesPerAttendee.some(venue => venue.guestId === 'mainInvitee');
    const hasMainInviteeVenue = Object.keys(this.multiVenueSelectionPerAttendee).includes('mainInvitee');
    if(!hasMainInviteeVenue){
      this.showedFieldIndexesPerGuestMultiVenue['mainInvitee'] = [];
      this.hiddenFieldIndexesPerGuestMultiVenue['mainInvitee'] = [];
      this.invitationRegistrationPage.locationDateTimeEventPageFields.forEach((element) => {
        element.goToQuestion.forEach((goTo) => {
          this.hiddenFieldIndexesPerGuestMultiVenue['mainInvitee'].push(goTo);
        });
      });
    }
    this.registrationForm.value.guests.forEach((guest)=>{
      const hasGuestVenue = Object.keys(this.multiVenueSelectionPerAttendee).includes(guest.id);
      // Ensure the arrays are initialized
      if (!this.showedFieldIndexesPerGuestMultiVenue[guest.id]) {
        this.showedFieldIndexesPerGuestMultiVenue[guest.id] = [];
      }
      if (!this.hiddenFieldIndexesPerGuestMultiVenue[guest.id]) {
          this.hiddenFieldIndexesPerGuestMultiVenue[guest.id] = [];
      }
      // If guests doesn't have venue create hiddenFields and showedFields
      if(!hasGuestVenue){
        this.showedFieldIndexesPerGuestMultiVenue[guest.id] = [];
        this.hiddenFieldIndexesPerGuestMultiVenue[guest.id] = [];
        this.invitationRegistrationPage.locationDateTimeEventPageFields.forEach((element) => {
          element.goToQuestion.forEach((goTo) => {
            this.hiddenFieldIndexesPerGuestMultiVenue[guest.id].push(goTo);
          });
        });
      }
    });
    // // Collect all goToQuestion IDs and matched goToQuestion IDs based on selected venues
    this.invitationRegistrationPage.locationDateTimeEventPageFields.forEach((element) => {
      element.goToQuestion.forEach((goTo) => {
        // Iterate over each venue to check if the current element's locationDateTimeId is included
        // locationDateTimesPerAttendee.forEach(venue => {
        Object.entries(this.multiVenueSelectionPerAttendee).forEach(([guestId, locationDateTimeIds]) => {
          // const { guestId, locationDateTimeIds } = venue;
          // Initialize arrays if not already initialized
          if (!allGoToQuestionsPerGuest[guestId]) {
              allGoToQuestionsPerGuest[guestId] = [];
          }
          if (!matchedGoToQuestionsPerGuest[guestId]) {
              matchedGoToQuestionsPerGuest[guestId] = [];
          }
          // Add to all go-to questions
          if (!allGoToQuestionsPerGuest[guestId].includes(goTo)) {
              allGoToQuestionsPerGuest[guestId].push(goTo);
          }
          // Add to matched go-to questions if the locationDateTimeId matches
          if (locationDateTimeIds.includes(element.locationDateTimeId)) {
              if (!matchedGoToQuestionsPerGuest[guestId].includes(goTo)) {
                  matchedGoToQuestionsPerGuest[guestId].push(goTo);
              }
          }
        });
      });
    });
    // Configure showedFieldIndexesPerGuestMultiVenue and showedFieldIndexesPerGuestMultiVenue
    Object.keys(allGoToQuestionsPerGuest).forEach(guestId => {
      const allGoToQuestions = allGoToQuestionsPerGuest[guestId] || [];
      const matchedGoToQuestions = matchedGoToQuestionsPerGuest[guestId] || [];
      this.showedFieldIndexesPerGuestMultiVenue[guestId] = [...matchedGoToQuestions];
      this.hiddenFieldIndexesPerGuestMultiVenue[guestId] = allGoToQuestions.filter(goTo => !matchedGoToQuestions.includes(goTo));
    });
    // Change hidden Fields for multivenue per attendee
    this.changeHiddenFieldsForVenueMultiVenuePerAttendee(this.hiddenFieldIndexesPerGuestMultiVenue, this.showedFieldIndexesPerGuestMultiVenue, null, 'mainInvitee');
    const guestsArr = this.registrationForm.controls['guests'].value;
    guestsArr.forEach((element, i) => {
      this.changeHiddenFieldsForVenueMultiVenuePerAttendee(this.hiddenFieldIndexesPerGuestMultiVenue[element.guestId || element.id],
        this.showedFieldIndexesPerGuestMultiVenue[element.guestId || element.id], i, element.guestId || element.id);
    });
  }

  public modifyHiddenFields(hiddenFieldIndexesPerVenue, showedFieldIndexesPerVenue) {
    showedFieldIndexesPerVenue.forEach(element => {
      const indexToDelete = hiddenFieldIndexesPerVenue.findIndex((ind) => ind === element);
      if (indexToDelete !== -1) {
        hiddenFieldIndexesPerVenue.splice(indexToDelete, 1);
      }
    });
  }

  public changeHiddenFieldsForVenue(hiddenFieldIndexesPerVenue, showedFieldIndexesPerVenue, guestIndex = null) {
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    this.setDoubleGoToForVenueRouting(guestIndex);
    hiddenFieldIndexesPerVenue.forEach((element) => {
      if (guestIndex !== null) {
        if (!this.hiddenGuestsFields[guestIndex].some((el) => el.order === element)) {
          if (this.canHideQuestion(this.fieldsForRegistrationForm[element], element, guestIndex)) {
            this.updateShowedFields(element, arrIndex);
            this.hideQuestion(this.hiddenGuestsFields[guestIndex], this.fieldsForRegistrationForm[element], element, guestIndex);
          }
        }
      } else {
        if (!this.hiddenInviteeFields.some((el) => el.order === element)) {
          if (this.canHideQuestion(this.fieldsForRegistrationForm[element], element, null)) {
            this.updateShowedFields(element, arrIndex);
            this.hideQuestion(this.hiddenInviteeFields, this.fieldsForRegistrationForm[element], element, guestIndex);
          }
        }
      }
    });
    this.currentShowedFields[arrIndex] = [...this.currentShowedFields[arrIndex], ...showedFieldIndexesPerVenue];
    const previousSelectedVenueGoTo = this.locationDateTimeField.optionsGoToQuestion.find((el) => el.locationDateTimeId === this.previousSelectedVenueId)?.goToQuestions;
    if (previousSelectedVenueGoTo && previousSelectedVenueGoTo.length) {
      previousSelectedVenueGoTo.forEach((goTo) => {
        const deletedIndex = this.currentShowedFields[arrIndex].findIndex((el) => el === goTo);
        if (deletedIndex !== -1) {
          this.currentShowedFields[arrIndex].splice(deletedIndex, 1);
        }
      });
    }
    this.currentShowedFields[arrIndex].forEach((index) => {
      this.showNextQuestion(guestIndex !== null ? this.hiddenGuestsFields[guestIndex] : this.hiddenInviteeFields, index, guestIndex);
    });
  }

  // Change hidden fields for multivenue per registration
  public changeHiddenFieldsForVenueMultiVenuePerRegistration(hiddenFieldIndexesPerVenue, showedFieldIndexesPerVenue, guestIndex = null, goToQuestions = []){
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    this.setDoubleGoToForVenueRoutingMultiVenuePerRegistration(guestIndex);
    hiddenFieldIndexesPerVenue.forEach((element) => {
      if (guestIndex !== null) {
        if (!this.hiddenGuestsFields[guestIndex].some((el) => el.order === element)) {
          if (this.canHideQuestion(this.fieldsForRegistrationForm[element], element, guestIndex)) {
            this.updateShowedFields(element, arrIndex);
            this.hideQuestion(this.hiddenGuestsFields[guestIndex], this.fieldsForRegistrationForm[element], element, guestIndex);
          }
        }
      } else {
        if (!this.hiddenInviteeFields.some((el) => el.order === element)) {
          if (this.canHideQuestion(this.fieldsForRegistrationForm[element], element, null)) {
            this.updateShowedFields(element, arrIndex);
            this.hideQuestion(this.hiddenInviteeFields, this.fieldsForRegistrationForm[element], element, guestIndex);
          }
        }
      }
    });
    this.currentShowedFields[arrIndex] = [...showedFieldIndexesPerVenue];
    this.currentShowedFields[arrIndex].forEach((index) => {
      this.showNextQuestion(guestIndex !== null ? this.hiddenGuestsFields[guestIndex] : this.hiddenInviteeFields, index, guestIndex);
    });
  }

  // Change hidden fields for multivenue per attendee
  public changeHiddenFieldsForVenueMultiVenuePerAttendee(hiddenFieldIndexesPerGuest, showedFieldIndexesPerGuest, guestIndex = null, attendeeId: string){
    if(hiddenFieldIndexesPerGuest && showedFieldIndexesPerGuest){
      const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
      // this.setDoubleGoToForVenueRoutingMultiVenuePerRegistration(guestIndex);
      if (guestIndex === null) {
          // Handle main attendee logic
          Object.keys(hiddenFieldIndexesPerGuest).forEach(guestId => {
            if(attendeeId === guestId){
              hiddenFieldIndexesPerGuest[guestId]?.forEach((element) => {
                if (!this.hiddenInviteeFields.some((el) => el.order === element)) {
                    if (this.canHideQuestion(this.fieldsForRegistrationForm[element], element, null)) {
                        this.updateShowedFields(element, arrIndex);
                        this.hideQuestion(this.hiddenInviteeFields, this.fieldsForRegistrationForm[element], element, guestIndex);
                    }
                }
            });
            this.currentShowedFields[arrIndex] = [...(showedFieldIndexesPerGuest[guestId] || [])];
            this.currentShowedFields[arrIndex].forEach((index) => {
                this.showNextQuestion(this.hiddenInviteeFields, index, null);
            });
            }
          });
      } else {
        hiddenFieldIndexesPerGuest?.forEach((element) => {
            if (!this.hiddenGuestsFields[guestIndex].some((el) => el.order === element)) {
              if (this.canHideQuestion(this.fieldsForRegistrationForm[element], element, guestIndex)) {
                this.updateShowedFields(element, arrIndex);
                this.hideQuestion(this.hiddenGuestsFields[guestIndex], this.fieldsForRegistrationForm[element], element, guestIndex);
              }
            }
          });
          this.currentShowedFields[arrIndex] = [...showedFieldIndexesPerGuest];
          this.currentShowedFields[arrIndex].forEach((index) => {
            this.showNextQuestion(this.hiddenGuestsFields[guestIndex], index, guestIndex);
          });
      }
    }
  }

  public setDoubleGoToForVenueRouting(guestIndex = null) {
    this.locationDateTimeField.optionsGoToQuestion.forEach((option) => {
      if (option.locationDateTimeId === this.locationDateTimeId) {
        option.goToQuestions.forEach((goTo) => {
          this.setDoubleGoTo(this.locationDateTimeField, goTo, guestIndex);
        });
      }
    });
  }

  public setDoubleGoToForVenueRoutingMultiVenuePerRegistration(guestIndex = null) {
    this.locationDateTimeField.optionsGoToQuestion.forEach((option) => {
      if (this.locationDateTimeIdsPerRegistration.includes(option.locationDateTimeId)) {
        option.goToQuestions.forEach((goTo) => {
          this.setDoubleGoTo(this.locationDateTimeField, goTo, guestIndex);
        });
      }
    });
  }

  public resetDoubleGoToForVenueRouting() {
    this.doubleGoTo.forEach((attendee) => {
      // eslint-disable-next-line guard-for-in
      for (const key in attendee) {
        attendee[key].forEach((field, i) => {
          if (field.id === 'locationDateTimeFieldId') {
            attendee[key].splice(i, 1);
          }
          return field;
        });
      }
    });
  }

  public prepareSimpleField(
      fieldType: string,
      show: boolean,
      showGuest: boolean,
      required: boolean,
      requiredGuest: boolean,
      configuration: IFieldConfiguration,
      fieldGoToQuestion = [],
      optionsGoToQuestion = [],
      order: number = 0
    ) {
    const fieldNameData = this.getFieldName(fieldType);
    const simpleFieldData = {
      type: fieldNameData.type,
      title: fieldNameData.title,
      show,
      showGuest,
      required,
      requiredGuest,
      configuration,
      fieldGoToQuestion,
      optionsGoToQuestion,
      order,
    };
    this.fieldsForRegistrationForm.push(simpleFieldData);
  }

  public setInitFieldsOrder() {
    this.fieldsForRegistrationForm.sort((a, b) => a.order - b.order);
  }

  public capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  public getFieldName(name): any {
    switch (name) {
      case 'mobile': {
        return {
          type: 'mobile',
          title: this.translation.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.MOBILE.LABEL'),
        };
      }
      case 'company': {
        return {
          type: 'company',
          title: this.translation.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.COMPANY.LABEL'),
        };
      }
      case 'address': {
        return {
          type: 'address',
          title: this.translation.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.ADDRESS.LABEL'),
        };
      }
      case 'jobTitle': {
        return {
          type: 'jobTitle',
          title: this.translation.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.JOBTITLE.LABEL'),
        };
      }
      case 'customerNumber': {
        return {
          type: 'customerNumber',
          title: this.translation.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.FORM.CUSTOMERNUMBER.LABEL'),
        };
      }
    }
  }

  public setInviteeFieldsForEdit() {
    if (this.hiddenFieldIndexesPerVenue.length) {
      this.hiddenFieldIndexesPerVenue.forEach((element) => {
        if (!this.hiddenInviteeFields.some((el) => el.order === element)) {
          if (this.canHideQuestion(this.fieldsForRegistrationForm[element], element, null)) {
            this.updateShowedFields(element, 0);
            this.hideQuestion(this.hiddenInviteeFields, this.fieldsForRegistrationForm[element], element, null);
          }
        }
      });
      this.setDoubleGoToForVenueRouting();
    }

    const visibleFieldIds = [];

    for (const field of this.fieldsForRegistrationForm) {
      if (field.customFieldType === CustomFieldType.SingleSelect || field.customFieldType === CustomFieldType.MultipleSelect) {
        if (this.invitationAttendees.customFields.length) {
          const customField = this.invitationAttendees.customFields.find((x) => x.id === field.id);
          const selectedFieldOptions = customField ? customField.customFieldOptions : [];
          this.initDisabledOptions(field, selectedFieldOptions);
          field.optionsGoToQuestion.forEach((option) => {
            if (selectedFieldOptions.length) {
              const isOptionSelected = selectedFieldOptions.some((x) => x.id === option.customFieldOptionId);
              option.goToQuestions.forEach((goTo) => {
                this.setDoubleGoTo(field, goTo, null);
                if (!isOptionSelected) {
                  const hiddenField = this.fieldsForRegistrationForm.find((el) => el.order === goTo);
                  if (hiddenField && !visibleFieldIds.includes(goTo)) {
                    this.updateShowedFields(goTo, 0);
                    this.hideQuestion(this.hiddenInviteeFields, this.fieldsForRegistrationForm[goTo], goTo, null);
                  }
                } else {
                  visibleFieldIds.push(goTo);
                  this.currentShowedFields[0].push(goTo);
                  this.showNextQuestion(this.hiddenInviteeFields, goTo, null);
                }
              });
            } else {
              option.goToQuestions.forEach(goTo => {
                this.setDoubleGoTo(field, goTo, null);
                const hiddenField = this.fieldsForRegistrationForm.find((el) => el.order === goTo);
                if (hiddenField && !visibleFieldIds.includes(goTo)) {
                  this.updateShowedFields(goTo, 0);
                  this.hideQuestion(this.hiddenInviteeFields, this.fieldsForRegistrationForm[goTo], goTo, null);
                }
              });
            }
          });
          if (field.customFieldType === CustomFieldType.SingleSelect && selectedFieldOptions.length) {
            this.previousSingleSelectValues[0][field.id] = selectedFieldOptions[0].id;
          }
        }
      } else if (field.customFieldType === CustomFieldType.Boolean) {
        if (this.invitationAttendees.customFields.length) {
          const customField = this.invitationAttendees.customFields.find((x) => x.id === field.id);
          const value = customField?.booleanValue;
          field.optionsGoToQuestion.forEach((option) => {
            const isOptionSelected = ((option.index === 0 && value) || (option.index === 1 && !value));
            option.goToQuestions.forEach((goTo) => {
              this.setDoubleGoTo(field, goTo, null);
              if (!isOptionSelected) {
                const hiddenField = this.fieldsForRegistrationForm.find((el) => el.order === goTo);
                if (hiddenField && !visibleFieldIds.includes(goTo)) {
                  this.updateShowedFields(goTo, 0);
                  this.hideQuestion(this.hiddenInviteeFields, this.fieldsForRegistrationForm[goTo], goTo, null);
                }
              } else {
                visibleFieldIds.push(goTo);
                this.currentShowedFields[0].push(goTo);
                this.showNextQuestion(this.hiddenInviteeFields, goTo, null);
              }
            });
          });
          if (value) {
            const fieldOption = field.customFieldOptions.find(x => x.index === 0);
            this.previousSingleSelectValues[0][field.id] = fieldOption.id;
          } else {
            const fieldOption = field.customFieldOptions.find(x => x.index === 1);
            this.previousSingleSelectValues[0][field.id] = fieldOption.id;
          }
        }
      } else {
        const controlName = field.id || field.type;
        const control = this.registrationForm.controls[controlName];
        const curFieldIndex = this.fieldsForRegistrationForm.indexOf(field);
        field.fieldGoToQuestion.forEach((goTo) => {
          this.setDoubleGoTo(field, goTo, null);
          if (control && !control.value) {
            const hiddenField = this.fieldsForRegistrationForm.find((el) => el.order === goTo);
            if (hiddenField && !visibleFieldIds.includes(goTo)) {
              this.updateShowedFields(goTo, 0);
              this.hideQuestion(this.hiddenInviteeFields, this.fieldsForRegistrationForm[goTo], goTo, null);
            }
          } else if (control && control.value) {
            visibleFieldIds.push(goTo);
            this.currentShowedFields[0].push(goTo);
            this.showNextQuestion(this.hiddenInviteeFields, goTo, null);
          }
        });
      }
    }
  }

  public setGuestsFieldsForEdit() {
    if (this.invitationAttendees.guests.length) {
      this.invitationAttendees.guests.forEach((element, guestIndex) => {
        this.previousSingleSelectValues.push({ });
        this.doubleGoTo.push({ });
        this.currentShowedFields.push([]);
        const guestHiddenFieldsList = [];
        if (this.hiddenFieldIndexesPerVenue.length) {
          this.hiddenFieldIndexesPerVenue.forEach((index) => {
            if (!guestHiddenFieldsList.some((el) => el.order === index)) {
              if (this.canHideQuestion(this.fieldsForRegistrationForm[index], index, guestIndex)) {
                this.updateShowedFields(index, guestIndex + 1);
                this.hideQuestion(guestHiddenFieldsList, this.fieldsForRegistrationForm[index], index, guestIndex);
              }
            }
          });
          this.setDoubleGoToForVenueRouting();
        }

        const visibleFieldIds = [];

        for (const field of this.fieldsForRegistrationForm) {
          if (field.customFieldType === CustomFieldType.SingleSelect || field.customFieldType === CustomFieldType.MultipleSelect) {
            const customFields = this.invitationAttendees.guests[guestIndex].customFields;
            if (customFields.length) {
              const customField = customFields.find((x) => x.id === field.id);
              const selectedFieldOptions = customField ? customField.customFieldOptions : [];
              this.initDisabledOptions(field, selectedFieldOptions, guestIndex);
              field.optionsGoToQuestion.forEach((option) => {
                if (selectedFieldOptions.length) {
                  const isOptionSelected = selectedFieldOptions.some((x) => x.id === option.customFieldOptionId);
                  option.goToQuestions.forEach((goTo) => {
                    this.setDoubleGoTo(field, goTo, guestIndex);
                    if (!isOptionSelected) {
                      const hiddenField = this.fieldsForRegistrationForm.find((el) => el.order === goTo);
                      if (hiddenField && !visibleFieldIds.includes(goTo)) {
                        this.updateShowedFields(goTo, guestIndex + 1);
                        this.hideQuestion(guestHiddenFieldsList, this.fieldsForRegistrationForm[goTo], goTo, guestIndex);
                      }
                    } else {
                      visibleFieldIds.push(goTo);
                      this.currentShowedFields[guestIndex + 1].push(goTo);
                      this.showNextQuestion(guestHiddenFieldsList, goTo, guestIndex);
                    }
                  });
                } else {
                  option.goToQuestions.forEach((goTo) => {
                    this.setDoubleGoTo(field, goTo, guestIndex);
                    const hiddenField = this.fieldsForRegistrationForm.find((el) => el.order === goTo);
                    if (hiddenField && !visibleFieldIds.includes(goTo)) {
                      this.updateShowedFields(goTo, guestIndex + 1);
                      this.hideQuestion(guestHiddenFieldsList, this.fieldsForRegistrationForm[goTo], goTo, guestIndex);
                    }
                  });
                }
              });
              if (field.customFieldType === CustomFieldType.SingleSelect && selectedFieldOptions.length) {
                this.previousSingleSelectValues[guestIndex + 1][field.id] = selectedFieldOptions[0].id;
              }
            }
          } else if (field.customFieldType === CustomFieldType.Boolean) {
            const customFields = this.invitationAttendees.guests[guestIndex].customFields;
            if (customFields.length) {
              const customField = customFields.find((x) => x.id === field.id);
              const value = customField?.booleanValue;
              field.optionsGoToQuestion.forEach((option) => {
                const isOptionSelected = ((option.index === 0 && value) || (option.index === 1 && !value));
                option.goToQuestions.forEach((goTo) => {
                  this.setDoubleGoTo(field, goTo, guestIndex);
                  if (!isOptionSelected) {
                    const hiddenField = this.fieldsForRegistrationForm.find((el) => el.order === goTo);
                    if (hiddenField && !visibleFieldIds.includes(goTo)) {
                      this.updateShowedFields(goTo, guestIndex + 1);
                      this.hideQuestion(guestHiddenFieldsList, this.fieldsForRegistrationForm[goTo], goTo, guestIndex);
                    }
                  } else {
                    visibleFieldIds.push(goTo);
                    this.currentShowedFields[guestIndex + 1].push(goTo);
                    this.showNextQuestion(guestHiddenFieldsList, goTo, guestIndex);
                  }
                });
              });
              if (value) {
                const fieldOption = field.customFieldOptions.find(x => x.index === 0);
                this.previousSingleSelectValues[guestIndex + 1][field.id] = fieldOption.id;
              } else {
                const fieldOption = field.customFieldOptions.find(x => x.index === 1);
                this.previousSingleSelectValues[guestIndex + 1][field.id] = fieldOption.id;
              }
            }
          } else {
            const controlName = field.id || field.type;
            const controlGuest = this.registrationForm.controls['guests']['controls'][guestIndex].controls[controlName];
            const curFieldIndex = this.fieldsForRegistrationForm.indexOf(field);
            field.fieldGoToQuestion.forEach((goTo) => {
              this.setDoubleGoTo(field, goTo, guestIndex);
              if (controlGuest && !controlGuest.value) {
                const hiddenField = this.fieldsForRegistrationForm.find((el) => el.order === goTo);
                if (hiddenField && !visibleFieldIds.includes(goTo)) {
                  this.updateShowedFields(goTo, guestIndex + 1);
                  this.hideQuestion(guestHiddenFieldsList, this.fieldsForRegistrationForm[goTo], goTo, guestIndex);
                }
              } else if (controlGuest && controlGuest.value && !this.isHiddenField(curFieldIndex, guestHiddenFieldsList)) {
                visibleFieldIds.push(goTo);
                this.currentShowedFields[guestIndex + 1].push(goTo);
                this.showNextQuestion(guestHiddenFieldsList, goTo, guestIndex);
              }
            });
          }
        }
        this.hiddenGuestsFields[guestIndex] = [ ...guestHiddenFieldsList];
      });
    }
  }

  public setInitFieldsValues(showedFields, data, guestIndex) {
    let currentShowedFields = showedFields[0];
    let attendeeData = data;
    let formGroup = this.registrationForm;
    if (guestIndex !== null) {
      currentShowedFields = showedFields[guestIndex + 1];
      attendeeData = data.guests[guestIndex];
      formGroup = this.registrationForm.controls['guests']['controls'][guestIndex];
    }
    currentShowedFields = currentShowedFields.filter((v, i, a) => a.indexOf(v) === i);
    currentShowedFields.forEach(index => {
      const field = this.fieldsForRegistrationForm.find((el) => el.order === index);
      if (field) {
        const controlName = field.type ? field.type : field.id;
        switch (controlName) {
          case 'address':
            this.addressFields.forEach((key) => {
              if (formGroup.get(key)) {
                formGroup.get(key).patchValue(attendeeData[key]);
              }
            });
            break;
          case 'company':
            if (formGroup.get(controlName)) {
              formGroup.get(controlName).patchValue(attendeeData['companyName']);
            }
            break;
          case 'jobTitle':
            if (formGroup.get(controlName)) {
              formGroup.get(controlName).patchValue(attendeeData['jobTitle']);
            }
            break;
          case 'customerNumber':
            if (attendeeData['customerNumber']) {
              if (formGroup.get(controlName)) {
                formGroup.get(controlName).patchValue(attendeeData['customerNumber']);
              }
            }
            break;
          case 'mobile':
            if (formGroup.get(controlName)) {
              formGroup.get(controlName).patchValue(attendeeData['mobile']);
            }
            break;
          default:
            const attendeeCustomField = attendeeData.customFields.find((x) => x.id === field.id);
            if (attendeeCustomField) {
              if (field.customFieldType === CustomFieldType.MultipleSelect || field.customFieldType === CustomFieldType.SingleSelect) {
                const controls = field.customFieldOptions.map((c) => {
                  const isPresent = attendeeCustomField.customFieldOptions.find((x) => x.id === c.id);
                  if (isPresent) {
                    return c.id;
                  } else {
                    return null;
                  }
                });
                formGroup.get(controlName).patchValue(controls);
              } else if (field.customFieldType === CustomFieldType.Boolean) {
                const controlValue = attendeeCustomField?.booleanValue;
                formGroup.get(controlName).patchValue(controlValue);
              } else {
                let controlValue;
                switch (field.freeTextFormat) {
                  case (FreeTextFormat.Text): {
                    controlValue = attendeeCustomField.textValue;
                    break;
                  }
                  case (FreeTextFormat.CountryCode): {
                    controlValue = attendeeCustomField.textValue;
                    break;
                  }
                  case (FreeTextFormat.Number): {
                    controlValue = attendeeCustomField.numberValue || attendeeCustomField.numberValue === 0 ?  +attendeeCustomField.numberValue : null;
                    break;
                  }
                  case (FreeTextFormat.Date): {
                    attendeeCustomField.dateTimeValue ? controlValue = new Date(attendeeCustomField.dateTimeValue) : controlValue = null;
                    break;
                  }
                  case (FreeTextFormat.Time): {
                    controlValue = SetDateBasedOnTimeString(attendeeCustomField.textValue);
                    break;
                  }
                }
                formGroup.get(controlName).patchValue(controlValue);
              }
            }
            break;
        }
      }
    });
  }

  public setDoubleGoTo(field, goTo, guestIndex) {
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    if (this.doubleGoTo[arrIndex][goTo]) {
      this.doubleGoTo[arrIndex][goTo] = [...this.doubleGoTo[arrIndex][goTo], field];
    } else {
      this.doubleGoTo[arrIndex][goTo] = [field];
    }
  }

  public initDisabledOptions(field, selectedFieldOptions, guestIndex = null) {
    let disabledOptions = this.multiDisabledOptions[0];
    if (guestIndex !== null) {
      if (this.multiDisabledOptions[guestIndex + 1]) {
        disabledOptions = this.multiDisabledOptions[guestIndex + 1];
      } else {
        disabledOptions = this.multiDisabledOptions[guestIndex + 1] = { [this.locationDateTimeId]: { } };
      }
    }
    selectedFieldOptions.forEach((x) => {
      Object.keys(x.entered).forEach((venueId) => {
        if (x.maxEntries && x.entered[venueId]) {
          disabledOptions[venueId] = disabledOptions[venueId] || { };
          disabledOptions[venueId][field.id] = disabledOptions[venueId][field.id] || [];
          disabledOptions[venueId][field.id].push(x.id);
        }
      });
    });
  }

 public getSelectedCustomFieldOptionIds(field, controlValue): string[] {
    if (controlValue === null || controlValue === undefined) {
      return [];
    }
    if (Array.isArray(controlValue)) {
      if (controlValue.length && controlValue.every((value) => typeof value === 'boolean')) {
        return field.customFieldOptions
          .filter((option, index) => controlValue[index])
          .map((option) => option.id);
      }
      return controlValue.filter((value) => typeof value === 'string' && !!value);
    }
    if (typeof controlValue === 'string') {
      return [controlValue];
    }
    return [];
  }

  public getDefaultHiddenFieldList(guestIndex, hiddenFieldIndexesPerVenue) {
    // Case Multivenue -> "Venue selection per attendee", all custom fields must be hidden firstly
    // Until user select a venue and we unshow hidden fields
    if(this.isMultiVenue && this.multiVenueType === MultiVenueType.Attendee &&
      (this.maxVenueType === MaxVenueType.Attendee || this.maxVenueType === MaxVenueType.Registration) && (this.maxVenues > 0 || this.maxVenues === null)){
        const defaultHiddenFieldIndexesGuest = [];
        this.invitationRegistrationPage.locationDateTimeEventPageFields.forEach((element) => {
          element.goToQuestion.forEach((goTo) => {
            defaultHiddenFieldIndexesGuest.push(goTo);
          });
        });
        hiddenFieldIndexesPerVenue = defaultHiddenFieldIndexesGuest;
    }else if(this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && (this.maxVenues > 1 || this.maxVenues === null)){
      const defaultHiddenFieldIndexesGuest = [];
        this.invitationRegistrationPage.locationDateTimeEventPageFields.forEach((element) => {
          element.goToQuestion.forEach((goTo) => {
            defaultHiddenFieldIndexesGuest.push(goTo);
          });
        });
        hiddenFieldIndexesPerVenue = defaultHiddenFieldIndexesGuest;
    }
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    const defaultHiddenList = [];
    if (hiddenFieldIndexesPerVenue.length) {
      hiddenFieldIndexesPerVenue.forEach((element) => {
        if (!defaultHiddenList.some((el) => el.order === element)) {
          if (this.canHideQuestion(this.fieldsForRegistrationForm[element], element, null)) {
            this.updateShowedFields(element, arrIndex);
            this.hideQuestion(defaultHiddenList, this.fieldsForRegistrationForm[element], element, guestIndex);
          }
        }
      });
      this.setDoubleGoToForVenueRouting(guestIndex);
    }
    for (const field of this.fieldsForRegistrationForm) {
      if (field.customFieldType === CustomFieldType.SingleSelect || field.customFieldType === CustomFieldType.MultipleSelect) {
        // Honour a pre-filled value (e.g. copied from a contact custom field): reveal the
        // routed questions of the options that are already selected and hide the rest.
        // Without this, routing only triggered once the option was manually (re)selected,
        // because the initial default hid every routed question unconditionally. (ID-4533)
        let formGroup = this.registrationForm;
        if (guestIndex !== null) {
          formGroup = this.registrationForm?.controls['guests']['controls'][guestIndex];
        }
        const controlName = field.type ? field.type : field.id;
        const control = formGroup?.controls[controlName];
        const selectedOptionIds = this.getSelectedCustomFieldOptionIds(field, control?.value);

        const visibleGoTos = new Set();
        field.optionsGoToQuestion.forEach((option) => {
          if (selectedOptionIds.includes(option.customFieldOptionId)) {
            option.goToQuestions.forEach((goTo) => visibleGoTos.add(goTo));
          }
        });

        field.optionsGoToQuestion.forEach((option) => {
          option.goToQuestions.forEach((goTo) => {
            this.setDoubleGoTo(field, goTo, guestIndex);
            const hiddenField = this.fieldsForRegistrationForm.find((el) => el.order === goTo);
            if (visibleGoTos.has(goTo)) {
              if (!this.currentShowedFields[arrIndex].includes(goTo)) {
                this.currentShowedFields[arrIndex].push(goTo);
              }
              this.showNextQuestion(defaultHiddenList, goTo, guestIndex);
            } else if (hiddenField && !defaultHiddenList.includes(hiddenField)) {
              this.updateShowedFields(goTo, arrIndex);
              this.hideQuestion(defaultHiddenList, this.fieldsForRegistrationForm[goTo], goTo, guestIndex);
            }
          });
        });

        // Remember the pre-filled single-select option so a later manual change hides it correctly.
        if (field.customFieldType === CustomFieldType.SingleSelect && selectedOptionIds.length) {
          if (this.previousSingleSelectValues[arrIndex]) {
            this.previousSingleSelectValues[arrIndex][field.id] = selectedOptionIds[0];
          } else {
            this.previousSingleSelectValues.push({ });
            this.previousSingleSelectValues[arrIndex][field.id] = selectedOptionIds[0];
          }
        }
      } else if (field.customFieldType === CustomFieldType.Boolean) {
        let option: any;
        if (field.checkboxFormat === CheckboxFormat.OptIn) {
          option = field.optionsGoToQuestion.find(x => x.index === 0);
          const fieldOption = field.customFieldOptions.find(x => x.index === 1);
          if (this.previousSingleSelectValues[arrIndex]) {
            this.previousSingleSelectValues[arrIndex][field.id] = fieldOption.id;
          } else {
            this.previousSingleSelectValues.push({ });
            this.previousSingleSelectValues[arrIndex][field.id] = fieldOption.id;
          }
        } else {
          option = field.optionsGoToQuestion.find(x => x.index === 1);
          const fieldOption = field.customFieldOptions.find(x => x.index === 0);
          if (this.previousSingleSelectValues[arrIndex]) {
            this.previousSingleSelectValues[arrIndex][field.id] = fieldOption.id;
          } else {
            this.previousSingleSelectValues.push({ });
            this.previousSingleSelectValues[arrIndex][field.id] = fieldOption.id;
          }
        }
        if (option) {
          option.goToQuestions.forEach((goTo) => {
            this.setDoubleGoTo(field, goTo, guestIndex);
            const hiddenField = this.fieldsForRegistrationForm.find((el) => el.order === goTo);
            if (hiddenField && !defaultHiddenList.includes(hiddenField)) {
              this.updateShowedFields(goTo, arrIndex);
              this.hideQuestion(defaultHiddenList, this.fieldsForRegistrationForm[goTo], goTo, guestIndex);
            }
          });
        }
      } else {
        let formGroup = this.registrationForm;
        if (guestIndex !== null) {
          formGroup = this.registrationForm.controls['guests']['controls'][guestIndex];
        }
        const controlName = field.type ? field.type : field.id;
        const control = formGroup.controls[controlName];
        const curFieldIndex = this.fieldsForRegistrationForm.indexOf(field);
        field.fieldGoToQuestion.forEach((goTo) => {
          this.setDoubleGoTo(field, goTo, guestIndex);
          if (control && !control.value) {
            const hiddenField = this.fieldsForRegistrationForm.find((el) => el.order === goTo);
            if (hiddenField && !defaultHiddenList.includes(hiddenField)) {
              this.updateShowedFields(goTo, arrIndex);
              this.hideQuestion(defaultHiddenList, this.fieldsForRegistrationForm[goTo], goTo, guestIndex);
            }
          } else if (control && control.value && !this.isHiddenField(curFieldIndex, defaultHiddenList)) {
            this.currentShowedFields[arrIndex].push(goTo);
            this.showNextQuestion(defaultHiddenList, goTo, guestIndex);
          }
        });
      }
    }
    return defaultHiddenList;
  }

  public isHiddenField(index, hiddenFields) {
    return hiddenFields.some((el) => el.order === index);
  }

  public updateShowedFields(goTo, arrIndex) {
    const goToIndex = this.currentShowedFields[arrIndex].indexOf(goTo);
    if (goToIndex !== -1) {
      this.currentShowedFields[arrIndex].splice(goToIndex, 1);
    }
  }

  public inputTextField(e, hiddenFields, field, controlName, guestIndex = null) {
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    const valueLength = e.target.value.length;
    const fieldIndexes = field.fieldGoToQuestion;
    if (valueLength && field.dirty !== guestIndex) {
      this.reinitForm();
      this.form = Object.assign(this.form, { form: this.registrationForm });
      field.dirty = guestIndex;
      this.currentShowedFields[arrIndex] = [...this.currentShowedFields[arrIndex], ...fieldIndexes];
      fieldIndexes.forEach((index) => {
        this.showNextQuestion(hiddenFields, index, guestIndex);
      });
    } else if (!valueLength) {
      this.reinitForm();
      this.form = Object.assign(this.form, { form: this.registrationForm });
      fieldIndexes.forEach((index) => {
        this.updateShowedFields(index, arrIndex);
        this.hideQuestion(hiddenFields, field, index, guestIndex);
      });
      delete field.dirty;
    }
    // set focus after reinit controls (only for guest control)
    // if (guestIndex !== null) {
    //   // TODO ukrinsoft check if need to set focus on change
    //   setTimeout(() => {
    //     const controlElement = document.getElementById(`guest-${guestIndex}${controlName}`);
    //     controlElement.focus();
    //   }, 0);
    // }
  }

  public modifyEnteredOptionsMultiVenuePerRegistration(value, remove: boolean){
    if(remove){
      this.fieldsForRegistrationForm.forEach((field)=>{
        const { isCustomFieldRouted } = this.customFieldHasRouting(field);
        if(isCustomFieldRouted){
          this.deleteEnteredOptionsLocationDateTimeIdMultiVenuePerRegistration(field, field.customFieldOptions, value);
        }
      });
    }else{
      this.fieldsForRegistrationForm.forEach((field)=>{
        const { isCustomFieldRouted, locationDateTimeIdsRouted } = this.customFieldHasRouting(field);
        if(isCustomFieldRouted){
          this.addEnteredOptionsLocationDateTimeIdMultiVenuePerRegistration(field, field.customFieldOptions, value, locationDateTimeIdsRouted);
        }
      });
    }
    this.onChangeVenueSelectionPerRegistration();
  }

  public modifyEnteredOptionsMultiVenuePerAttendee(value, remove: boolean, guestId){
    if(remove){
      this.fieldsForRegistrationForm.forEach((field) =>{
        const { isCustomFieldRouted } = this.customFieldHasRouting(field);
        if(isCustomFieldRouted){
          this.deleteEnteredOptionsLocationDateTimeIdMultiVenuePerAttendee(field, field.customFieldOptions, value, guestId);
        }
      });
    }else{
      this.fieldsForRegistrationForm.forEach((field)=>{
        const { isCustomFieldRouted, locationDateTimeIdsRouted } = this.customFieldHasRouting(field);
        if(isCustomFieldRouted){
          this.addEnteredOptionsLocationDateTimeIdMultiVenuePerAttendee(field, field.customFieldOptions, value, locationDateTimeIdsRouted, guestId);
        }
      });
    }
    // Change venue routing multivenue per attendee flow
    this.changeVenueRoutingMultiVenuePerAttendee(this.multiVenueSelectionPerAttendee);
    this.refreshFieldOptionsMultiVenue();
  }

  public addEnteredOptionsLocationDateTimeIdMultiVenuePerRegistration(field, fieldOptions, newVenue, locationDateTimeIdsRouted){
    // Main Invitee
    if(this.registrationForm.value[field.id]){
      this.registrationForm.value[field.id] = Array.isArray(this.registrationForm.value[field.id]) ? this.registrationForm.value[field.id] : [this.registrationForm.value[field.id]];
      for (let i = this.registrationForm.value[field.id].length - 1; i >= 0; i--) {
        const selectedOptionId = this.registrationForm.value[field.id][i];
        if(field.selectFormat === SelectFormat.CheckboxRadio){
          const fieldOption = fieldOptions[i];
          if(selectedOptionId && locationDateTimeIdsRouted.includes(newVenue)){
            fieldOption.entered[newVenue]++;
            if(fieldOption.maxEntries && fieldOption.entered[newVenue] > fieldOption.maxEntries){
              const mainInviteeControl = (this.registrationForm.get(field.id) as FormArray);
              const valuesArray = mainInviteeControl.get(field.id).value;
              if (Array.isArray(valuesArray) && valuesArray.length > i) {
                valuesArray[i] = false;
                mainInviteeControl.get(field.id).setValue(valuesArray);
              }
              fieldOption.entered[newVenue]--;
              const guestIndex = null;
              this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered--;
              if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked){
                delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked[guestIndex];
              }
              this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex =
              this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex?.filter(value => value !== guestIndex);
              if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered === 0){
                delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex;
              }
            }
          }
        }else{
          fieldOptions.forEach(fieldOption => {
            if(fieldOption.id === selectedOptionId && locationDateTimeIdsRouted.includes(newVenue)){
              fieldOption.entered[newVenue]++;
              if(fieldOption.maxEntries && fieldOption.entered[newVenue] > fieldOption.maxEntries){
                this.registrationForm.value[field.id].splice(i, 1);
                // fieldOption.entered[newVenue]--;
                const mainInviteeControl = (this.registrationForm.get(field.id) as FormArray);
                mainInviteeControl.setValue(this.registrationForm.value[field.id]);
                const routedSelectedLocationDateTimeId = this.locationDateTimeIdsPerRegistration.filter((locationDateTimeId) =>
                  locationDateTimeIdsRouted.includes(locationDateTimeId)
                );
                routedSelectedLocationDateTimeId.forEach((locationDateTimeId)=>{
                  fieldOption.entered[locationDateTimeId]--;
                });
              }
            }
          });
        }
      }
    }
    // Additional guests
    if(this.registrationForm.value.guests){
      this.registrationForm.value.guests.forEach((guest, guestIndex)=>{
      guest[field.id] = Array.isArray( guest[field.id]) ?  guest[field.id] : [guest[field.id]];
        if(guest[field.id]){
          guest[field.id].forEach((selectedOptionId, index) => {
            if(field.selectFormat === SelectFormat.CheckboxRadio){
                const fieldOption = fieldOptions[index];
                if(selectedOptionId && locationDateTimeIdsRouted.includes(newVenue)){
                  fieldOption.entered[newVenue]++;
                  if(fieldOption.maxEntries && fieldOption.entered[newVenue] > fieldOption.maxEntries){
                    const guestControl = (this.registrationForm.get('guests') as FormArray).at(guestIndex);
                    const valuesArray = guestControl.get(field.id).value;
                    if (Array.isArray(valuesArray) && valuesArray.length > index) {
                      valuesArray[index] = false;
                      guestControl.get(field.id).setValue(valuesArray);
                    }
                    const routedSelectedLocationDateTimeId = this.locationDateTimeIdsPerRegistration.filter((locationDateTimeId) =>
                      locationDateTimeIdsRouted.includes(locationDateTimeId)
                    );
                    routedSelectedLocationDateTimeId.forEach((locationDateTimeId)=>{
                      fieldOption.entered[locationDateTimeId]--;
                    });
                    this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered--;
                    if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked){
                      delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked[guestIndex];
                    }
                    this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex =
                    this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex?.filter(value => value !== guestIndex);
                    if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered === 0){
                      delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex;
                    }
                  }
                }
            }else{
              fieldOptions.forEach(fieldOption => {
                if(fieldOption.id === selectedOptionId && locationDateTimeIdsRouted.includes(newVenue)){
                  fieldOption.entered[newVenue]++;
                  if(fieldOption.maxEntries && fieldOption.entered[newVenue] > fieldOption.maxEntries){
                    guest[field.id].splice(index, 1);
                    const guestControl = (this.registrationForm.get('guests') as FormArray).at(guestIndex);
                    guestControl.get(field.id).setValue(guest[field.id]);
                    const routedSelectedLocationDateTimeId = this.locationDateTimeIdsPerRegistration.filter((locationDateTimeId) =>
                      locationDateTimeIdsRouted.includes(locationDateTimeId)
                    );
                    routedSelectedLocationDateTimeId.forEach((locationDateTimeId)=>{
                      fieldOption.entered[locationDateTimeId]--;
                    });
                  }
                }
              });
            }
          });
        }
      });
    }
  }

  public addEnteredOptionsLocationDateTimeIdMultiVenuePerAttendee(field, fieldOptions, newVenue, locationDateTimeIdsRouted, guestId){
    if(guestId === 'mainInvitee'){
      if(this.registrationForm.value[field.id]){
        this.registrationForm.value[field.id] = Array.isArray(this.registrationForm.value[field.id]) ? this.registrationForm.value[field.id] : [this.registrationForm.value[field.id]];
        for (let i = this.registrationForm.value[field.id].length - 1; i >= 0; i--) {
          const selectedOptionId = this.registrationForm.value[field.id][i];
          if(field.selectFormat === SelectFormat.CheckboxRadio){
            const fieldOption = fieldOptions[i];
            if(selectedOptionId && locationDateTimeIdsRouted.includes(newVenue)){
              fieldOption.entered[newVenue]++;
              if(fieldOption.maxEntries && fieldOption.entered[newVenue] > fieldOption.maxEntries){
                const mainInviteeControl = (this.registrationForm.get(field.id) as FormArray);
                const valuesArray = mainInviteeControl.get(field.id).value;
                if (Array.isArray(valuesArray) && valuesArray.length > i) {
                  valuesArray[i] = false;
                  mainInviteeControl.get(field.id).setValue(valuesArray);
                }
                fieldOption.entered[newVenue]--;
                const guestIndex = null;
                this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered--;
                if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked){
                  delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked[guestIndex];
                }
                this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex =
                this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex?.filter(value => value !== guestIndex);
                if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered === 0){
                  delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex;
                }
              }
            }
          }else{
            fieldOptions.forEach(fieldOption => {
              if(fieldOption.id === selectedOptionId && locationDateTimeIdsRouted.includes(newVenue)){
                fieldOption.entered[newVenue]++;
                if(fieldOption.maxEntries && fieldOption.entered[newVenue] > fieldOption.maxEntries){
                  this.registrationForm.value[field.id].splice(i, 1);
                  const mainInviteeControl = (this.registrationForm.get(field.id) as FormArray);
                  mainInviteeControl.setValue(this.registrationForm.value[field.id]);
                  const mainInviteeLdt = this.multiVenueSelectionPerAttendee['mainInvitee'];
                  if (mainInviteeLdt) {
                    const routedSelectedLocationDateTimeIds = mainInviteeLdt.filter((locationDateTimeId) =>
                      locationDateTimeIdsRouted.includes(locationDateTimeId)
                    );
                    routedSelectedLocationDateTimeIds.forEach((locationDateTimeId) => {
                      fieldOption.entered[locationDateTimeId]--;
                    });
                  }
                }
              }
            });
          }
        }
      }
    }else{
      if(this.registrationForm.value.guests){
        const filteredGuest = this.registrationForm.value.guests.filter(guest => guest.guestId === guestId);
        const guestIndex = this.registrationForm.value.guests.findIndex(guest => guest.guestId === guestId);
        if(filteredGuest[0][field.id]){
         filteredGuest[0][field.id] = Array.isArray( filteredGuest[0][field.id]) ?  filteredGuest[0][field.id] : [filteredGuest[0][field.id]];
          filteredGuest[0][field.id].forEach((selectedOptionId, index) => {
            if(field.selectFormat === SelectFormat.CheckboxRadio){
              const fieldOption = fieldOptions[index];
                  if(selectedOptionId && locationDateTimeIdsRouted.includes(newVenue)){
                    fieldOption.entered[newVenue]++;
                    if(fieldOption.maxEntries && fieldOption.entered[newVenue] > fieldOption.maxEntries){
                      const guestsFormArray = this.registrationForm.get('guests') as FormArray;
                      const guestControl = guestsFormArray.controls.find((control: AbstractControl) => control.value.guestId === guestId);
                      const valuesArray = guestControl.get(field.id).value;
                      if (Array.isArray(valuesArray) && valuesArray.length > index) {
                        valuesArray[index] = false;
                        guestControl.get(field.id).setValue(valuesArray);
                      }
                      const currentGuest = this.multiVenueSelectionPerAttendee[guestId];
                      if (currentGuest) {
                        const routedSelectedLocationDateTimeIds = currentGuest.filter((locationDateTimeId) =>
                          locationDateTimeIdsRouted.includes(locationDateTimeId)
                        );
                        routedSelectedLocationDateTimeIds.forEach((locationDateTimeId) => {
                          fieldOption.entered[locationDateTimeId]--;
                        });
                      }
                      this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered--;
                      if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked){
                        delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked[guestIndex];
                      }
                      this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex =
                      this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex?.filter(value => value !== guestIndex);
                      if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered === 0){
                        delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex;
                      }
                    }
                  }
            }else{
              fieldOptions.forEach(fieldOption => {
                if(fieldOption.id === selectedOptionId && locationDateTimeIdsRouted.includes(newVenue)){
                  fieldOption.entered[newVenue]++;
                  if(fieldOption.maxEntries && fieldOption.entered[newVenue] > fieldOption.maxEntries){
                    filteredGuest[0][field.id].splice(index, 1);
                    const guestsFormArray = this.registrationForm.get('guests') as FormArray;
                    const guestControl = guestsFormArray.controls.find((control: AbstractControl) => control.value.guestId === guestId);
                    guestControl.get(field.id).setValue(filteredGuest[0][field.id]);
                    const currentGuest = this.multiVenueSelectionPerAttendee[guestId];
                    if (currentGuest) {
                      const routedSelectedLocationDateTimeIds = currentGuest.filter((locationDateTimeId) =>
                        locationDateTimeIdsRouted.includes(locationDateTimeId)
                      );
                      routedSelectedLocationDateTimeIds.forEach((locationDateTimeId) => {
                        fieldOption.entered[locationDateTimeId]--;
                      });
                    }
                  }
                }
              });
            }
          });
        }
      }
    }
  }

  public deleteEnteredOptionsLocationDateTimeIdMultiVenuePerRegistration(field, fieldOptions, deletedVenue){
    if(this.registrationForm.value[field.id]){ // Main invitee
      this.registrationForm.value[field.id] = Array.isArray(this.registrationForm.value[field.id]) ? this.registrationForm.value[field.id] : [this.registrationForm.value[field.id]];
      for (let i = this.registrationForm.value[field.id].length - 1; i >= 0; i--) {
        const selectedOptionId = this.registrationForm.value[field.id][i];
        if(field.selectFormat === SelectFormat.CheckboxRadio){
          if(selectedOptionId){
            const fieldOption = fieldOptions[i];
            fieldOption.entered[deletedVenue]--;
            const mainInviteeIndex = null;
            if(this.previousSelectedOption[mainInviteeIndex] && this.previousSelectedOption[mainInviteeIndex][field.id]){
              delete this.previousSelectedOption[mainInviteeIndex][field.id];
            }
          }
        }else{
          fieldOptions.forEach(fieldOption => {
            if(fieldOption.id === selectedOptionId){
              fieldOption.entered[deletedVenue]--;
              const mainInviteeIndex = null;
              if(this.previousSelectedOption[mainInviteeIndex] && this.previousSelectedOption[mainInviteeIndex][field.id]){
                delete this.previousSelectedOption[mainInviteeIndex][field.id];
              }
            }
          });
        }
      }
    }
    if(this.registrationForm.value.guests){ // Additional guests
      this.registrationForm.value.guests.forEach((guest, guestIndex)=>{
        if(guest[field.id]){
          guest[field.id] = Array.isArray(guest[field.id]) ? guest[field.id] : [guest[field.id]];
          guest[field.id].forEach((selectedOptionId, index) => {
            if(field.selectFormat === SelectFormat.CheckboxRadio){
              if(selectedOptionId){
                const fieldOption = fieldOptions[index];
                fieldOption.entered[deletedVenue]--;
                if(this.previousSelectedOption[guestIndex] && this.previousSelectedOption[guestIndex][field.id]){
                  delete this.previousSelectedOption[guestIndex][field.id];
                }
              }
            }else{
              fieldOptions.forEach(fieldOption => {
                if(fieldOption.id === selectedOptionId){
                  fieldOption.entered[deletedVenue]--;
                  if(this.previousSelectedOption[guestIndex] && this.previousSelectedOption[guestIndex][field.id]){
                    delete this.previousSelectedOption[guestIndex][field.id];
                  }
                }
              });
            }
          });
        }
      });
    }
  }

  public deleteEnteredOptionsLocationDateTimeIdMultiVenuePerAttendee(field, fieldOptions, deletedVenue, guestId){
    if(guestId === 'mainInvitee'){
      if(this.registrationForm.value[field.id]){
        this.registrationForm.value[field.id] = Array.isArray(this.registrationForm.value[field.id]) ? this.registrationForm.value[field.id] : [this.registrationForm.value[field.id]];
        for (let i = this.registrationForm.value[field.id].length - 1; i >= 0; i--) {
          const selectedOptionId = this.registrationForm.value[field.id][i];
          if(field.selectFormat === SelectFormat.CheckboxRadio){
            if(selectedOptionId){
              const fieldOption = fieldOptions[i];
              fieldOption.entered[deletedVenue]--;
              const maniInviteeIndex = null;
              if(this.previousSelectedOption[maniInviteeIndex] && this.previousSelectedOption[maniInviteeIndex][field.id]){
                delete this.previousSelectedOption[maniInviteeIndex][field.id];
              }
            }
          }else{
            fieldOptions.forEach(fieldOption => {
              if(fieldOption.id === selectedOptionId){
                fieldOption.entered[deletedVenue]--;
                const maniInviteeIndex = null;
                if(this.previousSelectedOption[maniInviteeIndex] && this.previousSelectedOption[maniInviteeIndex][field.id]){
                  delete this.previousSelectedOption[maniInviteeIndex][field.id];
                }
              }
            });
          }
        }
      }
    }else{ // Additional guests
      if(this.registrationForm.value.guests){
        this.registrationForm.value.guests.forEach((guest, guestIndex)=>{
          guest[field.id] = Array.isArray(guest[field.id]) ? guest[field.id] : [guest[field.id]];
          if(guest[field.id] && guest.guestId === guestId){
            guest[field.id].forEach((selectedOptionId, index) => {
              if(field.selectFormat === SelectFormat.CheckboxRadio){
                if(selectedOptionId){
                  const fieldOption = fieldOptions[index];
                  fieldOption.entered[deletedVenue]--;
                  if(this.previousSelectedOption[guestIndex] && this.previousSelectedOption[guestIndex][field.id]){
                    delete this.previousSelectedOption[guestIndex][field.id];
                  }
                }
              }else{
                fieldOptions.forEach(fieldOption => {
                  if(fieldOption.id === selectedOptionId){
                    fieldOption.entered[deletedVenue]--;
                    if(this.previousSelectedOption[guestIndex] && this.previousSelectedOption[guestIndex][field.id]){
                      delete this.previousSelectedOption[guestIndex][field.id];
                    }
                  }
                });
              }
            });
          }
        });
      }
    }
  }

  public selectDateTime(e, hiddenFields, fieldIndexes, guestIndex = null) {
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    this.currentShowedFields[arrIndex] = [...this.currentShowedFields[arrIndex], ...fieldIndexes];
    fieldIndexes.forEach((index) => {
      this.showNextQuestion(hiddenFields, index, guestIndex);
    });
    this.reinitForm();
    this.form = Object.assign(this.form, { form: this.registrationForm });
  }

  public clearDateTime(e, field, hiddenFields, fieldIndexes, guestIndex = null) {
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    fieldIndexes.forEach((index) => {
      this.updateShowedFields(index, arrIndex);
      this.hideQuestion(hiddenFields, field, index, guestIndex);
    });
  }

  public inputCheckbox(e, hiddenFields, field, controlName, guestIndex = null) {
    let formGroup = this.registrationForm;
    if (guestIndex !== null) {
      formGroup = this.registrationForm.controls['guests']['controls'][guestIndex];
    }
    const control = formGroup.controls[controlName];
    const value = control.value;
    let fieldOption;
    let optionIndex;
    let defaultPrevOption;

    if (value) {
      fieldOption = field.customFieldOptions.find(x => x.index === 0);
      defaultPrevOption = field.optionsGoToQuestion.find(x => x.index === 0);
      optionIndex = 0;
    } else {
      fieldOption = field.customFieldOptions.find(x => x.index === 1);
      defaultPrevOption = field.optionsGoToQuestion.find(x => x.index === 1);
      optionIndex = 1;
    }

    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    const goToOption = field.optionsGoToQuestion.find((op) => fieldOption.id === op.customFieldOptionId);
    const fieldIndexes = goToOption ? goToOption.goToQuestions : [];

    if (this.previousSingleSelectValues[arrIndex][field.id]) {
      const prevOption = field.optionsGoToQuestion.find((op) => {
        return op.customFieldOptionId === this.previousSingleSelectValues[arrIndex][field.id];
      });
      if (prevOption) {
        const sameIndexes = fieldIndexes.filter((ind) => {
          return prevOption.goToQuestions.includes(ind);
        });
        sameIndexes.forEach((el) => {
          this.updateShowedFields(el, arrIndex);
        });
      }
    } else {
      if (defaultPrevOption) {
        const sameIndexes = fieldIndexes.filter((ind) => {
          return defaultPrevOption.goToQuestions.includes(ind);
        });
        sameIndexes.forEach((el) => {
          this.updateShowedFields(el, arrIndex);
        });
      }
    }
    this.currentShowedFields[arrIndex] = [...this.currentShowedFields[arrIndex], ...fieldIndexes];
    fieldIndexes.forEach((index) => {
      this.showNextQuestion(hiddenFields, index, guestIndex);
    });
    field.optionsGoToQuestion.forEach((option) => {
      if (option.customFieldOptionId === this.previousSingleSelectValues[arrIndex][field.id]) {
        option.goToQuestions.forEach((goTo) => {
          if (!fieldIndexes.includes(goTo)) {
            this.updateShowedFields(goTo, arrIndex);
            if (!this.isHiddenField(goTo, hiddenFields)) {
              this.hideQuestion(hiddenFields, field, goTo, guestIndex);
            }
          }
        });
      }
    });
    this.previousSingleSelectValues[arrIndex][field.id] = fieldOption.id; // save prev value
  }

  public changeSingleSelect(e, hiddenFields, fieldOption, optionIndex, field, guestIndex = null) {
    let formGroup = this.registrationForm;
    if (guestIndex !== null) {
      formGroup = this.registrationForm.controls['guests']['controls'][guestIndex];
    }
    const controlName = field.type ? field.type : field.id;
    const control = formGroup.controls[controlName];
    let values = control.value;
    const controlValue = values[optionIndex];
    values = values.map((element, i) => {
      if (i !== optionIndex && controlValue) {
        element = false;
      }
      return element;
    });
    control.setValue(values);
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    this.reinitForm();
    this.form = Object.assign(this.form, { form: this.registrationForm });
    const goToOption = field.optionsGoToQuestion.find((op) => fieldOption.id === op.customFieldOptionId);
    const remove = !control.value[optionIndex];
    this.enterOptionForSingleSelectByMaxEntries(fieldOption, field, guestIndex, !control.value[optionIndex]);
    const fieldIndexes = goToOption ? goToOption.goToQuestions : [];
    if (control.value[optionIndex]) {
      if (this.previousSingleSelectValues[arrIndex][field.id]) {
        const prevOption = field.optionsGoToQuestion.find((op) => {
          return op.customFieldOptionId === this.previousSingleSelectValues[arrIndex][field.id];
        });
        if (prevOption) {
          const sameIndexes = fieldIndexes.filter((ind) => {
            return prevOption.goToQuestions.includes(ind);
          });
          sameIndexes.forEach((el) => {
            this.updateShowedFields(el, arrIndex);
          });
        }
      }
      this.currentShowedFields[arrIndex] = [...this.currentShowedFields[arrIndex], ...fieldIndexes];
      fieldIndexes.forEach((index) => {
        this.showNextQuestion(hiddenFields, index, guestIndex);
      });
      field.optionsGoToQuestion.forEach((option) => {
        if (option.customFieldOptionId === this.previousSingleSelectValues[arrIndex][field.id]) {
          option.goToQuestions.forEach((goTo) => {
            if (!fieldIndexes.includes(goTo)) {
              this.updateShowedFields(goTo, arrIndex);
              if (!this.isHiddenField(goTo, hiddenFields)) {
                this.hideQuestion(hiddenFields, field, goTo, guestIndex);
              }
            }
          });
        }
      });
      this.previousSingleSelectValues[arrIndex][field.id] = fieldOption.id; // save prev value
    } else {
      field.optionsGoToQuestion.forEach((option) => {
        if (option.customFieldOptionId === fieldOption.id) {
          option.goToQuestions.forEach((goTo) => {
            this.updateShowedFields(goTo, arrIndex);
            if (!this.isHiddenField(goTo, hiddenFields)) {
              this.hideQuestion(hiddenFields, field, goTo, guestIndex);
            }
          });
        }
      });
      this.previousSingleSelectValues[arrIndex][field.id] = null;
    }

    if(!this.previousSelectedOption[guestIndex]){
      this.previousSelectedOption[guestIndex] = [];
    }
    if(!this.previousSelectedOption[guestIndex][field.id]){
      this.previousSelectedOption[guestIndex][field.id] = [];
    }
    this.previousSelectedOption[guestIndex][field.id] = fieldOption.id;
    if(remove && this.previousSelectedOption[guestIndex][field.id]){
      delete this.previousSelectedOption[guestIndex][field.id];
    }
  }

  public changeSingleSelectForDropdown(e, hiddenFields, field, remove: boolean, guestIndex = null) {
    const fieldOption = field?.customFieldOptions?.find(x => x.id === e);
    let formGroup = this.registrationForm;
    if (guestIndex !== null) {
      formGroup = this.registrationForm.controls['guests']['controls'][guestIndex];
    }
    const controlName = field.type ? field.type : field.id;
    const control = formGroup.controls[controlName];
    control.setValue(remove ? null : e);

    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    this.reinitForm();

    const goToOption = field.optionsGoToQuestion.find((op) => fieldOption.id === op.customFieldOptionId);
    this.enterOptionForSingleSelectByMaxEntries(fieldOption, field, guestIndex, false, remove);
    const fieldIndexes = goToOption ? goToOption.goToQuestions : [];
    if (control.value) {
      if (this.previousSingleSelectValues[arrIndex][field.id]) {
        const prevOption = field.optionsGoToQuestion.find((op) => {
          return op.customFieldOptionId === this.previousSingleSelectValues[arrIndex][field.id];
        });
        if (prevOption) {
          const sameIndexes = fieldIndexes.filter((ind) => {
            return prevOption.goToQuestions.includes(ind);
          });
          sameIndexes.forEach((el) => {
            this.updateShowedFields(el, arrIndex);
          });
        }
      }
      this.currentShowedFields[arrIndex] = [...this.currentShowedFields[arrIndex], ...fieldIndexes];
      fieldIndexes.forEach((index) => {
        this.showNextQuestion(hiddenFields, index, guestIndex);
      });
      field.optionsGoToQuestion.forEach((option) => {
        if (option.customFieldOptionId === this.previousSingleSelectValues[arrIndex][field.id]) {
          option.goToQuestions.forEach((goTo) => {
            if (!fieldIndexes.includes(goTo)) {
              this.updateShowedFields(goTo, arrIndex);
              if (!this.isHiddenField(goTo, hiddenFields)) {
                this.hideQuestion(hiddenFields, field, goTo, guestIndex);
              }
            }
          });
        }
      });
      this.previousSingleSelectValues[arrIndex][field.id] = fieldOption.id; // save prev value
    } else {
      field.optionsGoToQuestion.forEach((option) => {
        if (option.customFieldOptionId === fieldOption.id) {
          option.goToQuestions.forEach((goTo) => {
            this.updateShowedFields(goTo, arrIndex);
            if (!this.isHiddenField(goTo, hiddenFields)) {
              this.hideQuestion(hiddenFields, field, goTo, guestIndex);
            }
          });
        }
      });
      this.previousSingleSelectValues[arrIndex][field.id] = null;
    }

    if(this.isMultiVenue && this.multiVenueType === MultiVenueType.Attendee){
      this.refreshFieldOptionsMultiVenue();
    }else{
      this.refreshFieldOptions();
    }

    if(!this.previousSelectedOption[guestIndex]){
      this.previousSelectedOption[guestIndex] = [];
    }
    if(!this.previousSelectedOption[guestIndex][field.id]){
      this.previousSelectedOption[guestIndex][field.id] = [];
    }
    this.previousSelectedOption[guestIndex][field.id] = e;
    if(remove && this.previousSelectedOption[guestIndex][field.id]){
      delete this.previousSelectedOption[guestIndex][field.id];
    }
  }

  public changeMultipleSelect(e, hiddenFields, fieldOption, optionIndex, field, guestIndex = null) {
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    this.reinitForm();
    this.form = Object.assign(this.form, { form: this.registrationForm });
    const goToOption = field.optionsGoToQuestion.find((op) => fieldOption.id === op.customFieldOptionId);
    const remove = !e.target.checked;
    const isChecked = e.target.checked;
    this.enterOptionForMultiSelectByMaxEntries(fieldOption, field, guestIndex, remove, isChecked);
    const fieldIndexes = goToOption ? goToOption.goToQuestions : [];
    if (goToOption) {
      let formGroup = this.registrationForm;
      if (guestIndex !== null) {
        formGroup = this.registrationForm.controls['guests']['controls'][guestIndex];
      }
      const controlName = field.type ? field.type : field.id;
      const control = formGroup.controls[controlName];

      if (control.value[optionIndex]) {
        this.currentShowedFields[arrIndex] = [...this.currentShowedFields[arrIndex], ...fieldIndexes];
        fieldIndexes.forEach((index) => {
          this.showNextQuestion(hiddenFields, index, guestIndex);
        });
      } else {
        fieldIndexes.forEach((index) => {
          field.optionsGoToQuestion.forEach((option, i) => {
            let isFieldExistInOtherOption = false;
            control.value.forEach((element, ind) => {
              if (ind !== i && element) {
                isFieldExistInOtherOption = true;
              }
            });
            if (option.customFieldOptionId !== fieldOption.id) {
              if (!control.value[i] && !isFieldExistInOtherOption) {
                this.updateShowedFields(index, arrIndex);
                if (!this.isHiddenField(index, hiddenFields)) {
                  this.hideQuestion(hiddenFields, field, index, guestIndex);
                }
              } else {
                if (!option.goToQuestions.includes(index)) {
                  this.updateShowedFields(index, arrIndex);
                  if (!this.isHiddenField(index, hiddenFields)) {
                    this.hideQuestion(hiddenFields, field, index, guestIndex);
                  }
                }
              }
            } else {
              if (!control.value[i] && !isFieldExistInOtherOption) {
                this.updateShowedFields(index, arrIndex);
                if (!this.isHiddenField(index, hiddenFields)) {
                  this.hideQuestion(hiddenFields, field, index, guestIndex);
                }
              }
            }
          });
        });
      }
    }
  }

  public changeMultipleSelectForDropdown(e, hiddenFields, field, remove: boolean, guestIndex = null) {
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    this.reinitForm();
    const fieldOption = field?.customFieldOptions?.find(x => x.id === e);
    const goToOption = field.optionsGoToQuestion.find((op) => fieldOption.id === op.customFieldOptionId);
    this.enterOptionForMultiSelectByMaxEntries(fieldOption, field, guestIndex, remove);
    const currentControlValue = this.registrationForm.get(field.id)?.value || [];
    const uniqueValues = [...new Set(currentControlValue)];
    const currentControl = this.registrationForm.controls[field.id];
    currentControl.setValue(uniqueValues);
    const fieldIndexes = goToOption ? goToOption.goToQuestions : [];
    if (goToOption) {
      let formGroup = this.registrationForm;
      if (guestIndex !== null) {
        formGroup = this.registrationForm.controls['guests']['controls'][guestIndex];
      }
      const controlName = field.type ? field.type : field.id;
      const control = formGroup.controls[controlName];

      if (!remove) {
        this.currentShowedFields[arrIndex] = [...this.currentShowedFields[arrIndex], ...fieldIndexes];
        fieldIndexes.forEach((index) => {
          this.showNextQuestion(hiddenFields, index, guestIndex);
        });
      } else {
        fieldIndexes.forEach((index) => {
          field.optionsGoToQuestion.forEach((option, i) => {
            let isFieldExistInOtherOption = false;
            control.value.forEach((element, ind) => {
              if (ind !== i && element) {
                isFieldExistInOtherOption = true;
              }
            });
            if (option.customFieldOptionId !== fieldOption.id) {
              if (!control.value.includes(fieldOption.id) && !isFieldExistInOtherOption) {
                this.updateShowedFields(index, arrIndex);
                if (!this.isHiddenField(index, hiddenFields)) {
                  this.hideQuestion(hiddenFields, field, index, guestIndex);
                }
              } else if (!option.goToQuestions.includes(index)) {
                this.updateShowedFields(index, arrIndex);
                if (!this.isHiddenField(index, hiddenFields)) {
                  this.hideQuestion(hiddenFields, field, index, guestIndex);
                }
              } else {
                this.showNextQuestion(hiddenFields, index, guestIndex);
              }
            } else if (!control.value.includes(fieldOption.id) && !isFieldExistInOtherOption) {
              this.updateShowedFields(index, arrIndex);
              if (!this.isHiddenField(index, hiddenFields)) {
                this.hideQuestion(hiddenFields, field, index, guestIndex);
              }
            } else {
              this.showNextQuestion(hiddenFields, index, guestIndex);
            }
          });
        });
      }
    }

    if(this.isMultiVenue && this.multiVenueType === MultiVenueType.Attendee){
      this.refreshFieldOptionsMultiVenue();
    }else{
      this.refreshFieldOptions();
    }
  }

  public removeEnteredFromOption(hiddenField, goToFieldIndex, guestIndex) {
    if (goToFieldIndex !== null) {
      const attendeeIndex = guestIndex === null ? 0 : guestIndex + 1;
      if (this.fieldsForRegistrationForm[goToFieldIndex].customFieldType === CustomFieldType.MultipleSelect || hiddenField.customFieldType === CustomFieldType.SingleSelect) {
        if (this.multiDisabledOptions[attendeeIndex]) {
          const multiDisabledOptionsArray = this.multiDisabledOptions[attendeeIndex][this.locationDateTimeId][hiddenField.id];
          hiddenField.customFieldOptions.forEach((field) => {
            if (multiDisabledOptionsArray && multiDisabledOptionsArray.some((x) => x === field.id)) {
              field.entered[this.locationDateTimeId]--;
            }
          });
          delete this.multiDisabledOptions[attendeeIndex][this.locationDateTimeId][hiddenField.id];
        }
      }
    }
  }

  public removeEnteredFromRemovedGuest(guestIndex) {
    //remove Entered Option when is multivenue, old logic remain the same
    if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      this.fieldsForRegistrationForm.forEach((field) => {
        if (field.hasOwnProperty('id')) {
          if (field.customFieldType === CustomFieldType.MultipleSelect || field.customFieldType === CustomFieldType.SingleSelect) {
            if (this.multiDisabledOptions[guestIndex + 1]) {
              const multiDisabledOptionsArray = this.multiDisabledOptions[guestIndex + 1][this.locationDateTimeId][field.id];
              field.customFieldOptions.forEach((option) => {
                if (multiDisabledOptionsArray && multiDisabledOptionsArray.some((x) => x === option.id)) {
                  option.entered[this.locationDateTimeId]--;
                }
              });
            }
          }
        }
      });
    }else{
      this.fieldsForRegistrationForm.forEach((field) => {
        let routedSelectedLocationDateTimeId = [];
        const { isCustomFieldRouted, locationDateTimeIdsRouted } = this.customFieldHasRouting(field);

        if(this.multiVenueType === MultiVenueType.Registration){
          routedSelectedLocationDateTimeId = this.locationDateTimeIdsPerRegistration.filter((locationDateTimeId) =>
            locationDateTimeIdsRouted.includes(locationDateTimeId));
        }else if(this.multiVenueType === MultiVenueType.Attendee){
          if(guestIndex === null){
            const mainInvitee = this.multiVenueSelectionPerAttendee['mainInvitee'];
            if (mainInvitee) {
              routedSelectedLocationDateTimeId = mainInvitee.filter(locationDateTimeId =>
                locationDateTimeIdsRouted.includes(locationDateTimeId)
              );
            }
          }else{
            const additionalGuest = this.multiVenueSelectionPerAttendee[this.registrationForm.value.guests[guestIndex].guestId];
            if (additionalGuest) {
              routedSelectedLocationDateTimeId = additionalGuest.filter(locationDateTimeId =>
                locationDateTimeIdsRouted.includes(locationDateTimeId)
              );
            }
          }
        }
        field.customFieldOptions?.forEach(customFieldOption => {
          this.customFieldOptionsMultiVenue[field.id][customFieldOption.id].selectedOptionForGuestIndex?.forEach(selectedOptionForGuestIndex => {
            if(selectedOptionForGuestIndex === guestIndex){
              if(isCustomFieldRouted){
                routedSelectedLocationDateTimeId.forEach((locationDateTimeId)=>{
                    customFieldOption.entered[locationDateTimeId]--;
                });
              }
              this.customFieldOptionsMultiVenue[field.id][customFieldOption.id].entered--;
              this.customFieldOptionsMultiVenue[field.id][customFieldOption.id].selectedOptionForGuestIndex =
              this.customFieldOptionsMultiVenue[field.id][customFieldOption.id].selectedOptionForGuestIndex.filter(value => value !== guestIndex);
            }
          });
        });
      });
    }

    if(this.isMultiVenue && this.multiVenueType === MultiVenueType.Attendee){
      this.refreshFieldOptionsMultiVenue();
    }else{
      this.refreshFieldOptions();
    }
  }

  public enterOptionForSingleSelectByMaxEntries(fieldOption, field, guestIndex = null, isTheSameOption, remove?: boolean) {
    if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      let disabledOptions = this.multiDisabledOptions[0];
      if (guestIndex !== null) {
        disabledOptions = this.multiDisabledOptions[guestIndex + 1];
      }

      disabledOptions[this.locationDateTimeId][field.id] = disabledOptions[this.locationDateTimeId][field.id] || [];
      // if already selected
      if (disabledOptions[this.locationDateTimeId][field.id].length) {
        if (remove || isTheSameOption) {
          disabledOptions[this.locationDateTimeId][field.id] = [];
          fieldOption.entered[this.locationDateTimeId]--;
        } else {
          field.customFieldOptions.forEach((option) => {
            if (disabledOptions[this.locationDateTimeId][field.id][0] === option.id) {
              disabledOptions[this.locationDateTimeId][field.id] = [];
              if (option.entered[this.locationDateTimeId]) {
                option.entered[this.locationDateTimeId]--;
              }
            }
          });
          if (fieldOption.maxEntries && fieldOption.entered[this.locationDateTimeId] < fieldOption.maxEntries) {
            disabledOptions[this.locationDateTimeId][field.id].push(fieldOption.id);
            fieldOption.entered[this.locationDateTimeId]++;
          }
        }
      } else {
        if (fieldOption.maxEntries && fieldOption.entered[this.locationDateTimeId] < fieldOption.maxEntries) {
          disabledOptions[this.locationDateTimeId][field.id].push(fieldOption.id);
          fieldOption.entered[this.locationDateTimeId]++;
        }
      }
    }else{
      if(remove === undefined){
        remove = isTheSameOption;
      }
      this.enterOptionForSingleSelectByMaxEntriesMultiVenue(fieldOption, field, guestIndex, remove);
    }
  }

  public enterOptionForSingleSelectByMaxEntriesMultiVenue(fieldOption, field, guestIndex, remove){
    let routedSelectedLocationDateTimeId = [];
    const { isCustomFieldRouted, locationDateTimeIdsRouted } = this.customFieldHasRouting(field);

    if(this.multiVenueType === MultiVenueType.Registration){
      routedSelectedLocationDateTimeId = this.locationDateTimeIdsPerRegistration.filter((locationDateTimeId) =>
        locationDateTimeIdsRouted.includes(locationDateTimeId));
    }else if(this.multiVenueType === MultiVenueType.Attendee){
      if(guestIndex === null){
        const mainInvitee = this.multiVenueSelectionPerAttendee['mainInvitee'];
        if (mainInvitee) {
          routedSelectedLocationDateTimeId = mainInvitee.filter(locationDateTimeId =>
            locationDateTimeIdsRouted.includes(locationDateTimeId)
          );
        }
      }else{
        const additionalGuest = this.multiVenueSelectionPerAttendee[this.registrationForm.value.guests[guestIndex].guestId];
        if (additionalGuest) {
          routedSelectedLocationDateTimeId = additionalGuest.filter(locationDateTimeId =>
            locationDateTimeIdsRouted.includes(locationDateTimeId)
          );
        }
      }
    }
    // Multi venue
    if(remove){
      if(isCustomFieldRouted){
        routedSelectedLocationDateTimeId.forEach((locationDateTimeId)=>{
          fieldOption.entered[locationDateTimeId]--;
        });
      }
        this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered--;
        if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked){
          delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked[guestIndex];
        }
        this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex =
        this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex?.filter(value => value !== guestIndex);
        if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered === 0){
          delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex;
        }
    }else{
        if(isCustomFieldRouted){
          routedSelectedLocationDateTimeId.forEach((locationDateTimeId)=>{
            if(fieldOption.maxEntries && fieldOption.entered[locationDateTimeId] < fieldOption.maxEntries){
              fieldOption.entered[locationDateTimeId]++;
            }
            field.customFieldOptions.forEach(option => {
              if(this.previousSelectedOption[guestIndex] && this.previousSelectedOption[guestIndex][field.id] && option.id === this.previousSelectedOption[guestIndex][field.id]){
                option.entered[locationDateTimeId]--;
              }
            });
          });
        }
        this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered++;
        field.customFieldOptions.forEach(option => {
          if(this.previousSelectedOption[guestIndex] && this.previousSelectedOption[guestIndex][field.id] && option.id === this.previousSelectedOption[guestIndex][field.id]){
            this.customFieldOptionsMultiVenue[field.id][option.id].entered--;
            if(this.customFieldOptionsMultiVenue[field.id][option.id].isChecked){
              delete this.customFieldOptionsMultiVenue[field.id][option.id].isChecked[guestIndex];
            }
            delete this.customFieldOptionsMultiVenue[field.id][option.id].selectedOptionForGuestIndex;
          }
        });
        if (!Array.isArray(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex)) {
          this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex = [];
        }
        if (!this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex.includes(guestIndex)) {
            this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex.push(guestIndex);
        }
        if(!this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked){
          this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked = [];
        }
        this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex.forEach(element => {
          this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked[element] = true;
        });
    }
  }

  public enterOptionForMultiSelectByMaxEntries(fieldOption, field, guestIndex = null, remove: boolean, isChecked?) {
    if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      if (fieldOption && fieldOption.maxEntries) {
        let disabledOptions = this.multiDisabledOptions[0];
        if (guestIndex !== null) {
          disabledOptions = this.multiDisabledOptions[guestIndex + 1];
        }
        disabledOptions[this.locationDateTimeId][field.id] = disabledOptions[this.locationDateTimeId][field.id] || [];
        if (disabledOptions[this.locationDateTimeId][field.id].includes(fieldOption.id)) {
          fieldOption.entered[this.locationDateTimeId]--;
          disabledOptions[this.locationDateTimeId][field.id] = disabledOptions[this.locationDateTimeId][field.id].filter((id) => id !== fieldOption.id);
        } else {
          disabledOptions[this.locationDateTimeId][field.id].push(fieldOption.id);
          fieldOption.entered[this.locationDateTimeId]++;
        }
      }
    }else{
      // Multi venue
      this.enterOptionMultiSelectByMaxEntriesMultiVenue(fieldOption, field, guestIndex, remove, isChecked);
    }
  }

    public enterOptionMultiSelectByMaxEntriesMultiVenue(fieldOption, field, guestIndex, remove, isChecked?){
    let routedSelectedLocationDateTimeId = [];
    const { isCustomFieldRouted, locationDateTimeIdsRouted } = this.customFieldHasRouting(field);

    if(this.multiVenueType === MultiVenueType.Registration){
      routedSelectedLocationDateTimeId = this.locationDateTimeIdsPerRegistration.filter((locationDateTimeId) =>
        locationDateTimeIdsRouted.includes(locationDateTimeId)
      );
    }else if(this.multiVenueType === MultiVenueType.Attendee){
        if(guestIndex === null){
          const mainInvitee = this.multiVenueSelectionPerAttendee['mainInvitee'];
          if (mainInvitee) {
            routedSelectedLocationDateTimeId = mainInvitee.filter(locationDateTimeId =>
              locationDateTimeIdsRouted.includes(locationDateTimeId)
            );
          }
        }else{
          const additionalGuest = this.multiVenueSelectionPerAttendee[this.registrationForm.value.guests[guestIndex].guestId];
          if (additionalGuest) {
            routedSelectedLocationDateTimeId = additionalGuest.filter(locationDateTimeId =>
              locationDateTimeIdsRouted.includes(locationDateTimeId)
            );
          }
        }
    }
    if(remove){
      if(isCustomFieldRouted){
        routedSelectedLocationDateTimeId.forEach((locationDateTimeId)=>{
          fieldOption.entered[locationDateTimeId]--;
        });
      }
      this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered--;
      if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked){
        delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked[guestIndex];
      }
      this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex =
      this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex?.filter(value => value !== guestIndex);
      if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered === 0){
        delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex;
      }
    }else{
      if(isCustomFieldRouted){
        routedSelectedLocationDateTimeId.forEach((locationDateTimeId)=>{
          if(fieldOption.maxEntries && fieldOption.entered[locationDateTimeId] < fieldOption.maxEntries){
            fieldOption.entered[locationDateTimeId]++;
          }
        });
      }
      this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered++;
      if (!Array.isArray(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex)) {
        this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex = [];
      }
      if (!this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex.includes(guestIndex)) {
          this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex.push(guestIndex);
      }
      if(isChecked){
        this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked = [];
        this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex.forEach(element => {
          this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked[element] = true;
        });
      }
    }
  }

  public isOptionSelected(disabledOptions, optionId) {
    if (!disabledOptions) {
      disabledOptions = [];
    }
    return disabledOptions.includes(optionId);
  }

  public isOptionDisabledSingleSelect(field, option, guestIndex = null, guestId: string): boolean{
    let isOptionDisabled;
    let totalSumOptionEntered = 0;

    Object.values(option.entered).forEach((value: number) => {
      totalSumOptionEntered += value;
    });

    const { isCustomFieldRouted, locationDateTimeIdsRouted } = this.customFieldHasRouting(field);

    if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      guestIndex = guestIndex !== null ? guestIndex + 1 : 0;
      if(isCustomFieldRouted){
        if(guestIndex === 0){
          isOptionDisabled = field.configuration.isEditable && this.hasMaxEntries(option.maxEntries) ? ((option.entered[this.locationDateTimeId] === option.maxEntries &&
            !this.isOptionSelected(this.multiDisabledOptions[0][this.locationDateTimeId][field.id], option.id))|| null) : null;
        }else{
          isOptionDisabled = field.configuration.isEditable && this.hasMaxEntries(option.maxEntries) ? ((option.entered[this.locationDateTimeId] === option.maxEntries &&
            this.multiDisabledOptions[guestIndex] && !this.isOptionSelected(this.multiDisabledOptions[guestIndex][this.locationDateTimeId][field.id], option.id))
            || null) : null;
        }
      }else{
        if(guestIndex === 0){
          isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
            totalSumOptionEntered >= option.maxEntries && !this.isOptionSelected(this.multiDisabledOptions[0][this.locationDateTimeId][field.id], option.id)
            : null;
        }else{
          isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
            totalSumOptionEntered >= option.maxEntries && !this.isOptionSelected(this.multiDisabledOptions[guestIndex][this.locationDateTimeId][field.id], option.id)
            : null;
        }
      }
      return isOptionDisabled;
    }else{
      if(this.customFieldOptionsMultiVenue[field.id][option.id].isChecked && this.customFieldOptionsMultiVenue[field.id][option.id].isChecked[guestIndex] === true){
        return null;
      }

      if(isCustomFieldRouted){
        if(this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration){
          const routedSelectedLocationDateTimeId = this.locationDateTimeIdsPerRegistration.filter((locationDateTimeId) =>
            locationDateTimeIdsRouted.includes(locationDateTimeId)
          );
          isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
            routedSelectedLocationDateTimeId.some(locationDateTimeId => option.entered[locationDateTimeId] >= option.maxEntries) : false;
        }else if (this.multiVenueType === MultiVenueType.Attendee){
          const currentGuest = this.multiVenueSelectionPerAttendee[guestId];
          if(currentGuest){
            const routedSelectedLocationDateTimeId = currentGuest.filter((locationDateTimeId) =>
                                          locationDateTimeIdsRouted.includes(locationDateTimeId));
            isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
              routedSelectedLocationDateTimeId.some(locationDateTimeId => option.entered[locationDateTimeId] >= option.maxEntries) : false;
          }
        }
      }else{
        isOptionDisabled = this.maxEntriesReached(option, this.customFieldOptionsMultiVenue[field.id][option.id].entered);
      }
      return isOptionDisabled ? true : null;
    }
  }

  public isOptionDisabledMultipleSelect(field, option, guestIndex = null, guestId: string){

    let isOptionDisabled;
    let totalSumOptionEntered = 0;

    Object.values(option.entered).forEach((value: number) => {
      totalSumOptionEntered += value;
    });

    const { isCustomFieldRouted, locationDateTimeIdsRouted } = this.customFieldHasRouting(field);

    if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      guestIndex = guestIndex !== null ? guestIndex + 1 : 0;
      if(isCustomFieldRouted){
        if(guestIndex === 0){
          isOptionDisabled =  field.configuration.isEditable && this.hasMaxEntries(option.maxEntries) ?
            ((option.entered[this.locationDateTimeId] === option.maxEntries && !this.isOptionSelected(this.multiDisabledOptions[0][this.locationDateTimeId][field.id], option.id))
            || null) : null;
        }else{
          isOptionDisabled = field.configuration.isEditable && this.hasMaxEntries(option.maxEntries) ?
            ((option.entered[this.locationDateTimeId] === option.maxEntries && this.multiDisabledOptions[guestIndex] &&
              !this.isOptionSelected(this.multiDisabledOptions[guestIndex][this.locationDateTimeId][field.id], option.id))
              || null) : null;
        }
      }else{
        if(guestIndex === 0){
          isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
            totalSumOptionEntered >= option.maxEntries && !this.isOptionSelected(this.multiDisabledOptions[0][this.locationDateTimeId][field.id], option.id) : null;
        }else{
          isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
            totalSumOptionEntered >= option.maxEntries && !this.isOptionSelected(this.multiDisabledOptions[guestIndex][this.locationDateTimeId][field.id], option.id) : null;
        }
      }
      return isOptionDisabled;
    }else{
      if(this.customFieldOptionsMultiVenue[field.id][option.id].isChecked && this.customFieldOptionsMultiVenue[field.id][option.id].isChecked[guestIndex] === true){
        return null;
      }
      if(isCustomFieldRouted){
        if(this.multiVenueType === MultiVenueType.Registration){
          const routedSelectedLocationDateTimeId = this.locationDateTimeIdsPerRegistration.filter((locationDateTimeId) =>
            locationDateTimeIdsRouted.includes(locationDateTimeId)
          );
          isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
            routedSelectedLocationDateTimeId.some(locationDateTimeId => option.entered[locationDateTimeId] >= option.maxEntries) : false;
        }else if (this.multiVenueType === MultiVenueType.Attendee){
          const currentGuest = this.multiVenueSelectionPerAttendee[guestId];
          if(currentGuest){
            const routedSelectedLocationDateTimeId = currentGuest.filter((locationDateTimeId) =>
                                          locationDateTimeIdsRouted.includes(locationDateTimeId));
            isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
              routedSelectedLocationDateTimeId.some(locationDateTimeId => option.entered[locationDateTimeId] >= option.maxEntries) : false;
          }
        }
      }else{
        isOptionDisabled = this.maxEntriesReached(option, this.customFieldOptionsMultiVenue[field.id][option.id].entered);
      }
      return isOptionDisabled ? true : null;
    }
  }

  public isOptionLabelFullSingleSelect(field, option, guestId: string, guestIndex){
    let isOptionDisabled;
    let totalSumOptionEntered = 0;

    Object.values(option.entered).forEach((value: number) => {
      totalSumOptionEntered += value;
    });
    const { isCustomFieldRouted, locationDateTimeIdsRouted } = this.customFieldHasRouting(field);

    if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      if(isCustomFieldRouted){
        isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
          option.entered[this.locationDateTimeId] >= option.maxEntries && !this.isOptionSelected(this.multiDisabledOptions[guestIndex][this.locationDateTimeId][field.id], option.id) : false;
      }else{
        isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
        totalSumOptionEntered >= option.maxEntries && !this.isOptionSelected(this.multiDisabledOptions[guestIndex][this.locationDateTimeId][field.id], option.id) : false;
      }
    }else{
      if(isCustomFieldRouted){
        if(this.multiVenueType === MultiVenueType.Registration){
          const routedSelectedLocationDateTimeId = this.locationDateTimeIdsPerRegistration.filter((locationDateTimeId) =>
            locationDateTimeIdsRouted.includes(locationDateTimeId)
          );
          isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
            routedSelectedLocationDateTimeId.some(locationDateTimeId => option.entered[locationDateTimeId] >= option.maxEntries) : false;
        }else if (this.multiVenueType === MultiVenueType.Attendee){
          const currentGuest = this.multiVenueSelectionPerAttendee[guestId];
          if(currentGuest){
            const routedSelectedLocationDateTimeId = currentGuest.filter((locationDateTimeId) =>
                                          locationDateTimeIdsRouted.includes(locationDateTimeId));
            isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
              routedSelectedLocationDateTimeId.some(locationDateTimeId => option.entered[locationDateTimeId] >= option.maxEntries) : false;
          }
        }
      }else{
        if(this.customFieldOptionsMultiVenue[field.id]){
          isOptionDisabled = this.maxEntriesReached(option, this.customFieldOptionsMultiVenue[field.id][option.id].entered);
        }
      }
      guestIndex = guestIndex === 0 ? null : guestIndex - 1;
      if(this.customFieldOptionsMultiVenue[field.id][option.id].isChecked && this.customFieldOptionsMultiVenue[field.id][option.id].isChecked[guestIndex] === true){
        isOptionDisabled = false;
      }
    }
    return isOptionDisabled;
  }

  public isOptionLabelFullMultipleSelect(field, option, guestId: string, guestIndex){
    let isOptionDisabled;
    let totalSumOptionEntered = 0;

    Object.values(option.entered).forEach((value: number) => {
      totalSumOptionEntered += value;
    });
    const { isCustomFieldRouted, locationDateTimeIdsRouted } = this.customFieldHasRouting(field);

    if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      if(isCustomFieldRouted){
        isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
        option.entered[this.locationDateTimeId] >= option.maxEntries && !this.isOptionSelected(this.multiDisabledOptions[guestIndex][this.locationDateTimeId][field.id], option.id) : false;
      }else{
        isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
          totalSumOptionEntered >= option.maxEntries && !this.isOptionSelected(this.multiDisabledOptions[guestIndex][this.locationDateTimeId][field.id], option.id) : false;
      }
    }else{
      if(isCustomFieldRouted){
        if(this.multiVenueType === MultiVenueType.Registration){
          const routedSelectedLocationDateTimeId = this.locationDateTimeIdsPerRegistration.filter((locationDateTimeId) =>
            locationDateTimeIdsRouted.includes(locationDateTimeId)
          );
          isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
            routedSelectedLocationDateTimeId.some(locationDateTimeId => option.entered[locationDateTimeId] >= option.maxEntries) : false;
        }else if (this.multiVenueType === MultiVenueType.Attendee){
          const currentGuest = this.multiVenueSelectionPerAttendee[guestId];
          if(currentGuest){
            const routedSelectedLocationDateTimeId = currentGuest.filter((locationDateTimeId) =>
                                          locationDateTimeIdsRouted.includes(locationDateTimeId));
            isOptionDisabled = this.hasMaxEntries(option.maxEntries) ?
              routedSelectedLocationDateTimeId.some(locationDateTimeId => option.entered[locationDateTimeId] >= option.maxEntries) : false;
          }
        }
      }else{
        if(this.customFieldOptionsMultiVenue[field.id]){
          isOptionDisabled = this.maxEntriesReached(option, this.customFieldOptionsMultiVenue[field.id][option.id].entered);
        }
      }
      guestIndex = guestIndex === 0 ? null : guestIndex - 1;
      if(this.customFieldOptionsMultiVenue[field.id][option.id].isChecked && this.customFieldOptionsMultiVenue[field.id][option.id].isChecked[guestIndex] === true){
        isOptionDisabled = false;
      }
    }
    return isOptionDisabled;
  }

  public showNextQuestion(hiddenFields, fieldIndex, guestIndex) {
    const removedIndex = hiddenFields.findIndex((x) => {
      const isShow = guestIndex !== null ? x.showGuest : x.show;
      return x.order === fieldIndex && isShow;
    });
    if (removedIndex !== -1) {
      this.addFormControl(hiddenFields[removedIndex], hiddenFields, removedIndex, guestIndex);
    }
  }

  public showSubQuestion(field, hiddenFields, guestIndex) {
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    let formGroup = this.registrationForm;
    if (guestIndex !== null) {
      formGroup = this.registrationForm.controls['guests']['controls'][guestIndex];
    }
    const controlName = field.type ? field.type : field.id;
    const control = formGroup.controls[controlName];
    if (field.customFieldType === CustomFieldType.MultipleSelect || field.customFieldType === CustomFieldType.SingleSelect) {
      field.optionsGoToQuestion.forEach((option, i) => {
        if (control && control.value && control.value[i]) {
          option.goToQuestions.forEach((goTo) => {
            this.currentShowedFields[arrIndex].push(goTo);
            this.showNextQuestion(hiddenFields, goTo, guestIndex);
          });
        }
      });
    } else {
      if (control && control.value && field.fieldGoToQuestion.length) {
        field.fieldGoToQuestion.forEach((goTo) => {
          this.currentShowedFields[arrIndex].push(goTo);
          this.showNextQuestion(hiddenFields, goTo, guestIndex);
        });
      }
    }
  }

  public addFormControl(field, hiddenFields, removedIndex, guestIndex) {
    const controlName = field.type ? field.type : field.id;
    let formGroup = this.registrationForm;
    if (guestIndex !== null) {
      formGroup = this.registrationForm.controls['guests']['controls'][guestIndex];
    }
    switch (controlName) {
      case 'address':
        if (this.invitationRegistrationPage.showAddress && guestIndex === null) {
          this.addressFields.forEach((key) => {
            formGroup.addControl(key, new UntypedFormControl(this.invitationAttendees[key] ? this.invitationAttendees[key] : ''));
          });
        } else if (this.invitationRegistrationPage.showGuestAddress && guestIndex !== null) {
          this.addressFields.forEach((key) => {
            formGroup.addControl(key,  new UntypedFormControl(''));
          });
        }
        break;
      case 'company':
        if (this.invitationRegistrationPage.showCompany  && guestIndex === null) {
          formGroup.addControl('company', new UntypedFormControl(this.invitationAttendees.companyName ? this.invitationAttendees.companyName : ''));
        } else if (this.invitationRegistrationPage.showGuestCompany  && guestIndex !== null) {
            formGroup.addControl('company',  new UntypedFormControl(''));
        }
        break;
      case 'jobTitle':
        if (this.invitationRegistrationPage.showJobTitle  && guestIndex === null) {
          formGroup.addControl('jobTitle', new UntypedFormControl(this.invitationAttendees.jobTitle ? this.invitationAttendees.jobTitle : ''));
        } else if (this.invitationRegistrationPage.showGuestJobTitle  && guestIndex !== null) {
          formGroup.addControl('jobTitle',  new UntypedFormControl(''));
        }
        break;
      case 'customerNumber':
        if (this.invitationRegistrationPage.showCustomerNumber  && guestIndex === null) {
          formGroup.addControl('customerNumber', new UntypedFormControl(this.invitationAttendees.customerNumber ? this.invitationAttendees.customerNumber : ''));
        } else if (this.invitationRegistrationPage.showGuestCustomerNumber  && guestIndex !== null) {
          formGroup.addControl('customerNumber',  new UntypedFormControl(''));
        }
        break;
      case 'mobile':
        if (this.invitationRegistrationPage.showMobile && guestIndex === null) {
          formGroup.addControl('mobile', new UntypedFormControl(this.invitationAttendees.mobile ? this.invitationAttendees.mobile : ''));
        } else if (this.invitationRegistrationPage.showGuestMobile  && guestIndex !== null) {
          formGroup.addControl('mobile',  new UntypedFormControl(''));
        }
        break;
      default:
        if (field.show && guestIndex === null) {
          if (field.customFieldType === CustomFieldType.MultipleSelect || field.customFieldType === CustomFieldType.SingleSelect) {
            if (field?.selectFormat === SelectFormat.CheckboxRadio) {
              const controls = field.customFieldOptions.map((c) => {
                return new UntypedFormControl();
              });
              if (field.required) {
                formGroup.addControl(field.id, new UntypedFormArray(controls, this.multipleCheckboxRequireOne));
              } else {
                formGroup.addControl(field.id, new UntypedFormArray(controls));
              }
            } else {
              if (field.required) {
                formGroup.addControl(field.id, new UntypedFormControl(undefined, Validators.required));
              } else {
                formGroup.addControl(field.id, new UntypedFormControl());
              }
            }
          } else if (field.customFieldType === CustomFieldType.Boolean) {
            if (field.checkboxFormat === CheckboxFormat.OptIn) {
              formGroup.addControl(field.id, new UntypedFormControl(false));
            } else {
              formGroup.addControl(field.id, new UntypedFormControl(true));
            }
          } else {
            formGroup.addControl(field.id, new UntypedFormControl(''));
          }
        } else if (field.showGuest && guestIndex !== null) {
          if (field.customFieldType === CustomFieldType.MultipleSelect || field.customFieldType === CustomFieldType.SingleSelect) {
            if (field?.selectFormat === SelectFormat.CheckboxRadio) {
              const controls = field.customFieldOptions.map((c) => {
                return new UntypedFormControl();
              });
              if (field.requiredGuest) {
                formGroup.addControl(field.id, new UntypedFormArray(controls, this.multipleCheckboxRequireOne));
              } else {
                formGroup.addControl(field.id, new UntypedFormArray(controls));
              }
            } else {
              if (field.requiredGuest) {
                formGroup.addControl(field.id, new UntypedFormControl(undefined, Validators.required));
              } else {
                formGroup.addControl(field.id, new UntypedFormControl());
              }
            }
          } else if (field.customFieldType === CustomFieldType.Boolean) {
            if (field.checkboxFormat === CheckboxFormat.OptIn) {
              formGroup.addControl(field.id, new UntypedFormControl(false));
            } else {
              formGroup.addControl(field.id, new UntypedFormControl(true));
            }
          } else {
            formGroup.addControl(field.id, new UntypedFormControl(''));
          }
        }
        break;
    }
    hiddenFields.splice(removedIndex, 1);
    this.showSubQuestion(field, hiddenFields, guestIndex);
  }

  public canHideQuestion(currentField, index, guestIndex) {
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    let formGroup = this.registrationForm;
    if (guestIndex !== null) {
      formGroup = this.registrationForm.controls['guests']['controls'][guestIndex];
    }
    if (this.doubleGoTo[arrIndex][index]) {
      let doubleGoTo;
      if (currentField.id === 'locationDateTimeFieldId') {
        doubleGoTo = this.doubleGoTo[arrIndex][index];
      } else {
        doubleGoTo = this.doubleGoTo[arrIndex][index].filter((f) => currentField.order !== f.order);
      }
      return doubleGoTo.every((f) => {
        const controlName = f.type ? f.type : f.id;
        const control = formGroup.controls[controlName];
        if (control) {
          if (f.customFieldType === CustomFieldType.SingleSelect || f.customFieldType === CustomFieldType.MultipleSelect || f.customFieldType === CustomFieldType.Boolean) {
            const repeatedGoToValue = f.optionsGoToQuestion.find((el) => {
              return el.goToQuestions.includes(index);
            }).customFieldOptionId;
            return control.value !== repeatedGoToValue;
          } else if (f.id === 'locationDateTimeFieldId') {
            const repeatedGoToValue = f.optionsGoToQuestion.find((el) => {
              return el.goToQuestions.includes(index);
            }).locationDateTimeId;
            const hiddenArr = guestIndex ? this.hiddenGuestsFields[guestIndex] : this.hiddenInviteeFields;
            const isExistInHiddenArray = hiddenArr.some((x) => x.oreder === index);
            return this.locationDateTimeId !== repeatedGoToValue && !isExistInHiddenArray;
          } else {
            return !control.value;
          }
        } else {
          return true;
        }
      });
    } else {
      return true;
    }
  }

  public hideQuestion(hiddenFields, field, fieldIndex, guestIndex) {
    const hiddenField = this.fieldsForRegistrationForm.find((el) => el.order === fieldIndex);
    if (hiddenField && this.canHideQuestion(field, fieldIndex, guestIndex)) {
      delete hiddenField.dirty;
      if (!hiddenFields.some(x => x.order === fieldIndex)) {
        hiddenFields.push(hiddenField);
      }
      this.removeEnteredFromOption(hiddenField, fieldIndex, guestIndex);
      this.resetHiddenFields(hiddenFields, hiddenField, guestIndex);
      this.resetFormField(hiddenField, guestIndex);
      this.removeHiddenControls(this.hiddenInviteeFields, null);
      this.hiddenGuestsFields.forEach((hiddenfields, i) => {
        this.removeHiddenControls(hiddenfields, i);
      });
    }
  }

  public resetHiddenFields(hiddenFields, field, guestIndex) {
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    if (field.customFieldType === CustomFieldType.SingleSelect || field.customFieldType === CustomFieldType.MultipleSelect) {
      field.optionsGoToQuestion.forEach((option) => {
        option.goToQuestions.forEach((goTo) => {
          this.updateShowedFields(goTo, arrIndex);
          if (!this.isHiddenField(goTo, hiddenFields)) {
            this.hideQuestion(hiddenFields, field, goTo, guestIndex);
          }
        });
      });
    } else {
      field.fieldGoToQuestion.forEach((goTo) => {
        this.updateShowedFields(goTo, arrIndex);
        if (!this.isHiddenField(goTo, hiddenFields)) {
          this.hideQuestion(hiddenFields, field, goTo, guestIndex);
        }
      });
    }
  }

  public reinitForm() {
    const formData = this.registrationForm.value;
    const touchedControls = this.getTouchedControls(this.registrationForm);
    const guestsFormArray = this.registrationForm.controls['guests'].value;
    const guestsContols = this.registrationForm.controls['guests']['controls'];
    this.registrationForm = this.initForm(this.invitationRegistrationPage, this.invitationContact);
    this.removeHiddenControls(this.hiddenInviteeFields, null);
    if (guestsFormArray.length) {
      const control = this.registrationForm.controls['guests'] as UntypedFormArray;
      guestsFormArray.forEach((guest, i) => {
        const guestTouchedFields = this.getTouchedControls(guestsContols[i]);
        const guestId = guest.guestId ? guest.guestId : guest.id;
        const guestControl = this.initGuest(guestId, i, this.invitationRegistrationPage);
        control.push(guestControl);
        this.removeHiddenControls(this.hiddenGuestsFields[i], i);
        this.touchControls(guestControl, guestTouchedFields);
        this.updateFormData(guest, guestControl);
      });
    }
    this.touchControls(this.registrationForm, touchedControls);

    this.updateFormData(formData, this.registrationForm);
  }

  public getTouchedControls(formGroup: UntypedFormGroup) {
    const touchedControls = [];
    const controls = formGroup.controls;
    for (const key in controls) {
      if (key !== 'guests') {
        if (controls.hasOwnProperty(key)) {
          if (controls[key].touched) {
            touchedControls.push(key);
          }
        }
      }
    }
    return touchedControls;
  }

  public touchControls(formGroup, touchedControls) {
    touchedControls.forEach((key) => {
      if (formGroup.controls[key]) {
        formGroup.controls[key].markAsTouched();
      }
    });
  }

  public resetFormField(field, guestIndex) {
    this.reinitForm();
    let formGroup = this.registrationForm;
    if (guestIndex !== null) {
      formGroup = this.registrationForm.controls['guests']['controls'][guestIndex];
    }
    this.removeControls(formGroup, field);
    this.form = Object.assign(this.form, { form: this.registrationForm });

  }

  public removeControls(formGroup, control) {
    if (control.type) {
      if (control.type === 'address') {
        this.addressFields.forEach((el) => {
          if (formGroup.controls[el]) {
            formGroup.controls[el].setErrors(null);
            formGroup.removeControl(el);
          }
        });
      } else {
        if (formGroup.controls[control.type]) {
          formGroup.controls[control.type].setErrors(null);
          formGroup.removeControl(control.type);
        }
      }
    } else {
      if (formGroup.controls[control.id]) {
        formGroup.controls[control.id].setErrors(null);
        formGroup.removeControl(control.id);
      }
    }
  }

  public removeHiddenControls(hiddenFields, guestIndex = null) {
    let formGroup = this.registrationForm;
    if (guestIndex !== null) {
      formGroup = this.registrationForm.controls['guests']['controls'][guestIndex];
    }
    hiddenFields.forEach((field) => {
      this.removeControls(formGroup, field);
    });
  }

  public addGuest() {
    this.currentGuests++;
    const control = this.registrationForm.controls['guests'] as UntypedFormArray;
    const guestId = Guid.create().toString();
    control.push(this.initGuest(guestId, this.currentGuests - 2, this.invitationRegistrationPage));
    this.doubleGoTo.push({ });
    if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      this.multiDisabledOptions.push({ [this.locationDateTimeId]: { } });
    }
    this.configureCustomFieldItems(guestId);
    if(this.isMultiVenue && this.multiVenueType === MultiVenueType.Attendee){
      this.refreshFieldOptionsMultiVenue();
    }else{
      this.refreshFieldOptions();
    }
    this.hiddenGuestsFields.push([]);
    this.currentShowedFields.push([]);
    this.hiddenGuestsFields[this.currentGuests - 2] = this.getDefaultHiddenFieldList(this.currentGuests - 2, this.hiddenFieldIndexesPerVenue);
    this.currentShowedFields[this.currentGuests - 1] = [...this.currentShowedFields[this.currentGuests - 1], ...this.showedFieldIndexesPerVenue];
    this.currentShowedFields[this.currentGuests - 1].forEach((index) => {
      this.showNextQuestion(this.hiddenGuestsFields[this.currentGuests - 2], index, this.currentGuests - 2);
    });
    // remove hidden controls from invite
    this.removeHiddenControls(this.hiddenGuestsFields[this.currentGuests - 2], this.currentGuests - 2);
    this.previousSingleSelectValues.push({ });
  }

  public removeGuest(i: number, guestId?: string) {
    this.currentGuests--;
    const control = this.registrationForm.controls['guests'] as UntypedFormArray;
    const removedGuestId  = control.at(i).value.removedGuestId ;
    this.removeEnteredFromRemovedGuest(i);
    this.deleteCustomFieldItems(guestId);
    if(this.isMultiVenue && guestId){
      delete this.multiVenueSelectionPerAttendee[guestId];
      this.unvalidatedGuestsList = this.unvalidatedGuestsList.filter(id => id !== guestId);
      if(this.unvalidatedGuestsList.length === 0){
        this.guestNotValid = false;
      }
    }
    if(this.previousSelectedOption[i]){
      delete this.previousSelectedOption[i];
    }
    control.removeAt(i);
    this.hiddenGuestsFields.splice(i, 1);
    this.multiDisabledOptions.splice(i + 1, 1);
    this.currentShowedFields.splice(i + 1, 1);
    this.doubleGoTo.splice(i + 1, 1);
    this.previousSingleSelectValues.splice(i + 1, 1);

    // Remove files related to guests.
    this.guestFiles[removedGuestId ] = null;
    this.guestDragOver[removedGuestId ] = null;
    this.guestUploadInputs[removedGuestId ] = null;
    this.guestValidMap[removedGuestId ] = null;
  }

  public initForm(registrationPage: IInvitationRegistrationPage, contactDetails: IInvitationContactDetails): UntypedFormGroup {
    const form = this.fb.group({
      guests: this.fb.array([])
    });

    form.addControl('id', new UntypedFormControl(this.invitationAttendees.id));

    form.addControl('firstName', new UntypedFormControl(contactDetails.firstName ? contactDetails.firstName : '', Validators.required));
    form.addControl('lastNamePrefix', new UntypedFormControl(contactDetails.lastNamePrefix ? contactDetails.lastNamePrefix : ''));
    form.addControl('lastName', new UntypedFormControl(contactDetails.lastName ? contactDetails.lastName : '', Validators.required));
    form.addControl('email', new UntypedFormControl(contactDetails.email ? contactDetails.email : '', [Validators.required, Validators.email]));
    if (registrationPage.showMobile) {
      form.addControl('mobile', new UntypedFormControl(contactDetails.mobile ? contactDetails.mobile : ''));
    }
    if (registrationPage.showCompany) {
      form.addControl('company', new UntypedFormControl(contactDetails.companyName ? contactDetails.companyName : ''));
    }
    if (registrationPage.showAddress) {
      form.addControl('addressLineOne', new UntypedFormControl(contactDetails.addressLineOne ? contactDetails.addressLineOne : ''));
      form.addControl('addressLineTwo', new UntypedFormControl(contactDetails.addressLineTwo ? contactDetails.addressLineTwo : ''));
      form.addControl('city', new UntypedFormControl(contactDetails.city ? contactDetails.city : ''));
      form.addControl('postalCode', new UntypedFormControl(contactDetails.postalCode ? contactDetails.postalCode : ''));
      form.addControl('country', new UntypedFormControl(contactDetails.country ? contactDetails.country : ''));
    }

    if (registrationPage.showJobTitle) {
      form.addControl('jobTitle', new UntypedFormControl(contactDetails.title ? contactDetails.title : ''));
    }
    if (registrationPage.showCustomerNumber) {
      form.addControl('customerNumber', new UntypedFormControl(contactDetails.customerNumber));
    }

    if (registrationPage.customFields) {
      registrationPage.customFields.forEach((customField) => {
        if (customField.show) {
          if (customField.customFieldType === CustomFieldType.MultipleSelect  || customField.customFieldType === CustomFieldType.SingleSelect) {
            if (customField?.selectFormat === SelectFormat.CheckboxRadio) {
              const controls = customField.customFieldOptions.map((c) => {
                return new UntypedFormControl();
              });
              if (customField.required) {
                if (customField.customFieldType === CustomFieldType.SingleSelect) {
                    form.addControl(customField.id, new UntypedFormArray(controls, this.multipleCheckboxRequireOne));
                    if (!this.hasVisibleToMainInvitee(customField)) {
                        form.controls[customField.id].clearValidators();
                        form.controls[customField.id].updateValueAndValidity({ emitEvent: false });
                      }
                } else {
                    form.addControl(customField.id, new UntypedFormArray(
                        controls, this.multipleCheckboxRequireBetween(
                        customField.multipleChoiceConfigOptions.minSelect || 1, customField.multipleChoiceConfigOptions.maxSelect || controls.length)));
                    if (!this.hasVisibleToMainInvitee(customField)) {
                        form.controls[customField.id].clearValidators();
                        form.controls[customField.id].updateValueAndValidity({ emitEvent: false });
                    }
                }
              } else {
                form.addControl(customField.id, new UntypedFormArray(controls));
                if (customField.customFieldType === CustomFieldType.MultipleSelect) {
                    const customFieldControl = form.get(customField.id) as UntypedFormArray;
                    customFieldControl.valueChanges.subscribe(() => {
                    const checkedCount = customFieldControl.controls.filter(control => control.value).length;
                    if (checkedCount > 0) {
                    customFieldControl.setValidators(
                        this.multipleCheckboxRequireBetween(customField.multipleChoiceConfigOptions.minSelect || 1, customField.multipleChoiceConfigOptions.maxSelect || controls.length));
                    } else {
                    customFieldControl.clearValidators();
                    }
                    customFieldControl.updateValueAndValidity({ emitEvent: false });
                    });
                }
              }
            } else {
                form.addControl(customField.id, new UntypedFormControl(''));
                if (customField.customFieldType === CustomFieldType.MultipleSelect) {
                    if (customField.required) {
                        form.controls[customField.id]
                            .addValidators([this.minSelectValidator(customField.multipleChoiceConfigOptions.minSelect, customField.id),
                                            this.maxSelectValidator(customField.multipleChoiceConfigOptions.maxSelect ?? customField.customFieldOptions?.length, customField.id)
                        ]);
                        if (!this.hasVisibleToMainInvitee(customField)) {
                            form.controls[customField.id].clearValidators();
                            form.controls[customField.id].updateValueAndValidity({ emitEvent: false });
                          }
                    } else {
                        const control = form.get(customField.id);
                        control.valueChanges.subscribe(() => {
                            if (control.value?.length > 0) {
                                const validators = [
                                    this.minSelectValidator(customField.multipleChoiceConfigOptions.minSelect, customField.id),
                                    this.maxSelectValidator(customField.multipleChoiceConfigOptions.maxSelect ?? customField.customFieldOptions?.length, customField.id)
                                ];
                                control.setValidators(validators);
                                control.updateValueAndValidity({ emitEvent: false });
                            } else {
                                control.clearValidators();
                                control.updateValueAndValidity({ emitEvent: false });
                            }
                        });
                    }
                }
            }
          } else if (customField.customFieldType === CustomFieldType.Boolean) {
            if (customField.checkboxFormat === CheckboxFormat.OptIn) {
              form.addControl(customField.id, new UntypedFormControl(false));
            } else {
              form.addControl(customField.id, new UntypedFormControl(true));
            }
          } else {
            form.addControl(customField.id, new UntypedFormControl(''));
          }
          this.customFieldsInvitee++;
        }
      });
    }
    return form;
  }

  public minSelectValidator(minSelect: number, customFieldId: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const selectedCount = control.value?.length || 0;
        return selectedCount >= minSelect ? null : { minSelect: { requiredMinSelect: minSelect, actualSelect: selectedCount } };
    };
  }

  public maxSelectValidator(maxSelect: number, customFieldId: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const selectedCount = control.value ? control.value?.filter((item: any) => item !== null).length || 0 : 0;
      return selectedCount <= maxSelect ? null : { maxSelect: { requiredMaxSelect: maxSelect, actualSelect: selectedCount } };
    };
  }

  public minSelectValidatorGuest(minSelect: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const selectedCount = control.value?.length || 0;
      return selectedCount >= minSelect ? null : { minSelect: { requiredMinSelect: minSelect, actualSelect: selectedCount } };
    };
  }

  public maxSelectValidatorGuest(maxSelect: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const selectedCount = control.value ? control.value?.filter((item: any) => item !== null).length || 0 : 0;
      if (maxSelect) {
        return selectedCount <= maxSelect ? null : { maxSelect: { requiredMaxSelect: maxSelect, actualSelect: selectedCount } };
      } else {
        // Assuming a very high number to simulate "no limit"
        return selectedCount <= 1000 ? null : { maxSelect: true };
      }
    };
  }

  public initFormForEdit(registrationPage: IInvitationRegistrationPage, attendeeDetails: IAttendeeDetails): UntypedFormGroup {
    const form = this.fb.group({
      guests: this.fb.array([])
    });

    form.addControl('id', new UntypedFormControl(attendeeDetails.id));

    form.addControl('firstName', new UntypedFormControl(attendeeDetails.firstName, Validators.required));
    form.addControl('lastNamePrefix', new UntypedFormControl(attendeeDetails.lastNamePrefix ?? ''));
    form.addControl('lastName', new UntypedFormControl(attendeeDetails.lastName, Validators.required));
    form.addControl('email', new UntypedFormControl(attendeeDetails.email));
    if (registrationPage.showMobile) {
      form.addControl('mobile', new UntypedFormControl(attendeeDetails.mobile));
    }
    if (registrationPage.showCompany) {
      form.addControl('company', new UntypedFormControl(attendeeDetails.companyName));
    }
    if (registrationPage.showAddress) {
      form.addControl('addressLineOne', new UntypedFormControl(attendeeDetails.addressLineOne));
      form.addControl('addressLineTwo', new UntypedFormControl(attendeeDetails.addressLineTwo));
      form.addControl('city', new UntypedFormControl(attendeeDetails.city));
      form.addControl('postalCode', new UntypedFormControl(attendeeDetails.postalCode));
      form.addControl('country', new UntypedFormControl(attendeeDetails.country));
    }
    if (registrationPage.showJobTitle) {
      form.addControl('jobTitle', new UntypedFormControl(attendeeDetails.jobTitle));
    }
    if (registrationPage.showCustomerNumber) {
      form.addControl('customerNumber', new UntypedFormControl(attendeeDetails.customerNumber));
    }

    if (registrationPage.customFields) {
      registrationPage.customFields.forEach((customField) => {
        if (customField.show) {
          const attendeeCustomField = attendeeDetails.customFields.find((x) => x.id === customField.id);
          if (attendeeCustomField) {
            if (customField.customFieldType === CustomFieldType.MultipleSelect || customField.customFieldType === CustomFieldType.SingleSelect) {
              if (customField.selectFormat === SelectFormat.Dropdown) {
                const selectedValues = this.getSelectedCustomFieldOptions(attendeeCustomField);
                form.addControl(customField.id, new UntypedFormControl(selectedValues));
              } else {
                this.setMultipleCustomFields(form, customField, attendeeCustomField, false);
              }
            } else if (customField.customFieldType === CustomFieldType.Boolean) {
              const controlValue = attendeeCustomField?.booleanValue;
              form.addControl(customField.id, new UntypedFormControl(controlValue));
            } else {
              let controlValue;
              switch (customField.freeTextFormat) {
                case (FreeTextFormat.Text): {
                  controlValue = attendeeCustomField.textValue;
                  break;
                }
                case (FreeTextFormat.CountryCode): {
                  controlValue = attendeeCustomField.textValue;
                  break;
                }
                case (FreeTextFormat.Number): {
                  controlValue = attendeeCustomField.numberValue || attendeeCustomField.numberValue === 0 ?  +attendeeCustomField.numberValue : null;
                  break;
                }
                case (FreeTextFormat.Date): {
                  attendeeCustomField.dateTimeValue ? controlValue = new Date(attendeeCustomField.dateTimeValue) : controlValue = null;
                  break;
                }
                case (FreeTextFormat.Time): {
                    controlValue = SetDateBasedOnTimeString(attendeeCustomField.textValue);
                  break;
                }
              }
              form.addControl(customField.id, new UntypedFormControl(controlValue));
            }
            this.customFieldsInvitee++;
          } else {
            if (customField.customFieldType === CustomFieldType.MultipleSelect || customField.customFieldType === CustomFieldType.SingleSelect) {
              if (customField.selectFormat === SelectFormat.Dropdown) {
                if (customField.required) {
                  form.addControl(customField.id, new UntypedFormControl('', Validators.required));
                  if (!this.hasVisibleToMainInvitee(customField)) {
                    form.controls[customField.id].clearValidators();
                    form.controls[customField.id].updateValueAndValidity({ emitEvent: false });
                  }
                } else {
                  form.addControl(customField.id, new UntypedFormControl(''));
                }
              } else {
                const controls = customField.customFieldOptions.map((c) => {
                  return new UntypedFormControl();
                });
                if (customField.required) {
                  form.addControl(customField.id, new UntypedFormArray(controls, this.multipleCheckboxRequireOne));
                  if (!this.hasVisibleToMainInvitee(customField)) {
                    form.controls[customField.id].clearValidators();
                    form.controls[customField.id].updateValueAndValidity({ emitEvent: false });
                  }

                } else {
                  form.addControl(customField.id, new UntypedFormArray(controls));
                }
              }
            } else {
              form.addControl(customField.id, new UntypedFormControl(''));
            }
            this.customFieldsInvitee++;
          }
        }
      });
    }

    for (const guest of attendeeDetails.guests) {
      this.currentGuests++;
      const control = form.controls['guests'] as UntypedFormArray;
      control.push(this.initGuestForEdit(registrationPage, guest));
      this.multiDisabledOptions.push({ [this.locationDateTimeId]: { } });
    }

    // Always add agreement checkbox
    form.addControl('agreement', new UntypedFormControl(false, Validators.required));
    return form;
  }

  public initGuest(guestId: string, guestIndex: number, registrationPage: IInvitationRegistrationPage) {
    const guest = this.fb.group({
      guestId
    });

    if (registrationPage.showGuestName) {
      guest.addControl('firstName', new UntypedFormControl(''));
      guest.addControl('lastNamePrefix', new UntypedFormControl(''));
      guest.addControl('lastName', new UntypedFormControl(''));
    }
    if (registrationPage.showGuestEmail) {
      guest.addControl('email', new UntypedFormControl(''));
    }
    if (registrationPage.showGuestMobile) {
      guest.addControl('mobile', new UntypedFormControl(''));
    }
    if (registrationPage.showGuestCompany) {
      guest.addControl('company', new UntypedFormControl(''));
    }
    if (registrationPage.showGuestAddress) {
      guest.addControl('addressLineOne', new UntypedFormControl(''));
      guest.addControl('addressLineTwo', new UntypedFormControl(''));
      guest.addControl('city', new UntypedFormControl(''));
      guest.addControl('postalCode', new UntypedFormControl(''));
      guest.addControl('country', new UntypedFormControl(''));
    }

    if (registrationPage.showGuestJobTitle) {
      guest.addControl('jobTitle', new UntypedFormControl(''));
    }
    if (registrationPage.showGuestCustomerNumber) {
      guest.addControl('customerNumber', new UntypedFormControl(''));
    }

    if (registrationPage.customFields) {
      registrationPage.customFields.forEach((customField) => {
        if (customField.showGuest) {
          if (customField.customFieldType === CustomFieldType.MultipleSelect || customField.customFieldType === CustomFieldType.SingleSelect) {
            if (customField?.selectFormat === SelectFormat.CheckboxRadio) {
              const selection = customField.customFieldOptions
                .map(c => c.isDefault);
              const controls = selection.map((c) => {
                return new UntypedFormControl(c);
              });
            if (customField.requiredGuest) {
                if (customField.customFieldType === CustomFieldType.SingleSelect) {
                    guest.addControl(customField.id, new UntypedFormArray(controls, this.multipleCheckboxRequireOne));
                } else {
                guest.addControl(customField.id, new UntypedFormArray(
                    controls, this.multipleCheckboxRequireBetween(customField.multipleChoiceConfigOptions.minSelect || 1, customField.multipleChoiceConfigOptions.maxSelect || controls.length)));
                }
                if (customField.customFieldOptionsExtraGuest.length === 0) {
                    const control = guest.get(customField.id);
                    control.clearValidators();
                    control.updateValueAndValidity({ emitEvent: false });
                }
            } else {
                guest.addControl(customField.id, new UntypedFormArray(controls));
                if (customField.customFieldType === CustomFieldType.MultipleSelect) {
                    const customFieldControl = guest.get(customField.id) as UntypedFormArray;
                    customFieldControl.valueChanges.subscribe(() => {
                        const checkedCount = customFieldControl.controls.filter(control => control.value).length;
                        if (checkedCount > 0) {
                            customFieldControl.setValidators(this.multipleCheckboxRequireBetween(
                                customField.multipleChoiceConfigOptions.minSelect || 1, customField.multipleChoiceConfigOptions.maxSelect || controls.length));
                        } else {
                            customFieldControl.clearValidators();
                        }
                        customFieldControl.updateValueAndValidity({ emitEvent: false });
                    });
                }
            }
          } else {
            const selection = customField.customFieldOptions
                .filter(x => x.isDefault)
                .map(x => x.id);
            guest.addControl(customField.id, new UntypedFormControl(selection));
              if (customField.customFieldType === CustomFieldType.MultipleSelect) {
                if (customField.requiredGuest) {
                    guest.controls[customField.id]
                    .addValidators([this.minSelectValidatorGuest(customField.multipleChoiceConfigOptions.minSelect),
                                    this.maxSelectValidatorGuest(customField.multipleChoiceConfigOptions.maxSelect ?? customField.customFieldOptions?.length)]);
                    if (customField.customFieldOptionsExtraGuest.length === 0) {
                        const control = guest.get(customField.id);
                        control.clearValidators();
                        control.updateValueAndValidity({ emitEvent: false });
                    }

                } else {
                    guest.addControl(customField.id, new UntypedFormControl(selection));
                    const control = guest.get(customField.id);
                    control.valueChanges.subscribe(() => {
                        control.clearValidators();
                        control.updateValueAndValidity({ emitEvent: false });
                        if (control.value?.length > 0) {
                            const validators = [
                            this.minSelectValidatorGuest(customField.multipleChoiceConfigOptions.minSelect),
                            this.maxSelectValidatorGuest(customField.multipleChoiceConfigOptions.maxSelect ?? customField.customFieldOptions?.length)
                            ];
                            control.setValidators(validators);
                            control.updateValueAndValidity({ emitEvent: false });
                        } else {
                            control.clearValidators();
                            control.updateValueAndValidity({ emitEvent: false });
                        }
                    });
                }
              } else {
                if (customField.requiredGuest && customField.customFieldOptionsExtraGuest.length === 0) {
                    const control = guest.get(customField.id);
                    control.clearValidators();
                    control.updateValueAndValidity({ emitEvent: false });
                }
              }
          }
        } else if (customField.customFieldType === CustomFieldType.Boolean) {
            if (customField.checkboxFormat === CheckboxFormat.OptIn) {
              guest.addControl(customField.id, new UntypedFormControl(false));
            } else {
              guest.addControl(customField.id, new UntypedFormControl(true));
            }
          } else {
            guest.addControl(customField.id, new UntypedFormControl(''));
          }
          this.customFieldsGuest++;
        }
      });
    }

    this.initGuestFileUpload(guestId, guestIndex, this.invitationRegistrationPage, this.invitationAttendees);

    return guest;
  }

  private getSelectedCustomFieldOptions(fieldData: ICustomFieldEdit): any {
    return fieldData.customFieldOptions.map(f => f.id);
  }

  public initGuestForEdit(registrationPage: IInvitationRegistrationPage, existingGuest: IGuestDetails) {
    const guest = this.fb.group({
    });

    guest.addControl('id', new UntypedFormControl(existingGuest.id));

    if (registrationPage.showGuestName) {
      guest.addControl('firstName', new UntypedFormControl(existingGuest.firstName));
      guest.addControl('lastNamePrefix', new UntypedFormControl(existingGuest.lastNamePrefix ?? ''));
      guest.addControl('lastName', new UntypedFormControl(existingGuest.lastName));
    }
    if (registrationPage.showGuestEmail) {
      guest.addControl('email', new UntypedFormControl(existingGuest.email));
    }
    if (registrationPage.showGuestMobile) {
      guest.addControl('mobile', new UntypedFormControl(existingGuest.mobile));
    }
    if (registrationPage.showGuestCompany) {
      guest.addControl('company', new UntypedFormControl(existingGuest.companyName));
    }
    if (registrationPage.showGuestAddress) {
      guest.addControl('addressLineOne', new UntypedFormControl(existingGuest.addressLineOne));
      guest.addControl('addressLineTwo', new UntypedFormControl(existingGuest.addressLineTwo));
      guest.addControl('city', new UntypedFormControl(existingGuest.city));
      guest.addControl('postalCode', new UntypedFormControl(existingGuest.postalCode));
      guest.addControl('country', new UntypedFormControl(existingGuest.country));
    }
    if (registrationPage.showGuestJobTitle) {
      guest.addControl('jobTitle', new UntypedFormControl(existingGuest.jobTitle));
    }
    if (registrationPage.showGuestCustomerNumber) {
      guest.addControl('customerNumber', new UntypedFormControl(existingGuest.customerNumber));
    }

    if (registrationPage.customFields) {
      registrationPage.customFields.forEach((customField) => {
        if (customField.showGuest) {
          const attendeeCustomField = existingGuest.customFields.find((x) => x.id === customField.id);
          if (attendeeCustomField) {
            if (customField.customFieldType === CustomFieldType.MultipleSelect || customField.customFieldType === CustomFieldType.SingleSelect) {
              if (customField.selectFormat === SelectFormat.Dropdown) {
                const selectedValues = this.getSelectedCustomFieldOptions(attendeeCustomField);
                guest.addControl(customField.id, new UntypedFormControl(selectedValues));
              } else {
                this.setMultipleCustomFields(guest, customField, attendeeCustomField, true);
              }
            } else if (customField.customFieldType === CustomFieldType.Boolean) {
              const guestControlValue = attendeeCustomField?.booleanValue;
              guest.addControl(customField.id, new UntypedFormControl(guestControlValue));
            } else {
              let guestControlValue;
              switch (customField.freeTextFormat) {
                case (FreeTextFormat.Text): {
                  guestControlValue = attendeeCustomField.textValue;
                  break;
                }
                case (FreeTextFormat.CountryCode): {
                  guestControlValue = attendeeCustomField.textValue;
                  break;
                }
                case (FreeTextFormat.Number): {
                  guestControlValue = attendeeCustomField.numberValue || attendeeCustomField.numberValue === 0 ?  +attendeeCustomField.numberValue : null;
                  break;
                }
                case (FreeTextFormat.Date): {
                  attendeeCustomField.dateTimeValue ? guestControlValue = new Date(attendeeCustomField.dateTimeValue) : guestControlValue = null;
                  break;
                }
                case (FreeTextFormat.Time): {
                    guestControlValue = SetDateBasedOnTimeString(attendeeCustomField.textValue);
                  break;
                }
              }
              guest.addControl(customField.id, new UntypedFormControl(guestControlValue));
            }
            this.customFieldsGuest++;
          } else {
            if (customField.customFieldType === CustomFieldType.MultipleSelect || customField.customFieldType === CustomFieldType.SingleSelect) {
              if (customField.selectFormat === SelectFormat.Dropdown) {
                if (customField.requiredGuest) {
                  guest.addControl(customField.id, new UntypedFormControl('', Validators.required));
                } else {
                  guest.addControl(customField.id, new UntypedFormControl(''));
                }
              } else {
                const controls = customField.customFieldOptions.map(c => {
                  return new UntypedFormControl();
                });
                if (customField.requiredGuest) {
                  guest.addControl(customField.id, new UntypedFormArray(controls, this.multipleCheckboxRequireOne));
                } else {
                  guest.addControl(customField.id, new UntypedFormArray(controls));
                }
              }
            } else {
              guest.addControl(customField.id, new UntypedFormControl(''));
            }
            this.customFieldsInvitee++;
          }
        }
      });
    }
    return guest;
  }

  public setMultipleCustomFields(form, customField, customFieldData, guest = false) {
    const controls = customField.customFieldOptions.map((c) => {
      const isPresent = customFieldData.customFieldOptions.find((x) => x.id === c.id);
      if (isPresent) {
        return new UntypedFormControl(c.id);
      } else {
        return new UntypedFormControl();
      }
    });
    const requiredProperty = guest ? 'requiredGuest' : 'required';
    if (customField[requiredProperty]) {
      form.addControl(customField.id, new UntypedFormArray(controls, this.multipleCheckboxRequireOne));
    } else {
      form.addControl(customField.id, new UntypedFormArray(controls));
    }
  }

  public isValidInviteeCustomField(fieldName: string) {
    const f = this.registrationForm.get(fieldName);
    return !f.valid && (f.dirty || f.touched);
  }

  public isValidGuestCustomField(formArray: string, formGroup: number, fieldName: string) {
    const f = this.registrationForm.get(formArray).get(formGroup.toString()).get(fieldName);
    return !f.valid && (f.dirty || f.touched);
  }

  public showInvitee() {
    return true;
  }

  public showGuest() {
    return (this.invitationRegistrationPage.showGuestName || this.invitationRegistrationPage.showGuestEmail || this.invitationRegistrationPage.showGuestMobile ||
      this.invitationRegistrationPage.showGuestAddress || this.invitationRegistrationPage.showGuestCompany || this.invitationRegistrationPage.showGuestJobTitle ||
      this.invitationRegistrationPage.customFields.find((x) => x.showGuest));
  }

  public onFocusCalendar(event) {
    event.target.parentNode.parentNode.classList.add('focus');
  }
  public onCloseCalendar(event) {
    event.target.parentNode.parentNode.classList.remove('focus');
  }

  private setData() {
    // Initialize edit data form for multivenue
    if(this.eventDetails && this.eventDetails.multiVenueConfiguration && this.eventDetails.multiVenueConfiguration.isMultiVenue){
      this.assignLocationDateTimeIdsToForm();
      this.removeDuplicatedGuests();
    }
    this.registrationForm = this.initFormForEdit(this.invitationRegistrationPage, this.invitationAttendees);
    this.form.form = this.registrationForm;
    this.state.form = this.form;
    this.isClosed = false;
    this.initFileUpload(this.invitationRegistrationPage, this.invitationAttendees);
  }

  private canShowVenueDropdown(invitationStatus: IInvitationStatus) {
    if (invitationStatus.locationDateTimes.length > 0) {
      this.showVenueDropdown = true;
    } else {
      this.showVenueDropdown = false;
    }
  }

  public duplicateMainInviteePerRegistration(mainInviteeData: IRegistration){
    // Find related mainInvitees
    const duplicatedMainInvitees = [];
    let mainInviteDuplicated: any = [];

    const relatedMainInvitees = this.copiedAttendeeDetails.guests.filter(a => a.parentId === mainInviteeData.id);
    this.locationDateTimeIdsPerRegistration.forEach((locationDateTimeId, locationDateTimeIdIndex)=>{
      if(mainInviteeData.locationDateTimeId !== locationDateTimeId){
        if(relatedMainInvitees.length === 0){
          // No relatedMainInvitees, duplicate mainInvitee with a new guid (id)
          mainInviteDuplicated = {
            id: uuid(),
            parentId: mainInviteeData.id,
            isAnonymous: false,
            firstName: mainInviteeData.firstName,
            lastNamePrefix: mainInviteeData.lastNamePrefix,
            lastName: mainInviteeData.lastName,
            email: mainInviteeData.email,
            mobile: mainInviteeData.mobile,
            companyName: mainInviteeData.companyName,
            addressLineOne: mainInviteeData.addressLineOne,
            addressLineTwo: mainInviteeData.addressLineTwo,
            city: mainInviteeData.city,
            postalCode: mainInviteeData.postalCode,
            country: mainInviteeData.country,
            locationDateTimeId,
            customFields: mainInviteeData.customFields.slice(),
            jobTitle: mainInviteeData.jobTitle,
            customerNumber: mainInviteeData.customerNumber,
          };
        }else{
            // There are relatedMainInvitees, iterate through them and assign their id
          relatedMainInvitees.forEach(() => {
            mainInviteDuplicated = {
             id: (locationDateTimeIdIndex - 1 < relatedMainInvitees.length)
               ? relatedMainInvitees[locationDateTimeIdIndex - 1].id
               : uuid(),
             parentId: mainInviteeData.id,
             isAnonymous: false,
             firstName: mainInviteeData.firstName,
             lastNamePrefix: mainInviteeData.lastNamePrefix,
             lastName: mainInviteeData.lastName,
             email: mainInviteeData.email,
             mobile: mainInviteeData.mobile,
             companyName: mainInviteeData.companyName,
             addressLineOne: mainInviteeData.addressLineOne,
             addressLineTwo: mainInviteeData.addressLineTwo,
             city: mainInviteeData.city,
             postalCode: mainInviteeData.postalCode,
             country: mainInviteeData.country,
             locationDateTimeId,
             customFields: mainInviteeData.customFields.slice(),
             jobTitle: mainInviteeData.jobTitle,
             customerNumber: mainInviteeData.customerNumber,
           };
         });
        }
        duplicatedMainInvitees.push(mainInviteDuplicated);
      }
    });

    return duplicatedMainInvitees;
  }

  public duplicateMainInviteePerAttendee(mainInviteeData: IRegistration){
    // Find related mainInvitees
    const duplicatedMainInvitees = [];
    let mainInviteDuplicated: any = [];

    const relatedMainInvitees = this.copiedAttendeeDetails.guests.filter(a => a.parentId === mainInviteeData.id);

    Object.keys(this.multiVenueSelectionPerAttendee).forEach((guestId) => {
      if (guestId === 'mainInvitee') {
        const locationDateTimeIds = this.multiVenueSelectionPerAttendee[guestId];
        locationDateTimeIds.forEach((locationDateTimeId, locationDateTimeIdIndex) => {
          if (mainInviteeData.locationDateTimeId !== locationDateTimeId) {
            if (relatedMainInvitees.length === 0) {
              // No relatedMainInvitees, duplicate mainInvitee with a new guid (id)
              mainInviteDuplicated = {
                id: uuid(),
                parentId: mainInviteeData.id,
                isAnonymous: false,
                firstName: mainInviteeData.firstName,
                lastNamePrefix: mainInviteeData.lastNamePrefix,
                lastName: mainInviteeData.lastName,
                email: mainInviteeData.email,
                mobile: mainInviteeData.mobile,
                companyName: mainInviteeData.companyName,
                addressLineOne: mainInviteeData.addressLineOne,
                addressLineTwo: mainInviteeData.addressLineTwo,
                city: mainInviteeData.city,
                postalCode: mainInviteeData.postalCode,
                country: mainInviteeData.country,
                locationDateTimeId,
                customFields: mainInviteeData.customFields.slice(),
                jobTitle: mainInviteeData.jobTitle,
                customerNumber: mainInviteeData.customerNumber,
              };
            } else {
              // There are relatedMainInvitees, iterate through them and assign their id
              relatedMainInvitees.forEach((_, index) => {
                mainInviteDuplicated = {
                  id: (locationDateTimeIdIndex - 1 < relatedMainInvitees.length)
                    ? relatedMainInvitees[locationDateTimeIdIndex - 1].id
                    : uuid(),
                  parentId: mainInviteeData.id,
                  isAnonymous: false,
                  firstName: mainInviteeData.firstName,
                  lastNamePrefix: mainInviteeData.lastNamePrefix,
                  lastName: mainInviteeData.lastName,
                  email: mainInviteeData.email,
                  mobile: mainInviteeData.mobile,
                  companyName: mainInviteeData.companyName,
                  addressLineOne: mainInviteeData.addressLineOne,
                  addressLineTwo: mainInviteeData.addressLineTwo,
                  city: mainInviteeData.city,
                  postalCode: mainInviteeData.postalCode,
                  country: mainInviteeData.country,
                  locationDateTimeId,
                  customFields: mainInviteeData.customFields.slice(),
                  jobTitle: mainInviteeData.jobTitle,
                  customerNumber: mainInviteeData.customerNumber,
                };
              });
            }
            duplicatedMainInvitees.push(mainInviteDuplicated);
          }
        });
      }
    });
    return duplicatedMainInvitees;
  }

  public updateAllAttendees(attendeeDetails, isPerAttendeeFlow: boolean){
    const newAttendeeDetails = [];

    attendeeDetails.forEach(attendee => {
        if(!isPerAttendeeFlow){
          newAttendeeDetails.push(...this.duplicateAttendeesPerRegistration([attendee], this.copiedAttendeeDetails, this.locationDateTimeIdsPerRegistration));
        }else{
          newAttendeeDetails.push(...this.duplicateAttendeesPerAttendee([attendee], this.copiedAttendeeDetails, this.multiVenueSelectionPerAttendee));
        }
    });
    return newAttendeeDetails;
  }

  public duplicateAttendeesPerRegistration(attendeeDetails, copiedAttendeeDetails, locationDateTimeIdsPerRegistration) {
    const newguestlist = [];
    // Iterate over each set of guests in attendeeDetails
    attendeeDetails.forEach((attendee) => {
      let firstOriginalAttendee = true;
      let parentId = null;

      locationDateTimeIdsPerRegistration.forEach((locationDateTimeId, locationDateTimeIdIndex) => {
          const newAttendee = { ...JSON.parse(JSON.stringify(attendee)) };
          newAttendee.locationDateTimeId = locationDateTimeId;
          newAttendee.isAnonymous = this.guestsAnonymous ? true : false;

          if (firstOriginalAttendee) {
              newAttendee.parentId = null;
              parentId = newAttendee.id;
              firstOriginalAttendee = false;
          } else {
              // Assign parentId from original guest
              newAttendee.parentId = parentId;

              const relatedAttendees = copiedAttendeeDetails.guests.filter(a => a.parentId === parentId);

              if (locationDateTimeIdIndex - 1 < relatedAttendees.length) {
                  newAttendee.id = relatedAttendees[locationDateTimeIdIndex - 1].id;
              } else {
                  newAttendee.id = uuid();
              }
          }
          newguestlist.push(newAttendee);
      });
    });
    attendeeDetails = newguestlist;
    return attendeeDetails;
  }

  public duplicateAttendeesPerAttendee(attendeeDetails, copiedAttendeeDetails, multiVenueSelectionPerAttendee) {
    const newguestlist = [];

    attendeeDetails.forEach((attendee) => {
      let firstOriginalAttendee = true;
      let parentId = null;

      let locationDateTimeIds;
      if(this.showGuestDropdown && this.guestsAnonymous){
         locationDateTimeIds = multiVenueSelectionPerAttendee['mainInvitee'];
      }else{
        locationDateTimeIds = multiVenueSelectionPerAttendee[attendee.id];
      }
      if (locationDateTimeIds) {
        locationDateTimeIds.forEach((locationDateTimeId, locationDateTimeIdIndex) => {
          const newAttendee = { ...JSON.parse(JSON.stringify(attendee)) };
          newAttendee.locationDateTimeId = locationDateTimeId;
          newAttendee.isAnonymous = this.guestsAnonymous ? true : false;

          if (firstOriginalAttendee) {
            newAttendee.parentId = null;
            parentId = newAttendee.id;
            firstOriginalAttendee = false;
          } else {
            // Assign parentId from original guest
            newAttendee.parentId = parentId;

            const relatedAttendees = copiedAttendeeDetails.guests.filter(a => a.parentId === parentId);

            if (relatedAttendees.length > 0 && (locationDateTimeIdIndex < relatedAttendees.length)) {
              newAttendee.id = relatedAttendees[locationDateTimeIdIndex].id;
            } else {
              newAttendee.id = uuid();
            }
          }
          newguestlist.push(newAttendee);
        });
      }
    });

    return newguestlist;
  }

  private canShowGuestDropdown(status: IInvitationStatus) {
    if (status.maxGuests > 1 || this.invitationRegistrationPage.overrideGuests) {
      this.showGuestDropdown = true;
    } else {
      this.showGuestDropdown = false;
    }
  }

  private populateEventsDropdown() {

    let locationDateTimes = this.invitationStatus.locationDateTimes;

    if (this.eventDetails.multiVenueConfiguration.isMultiVenue && this.eventDetails.multiVenueConfiguration.multiVenueType === 2) {
      locationDateTimes = this.configureVenuesDynamicSegments();
    }

    for (const locationDateTime of locationDateTimes) {

      // Construct date and time
      const formattedDate = moment(locationDateTime.fromDate).format('L');
      const formattedTime = moment(locationDateTime.fromDate).format('LT');

      // Set venue in dropdown greyed out when venue is sold out and does not have unlimited overbooking or venue date has surpassed current date
      // This does not apply when the registration is being edited, otherwise the user cannot reselect the registrered venue when sold out
      let venueSoldOut = false;

      venueSoldOut =
        (locationDateTime.isSoldOut &&
          locationDateTime.overBooking !== 1) ||
        new Date(locationDateTime.fromDate) < new Date();

      let venueSoldOutLabel = '';
      if (venueSoldOut) {
        venueSoldOutLabel = ' ' + '(' + this.translation.instant('INPUT.VENUENAME.SOLDOUT') + ')';
      }

      const dropdownVenueModelData: [string, string, boolean] = [
        locationDateTime.id,
        locationDateTime.name + ' - ' + formattedDate + ' ' + formattedTime + venueSoldOutLabel,
        false
      ];

      const extraParams = this.isMultiVenue && this.multiVenueType === MultiVenueType.Attendee ?
                          [locationDateTime.isVisibleToMainInvitee, locationDateTime.isVisibleToGuests] : [true];
      this.itemsEvents.push(new DropdownVenueModel(...dropdownVenueModelData, ...extraParams));
    }
  }

  private mergeVenuesDynamicSegments(locationDateTimesMainInvitee: any, locationDateTimesGuests: any){
    for (const locationDateTime of locationDateTimesMainInvitee) {
      locationDateTime.isVisibleToMainInvitee = true;
      const matchingGuestVenue = locationDateTimesGuests.find(extra => extra.id === locationDateTime.id);
      if (matchingGuestVenue) {
          locationDateTime.isVisibleToGuests = true;
      }
    }
    for (const locationDateTime of locationDateTimesGuests) {
      locationDateTime.isVisibleToGuests = true;
    }
    return [
      ...locationDateTimesMainInvitee,
      ...locationDateTimesGuests.filter(extra =>
          !locationDateTimesMainInvitee.some(existing => existing.id === extra.id)
      )
    ];
  }

  private configureVenuesDynamicSegments(){
    const locationDateTimesDynamicSegments = this.mergeVenuesDynamicSegments(this.invitationRegistrationPage.locationDateTimes, this.invitationRegistrationPage.locationDateTimesGuests);

    const visibilityMap = new Map(
      locationDateTimesDynamicSegments.map(locationDateTimeId => [locationDateTimeId.id, {
        isVisibleToMainInvitee: locationDateTimeId.isVisibleToMainInvitee ?? false,
        isVisibleToGuests: locationDateTimeId.isVisibleToGuests ?? false
      }])
    );

    return this.invitationStatus.locationDateTimes.map(item => ({
      ...item,
      ...visibilityMap.get(item.id)
    }));
  }


  private populateGuestsDropdown(amount: number) {
    this.itemsGuests = [];
    for (let i = 0; i < amount; i++) {
      if (i === 0) {
        this.itemsGuests.push(new DropdownModel(i, this.translation.instant('PERSON.SINGLE')));
      } else {
        this.itemsGuests.push(new DropdownModel(i, i + 1 + ' ' + this.translation.instant('PERSON.MULTIPLE')));
      }
    }
  }

  private populateGuestsDropdownMultiVenue(){
    if(this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && (this.maxVenues > 1 || this.maxVenues === null)){
      this.populateGuestsDropdown(this.maxGuests);
      this.selectedNumberOfGuests = this.invitationAttendees.guests.length;
    }else if(this.isMultiVenue && this.multiVenueType === MultiVenueType.Attendee && (this.maxVenueType === MaxVenueType.Attendee || this.maxVenueType === MaxVenueType.Registration)){
      this.populateGuestsDropdown(this.maxGuests);
      this.selectedNumberOfGuests = this.invitationAttendees.guests.length;
    }
  }

  private setMaxGuests(value: string) {
    // Check if overbooking is set to none
    if (this.invitationStatus.locationDateTimes.find((x) => x.id === value).overBooking === 0) {

      if (this.invitationRegistrationPage.overrideGuests) {
        // If tickets available for the venue is more than the max amount of guests specified by the user
        if (this.invitationStatus.locationDateTimes.find((x) => x.id === value).ticketAvailable >= 100) {

          // Set the max amount of guests to the value specified by the user
          this.maxGuests = 100;

          // If tickets available for the venue is less than the max amount of guests specified by the user
        } else {
          // If user edits registration, add the amount of subscribed guests for this venue, since ticketsAvailable has been reduced
          if (this.invitationAttendees.locationDateTimeId === value) {
            // Check of het aantal maxGuests kleiner is dan het aantal geregistreerde guests
            // Dit kan voorkomen wanneer een user het aantal guests verminderd voor een geregistreerde gebruiker
            this.maxGuests = (this.invitationAttendees.guests.length + 1) + this.invitationStatus.locationDateTimes.find((x) => x.id === value).ticketAvailable;
          } else {
            // Set the max amount of guests to the amount of tickets available for the venue
            this.maxGuests = this.invitationStatus.locationDateTimes.find((x) => x.id === value).ticketAvailable;
          }
        }
      } else {
        // If tickets available for the venue is more than the max amount of guests specified by the user
        if (this.invitationStatus.locationDateTimes.find((x) => x.id === value).ticketAvailable >= this.invitationStatus.maxGuests) {

          // Set the max amount of guests to the value specified by the user
          this.maxGuests = this.invitationStatus.maxGuests;

          // If tickets available for the venue is less than the max amount of guests specified by the user
        } else {
          // If user edits registration, add the amount of subscribed guests for this venue, since ticketsAvailable has been reduced
          if (this.invitationAttendees.locationDateTimeId === value) {
            // Check of het aantal maxGuests kleiner is dan het aantal geregistreerde guests
            // Dit kan voorkomen wanneer een user het aantal guests verminderd voor een geregistreerde gebruiker
            this.maxGuests = (this.invitationAttendees.guests.length + 1) + this.invitationStatus.locationDateTimes.find((x) => x.id === value).ticketAvailable;
            // If more than the invitations max guests
            if (this.maxGuests > this.invitationStatus.maxGuests) {
              this.maxGuests = this.invitationStatus.maxGuests;
            }
          } else {
            // Set the max amount of guests to the amount of tickets available for the venue
            this.maxGuests = this.invitationStatus.locationDateTimes.find((x) => x.id === value).ticketAvailable;
          }
        }
      }

      // If overbooking is unlimited, set maxGuests to max specified by user
    } else {
      if (this.invitationRegistrationPage.overrideGuests) {
        this.maxGuests = 100;
      } else {
        this.maxGuests = this.invitationStatus.maxGuests;
      }
    }

    // Set dropdown to max of invitee
    this.populateGuestsDropdown(this.maxGuests);
  }

  private selectEvent(value: string) {
    const previousLocationDateTimeId = this.locationDateTimeId;
    // Change venue
    this.locationDateTimeId = value;
    this.multiDisabledOptions.forEach((op) => {
      op[this.locationDateTimeId] = op[this.locationDateTimeId] || { };
    });


    this.previousSelectedVenueIdCustomFieldRouting = previousLocationDateTimeId;
    this.multiDisabledOptions.forEach((multiDisabledOption)=>{
      multiDisabledOption[this.locationDateTimeId] = multiDisabledOption[this.previousSelectedVenueIdCustomFieldRouting];
    });
    this.multiDisabledOptions.forEach((multiDisabledOption)=>{
      multiDisabledOption[this.previousSelectedVenueIdCustomFieldRouting] = [];
    });
    this.fieldsForRegistrationForm.forEach((field)=>{
      const { isCustomFieldRouted } = this.customFieldHasRouting(field);
      if(isCustomFieldRouted){
        this.modifyEnteredOptionsSingleSelectFlow(field, field.customFieldOptions);
      }
    });

    const guestsArr = this.registrationForm.controls['guests'].value;
    if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
      this.changeVenueRouting(this.locationDateTimeId);
      this.resetDoubleGoToForVenueRouting();
      this.changeHiddenFieldsForVenue(this.hiddenFieldIndexesPerVenue, this.showedFieldIndexesPerVenue);
      guestsArr.forEach((element, i) => {
        this.changeHiddenFieldsForVenue(this.hiddenFieldIndexesPerVenue, this.showedFieldIndexesPerVenue, i);
      });
    }

    const selectedVenue = this.invitationStatus.locationDateTimes.find((x) => x.id === value);
    if(this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1 && !selectedVenue.isSoldOut && selectedVenue.overBooking === 0){
      this.setNewGuestForm(value);
    }
    if(!this.isMultiVenue){
      if((this.previousVenueSelectionTicketAvailable > selectedVenue.ticketAvailable) && this.currentGuests
        && selectedVenue.overBooking === 0 && !selectedVenue.isSoldOut){
          const targetGuestCount = selectedVenue.ticketAvailable - 1;
          const currentGuestCount = this.registrationForm.value.guests.length;
          if (currentGuestCount > targetGuestCount) {
            // Remove guests from end to start
            for (let i = currentGuestCount - 1; i >= targetGuestCount; i--) {
              this.removeGuest(i);
            }
          }
      }
      this.previousVenueSelectionTicketAvailable = selectedVenue.ticketAvailable;
    }

    // Set max amount of guests based on selected venue
    this.setMaxGuests(value);
    this.previousSelectedVenueId = this.locationDateTimeId;

    this.refreshFieldOptions();
  }

  public deleteCustomFieldItems(guestId: string){
    if(this.customFieldOptionsItems[guestId]){
      delete this.customFieldOptionsItems[guestId];
    }
  }

  public modifyEnteredOptionsSingleSelectFlow(field, fieldOptions){
    if(this.registrationForm.value[field.id]){
    this.registrationForm.value[field.id] = Array.isArray(this.registrationForm.value[field.id]) ? this.registrationForm.value[field.id] : [this.registrationForm.value[field.id]];
      for (let i = this.registrationForm.value[field.id].length - 1; i >= 0; i--) {
        const selectedOptionId = this.registrationForm.value[field.id][i];
        if(field.selectFormat === SelectFormat.CheckboxRadio){
          const fieldOption = fieldOptions[i];
          if(selectedOptionId){
            fieldOption.entered[this.locationDateTimeId]++;
            fieldOption.entered[this.previousSelectedVenueIdCustomFieldRouting]--;
            if(fieldOption.maxEntries && fieldOption.entered[this.locationDateTimeId] > fieldOption.maxEntries){
              const mainInviteeControl = (this.registrationForm.get(field.id) as FormArray);
              const valuesArray = mainInviteeControl.get(field.id).value;
              if (Array.isArray(valuesArray) && valuesArray.length > i) {
                valuesArray[i] = false;
                mainInviteeControl.get(field.id).setValue(valuesArray);
              }
              fieldOption.entered[this.locationDateTimeId]--;
              const guestIndex = null;
              this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered--;
              if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked){
                delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked[guestIndex];
              }
              this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex =
              this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex?.filter(value => value !== guestIndex);
              if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered === 0){
                delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex;
              }
              const multiDisabledOptions = this.multiDisabledOptions[guestIndex + 1][this.locationDateTimeId][field.id];
              const indexToDelete = multiDisabledOptions.indexOf(selectedOptionId);
              if (indexToDelete !== -1) {
                multiDisabledOptions.splice(indexToDelete, 1);
              }
            }
          }
        }else{
          fieldOptions.forEach(fieldOption => {
            if(fieldOption.id === selectedOptionId){
              fieldOption.entered[this.previousSelectedVenueIdCustomFieldRouting]--;
              fieldOption.entered[this.locationDateTimeId]++;
              if(fieldOption.maxEntries && fieldOption.entered[this.locationDateTimeId] > fieldOption.maxEntries){
                const mainInviteeControl = this.registrationForm.get(field.id) as FormArray;
                mainInviteeControl.setValue(null);
                fieldOption.entered[this.locationDateTimeId]--;
                this.multiDisabledOptions[0][this.locationDateTimeId] = [];
              }
            }
          });
        }
      }
    }

    if(this.registrationForm.value.guests){
      this.registrationForm.value.guests.forEach((guest, guestIndex)=>{
        guest[field.id] = Array.isArray(guest[field.id]) ? guest[field.id] : [guest[field.id]];
        if(guest[field.id]){
          guest[field.id].forEach((selectedOptionId, index) => {
            if(field.selectFormat === SelectFormat.CheckboxRadio){
              const fieldOption = fieldOptions[index];
              if(selectedOptionId){
                fieldOption.entered[this.locationDateTimeId]++;
                fieldOption.entered[this.previousSelectedVenueIdCustomFieldRouting]--;
                if(fieldOption.maxEntries && fieldOption.entered[this.locationDateTimeId] > fieldOption.maxEntries){
                  const guestControl = (this.registrationForm.get('guests') as FormArray).at(guestIndex);
                  const valuesArray = guestControl.get(field.id).value;
                  if (Array.isArray(valuesArray) && valuesArray.length > index) {
                    valuesArray[index] = false;
                    guestControl.get(field.id).setValue(valuesArray);
                  }
                  fieldOption.entered[this.locationDateTimeId]--;
                  this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered--;
                  if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked){
                    delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].isChecked[guestIndex];
                  }
                  this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex =
                  this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex?.filter(value => value !== guestIndex);
                  if(this.customFieldOptionsMultiVenue[field.id][fieldOption.id].entered === 0){
                    delete this.customFieldOptionsMultiVenue[field.id][fieldOption.id].selectedOptionForGuestIndex;
                  }
                  const multiDisabledOptions = this.multiDisabledOptions[guestIndex + 1][this.locationDateTimeId][field.id];
                  const indexToDelete = multiDisabledOptions.indexOf(fieldOption.id);
                  if (indexToDelete !== -1) {
                    multiDisabledOptions.splice(indexToDelete, 1);
                  }
                }
              }
          }else{
            fieldOptions.forEach(fieldOption => {
              if(fieldOption.id === selectedOptionId){
                fieldOption.entered[this.previousSelectedVenueIdCustomFieldRouting]--;
                fieldOption.entered[this.locationDateTimeId]++;
                if(fieldOption.maxEntries && fieldOption.entered[this.locationDateTimeId] > fieldOption.maxEntries){
                  const guestControl = this.registrationForm.get(`guests.${guestIndex}.${field.id}`) as FormArray;
                  if(field.checkboxFormat === CheckboxFormat.OptIn){
                    const valuesArray = guestControl.value;
                    if (Array.isArray(valuesArray) && valuesArray.length > index) {
                      valuesArray[index] = false;
                      guestControl.setValue(valuesArray);
                    }
                  }else{
                    guestControl.setValue(null);
                  }
                  fieldOption.entered[this.locationDateTimeId]--;
                  const multiDisabledOptions = this.multiDisabledOptions[guestIndex + 1][this.locationDateTimeId][field.id];
                  const indexToDelete = multiDisabledOptions.indexOf(selectedOptionId);
                  if (indexToDelete !== -1) {
                    multiDisabledOptions.splice(indexToDelete, 1);
                  }
                }
              }
            });
          }
          });
        }
      });
    }
  }

  // Assign locationDateTimeIds to form -> multivenue update
  public assignLocationDateTimeIdsToForm(){
    const multiVenueType = this.eventDetails.multiVenueConfiguration.multiVenueType;
    const maxVenues = this.eventDetails.multiVenueConfiguration.maxVenues;
    const maxVenueType = this.eventDetails.multiVenueConfiguration.maxVenueType;
    // Assign locationDateTimeId/s (from guests) to form
    switch(multiVenueType){
      case MultiVenueType.Registration:
        if(maxVenues === 1){
          this.locationDateTimeId = this.invitationAttendees.locationDateTimeId;
        }else if(maxVenues > 1 || maxVenues === null){
          this.locationDateTimeIdsPerRegistration.push(this.invitationAttendees.locationDateTimeId);
          this.invitationAttendees.guests.forEach((guest) => {
            const isNewVenue = !this.locationDateTimeIdsPerRegistration.includes(guest.locationDateTimeId);
            if (isNewVenue) {
                this.locationDateTimeIdsPerRegistration.push(guest.locationDateTimeId);
            }
          });
        }
        break;
      case MultiVenueType.Attendee:
        if(maxVenueType === MaxVenueType.Attendee || maxVenueType === MaxVenueType.Registration){
          if(maxVenues === 1){
            // locationDateTimeIds is assigned to mainInvitee on ngOnInit()
            // Assign locationDateTimeId to mainInvitee
            this.multiVenueSelectionPerAttendee['mainInvitee'] = [this.invitationAttendees.locationDateTimeId];

            // For each guest, assign data to the form
            this.invitationAttendees.guests.forEach(guest => {
              this.multiVenueSelectionPerAttendee[guest.id] = [guest.locationDateTimeId];
            });
          }else if(maxVenues > 1 || maxVenues === null){
          // Assign locationDateTimeId for mainInvitee from model
          this.multiVenueSelectionPerAttendee['mainInvitee'] = [this.invitationAttendees.locationDateTimeId];

          // Assign more locationDateTimeIds from duplicated mainInvitee if they exist
          const matchingMainInvitees = this.invitationAttendees.guests.filter(guest => guest.parentId === this.invitationAttendees.id);
          if (matchingMainInvitees.length > 0) {
            matchingMainInvitees.forEach(matchingMainInvitee => {
              if (this.multiVenueSelectionPerAttendee['mainInvitee']) {
                this.multiVenueSelectionPerAttendee['mainInvitee'].push(matchingMainInvitee.locationDateTimeId);
              }
            });
          }

          // Find original guests from invitationAttendees
          const originalGuests = this.invitationAttendees.guests.filter(attendee => !attendee.parentId);

          // Iterate over guests to assign locationDateTimeIds to specific guest on form
          originalGuests.forEach(originalGuest => {
            const guestLocationDateTimes = [originalGuest.locationDateTimeId];
            this.invitationAttendees.guests.forEach(guest => {
              if (originalGuest.id === guest.parentId && guest.locationDateTimeId !== originalGuest.locationDateTimeId) {
                guestLocationDateTimes.push(guest.locationDateTimeId);
              }
            });
            if (guestLocationDateTimes.length > 0) {
              this.multiVenueSelectionPerAttendee[originalGuest.id] = guestLocationDateTimes;
            }
          });
          }
        }
        break;
      default:
        break;
    }
  }

  public removeDuplicatedGuests(){
    // Check does removeDuplicated Guests works in every case
    // Create a copy of attendees
    this.copiedAttendeeDetails  = JSON.parse(JSON.stringify(this.invitationAttendees));
    // Delete duplicated guests to display form on correct way for multivenue
    this.invitationAttendees.guests = this.invitationAttendees.guests.filter(attendee => {
      return !(attendee.parentId);
    });
  }

  public setNewGuestForm(value: string){
    let ticketsAvailable;
    this.invitationStatus.locationDateTimes.forEach((locationDateTime)=>{
      if(locationDateTime.id === value){
        ticketsAvailable = locationDateTime.ticketAvailable > this.invitationStatus.maxGuests ? this.invitationStatus.maxGuests : locationDateTime.ticketAvailable;
      }
    });
    if((this.previousSelectedVenueAvailableTickets > ticketsAvailable) && this.currentGuests > 1){
      const targetGuestCount = ticketsAvailable - 1;
      const currentGuestCount = this.registrationForm.value.guests.length;
      if(currentGuestCount > targetGuestCount){
        for (let i = currentGuestCount - 1; i >= targetGuestCount; i--) {
          this.removeGuest(i);
        }
      }
    }
  }

  private saveSelectedOptionByVenue() {
    this.selectedOptionsByVenue[this.locationDateTimeId] = this.selectedOptionsByVenue[this.locationDateTimeId] || [];
    this.fieldsForRegistrationForm.forEach((field) => {
      if (field.id && (field.customFieldType === CustomFieldType.SingleSelect || field.customFieldType === CustomFieldType.MultipleSelect)) {
        const hasFieldMaxEntries = field.customFieldOptions.some((op) => op.maxEntries);
        if (hasFieldMaxEntries) {
          if (this.registrationForm.controls[field.id] && this.registrationForm.controls[field.id].value) {
            this.selectedOptionsByVenue[this.locationDateTimeId][0] = this.selectedOptionsByVenue[this.locationDateTimeId][0] || { };
            this.selectedOptionsByVenue[this.locationDateTimeId][0][field.id] = this.registrationForm.controls[field.id].value;
            this.registrationForm.controls[field.id].reset();
            this.hideFieldsAfterResetControl(field, this.hiddenInviteeFields);
          }
          for (let i = 1; i < this.currentGuests; i++) {
            this.selectedOptionsByVenue[this.locationDateTimeId][i] = this.selectedOptionsByVenue[this.locationDateTimeId][i] || { };
            const guestControlGroup = this.registrationForm.controls['guests']['controls'][i - 1];
            if (guestControlGroup && guestControlGroup.controls[field.id] && guestControlGroup.controls[field.id].value) {
              this.selectedOptionsByVenue[this.locationDateTimeId][i][field.id] = guestControlGroup.controls[field.id].value;
              guestControlGroup.controls[field.id].reset();
              this.hideFieldsAfterResetControl(field, this.hiddenGuestsFields[i - 1], i - 1);
            }
          }
        }
      }
    });
  }

  public hideFieldsAfterResetControl(field, hiddenFields, guestIndex = null) {
    const arrIndex = guestIndex !== null ? guestIndex + 1 : 0;
    field.customFieldOptions.forEach((option) => {
      const optionWithGoTo = field.optionsGoToQuestion.find((el) => el.customFieldOptionId === option.id);
      if (optionWithGoTo && optionWithGoTo.goToQuestions.length) {
        optionWithGoTo.goToQuestions.forEach((goTo) => {
          const removedIndex = this.currentShowedFields[arrIndex].findIndex((ind) => ind === goTo);
          if (removedIndex !== -1) {
            this.currentShowedFields[arrIndex].splice(removedIndex, 1);
          }
          if (!this.isHiddenField(goTo, hiddenFields)) {
            this.hideQuestion(hiddenFields, field, goTo, guestIndex);
          }
        });
      }
    });
  }

  private setOptionValueByVenue() {
    if (this.selectedOptionsByVenue[this.locationDateTimeId]) {
      this.selectedOptionsByVenue[this.locationDateTimeId].forEach((x, i) => {
        Object.keys(x).forEach((fieldKey) => {
          this.setFieldValue(fieldKey, x[fieldKey], i);
        });
      });
    }
  }

  private setFieldValue(fieldKey, value, attendeeIndex) {
    const formGroup = attendeeIndex === 0 ? this.registrationForm.controls : this.registrationForm.controls['guests']['controls'][attendeeIndex - 1]['controls'];
    formGroup[fieldKey].setValue(value);
    const field = this.fieldsForRegistrationForm.find((el) => el.id === fieldKey);
    value.forEach((element, i) => {
      if (element) {
        const optionWithGoTo = field.optionsGoToQuestion.find(el => el.customFieldOptionId === field.customFieldOptions[i].id);
        if (optionWithGoTo && optionWithGoTo.goToQuestions.length) {
          optionWithGoTo.goToQuestions.forEach((goTo) => {
            this.currentShowedFields[attendeeIndex].push(goTo);
            this.showNextQuestion(attendeeIndex === 0 ? this.hiddenInviteeFields : this.hiddenGuestsFields[attendeeIndex - 1], goTo, attendeeIndex === 0 ? null : attendeeIndex - 1);
          });
        }
      }
    });
  }

  private selectNumberOfGuests(value: any) {
    this.selectedNumberOfGuests = value;
  }

  private multipleCheckboxRequireOne(fa: UntypedFormArray) {
    let valid = false;

    for (let x = 0; x < fa.length; ++x) {
      if (fa.at(x)?.value) {
        valid = true;
        break;
      }
    }
    return valid ? null : {
      multipleCheckboxRequireOne: true
    };
  }

  private multipleCheckboxRequireBetween(min: number, max: number): ValidatorFn {
    return (fa: UntypedFormArray): ValidationErrors | null => {
      const selectedCount = fa.controls
        .map(control => control.value)
        .filter(value => value).length;

      if (selectedCount >= min && selectedCount <= max) {
        return null;
      } else {
        return { multipleCheckboxRequireBetween: {min, max} };
      }
    };
  }

  private setCustomFieldOptionsMultiVenueUpdateRegistration(){
      // Set selected option for mainInvitee
      this.invitationAttendees.customFields?.forEach((customField)=>{
        customField.customFieldOptions.forEach((fieldOption)=>{
          if(!this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].selectedOptionForGuestIndex){
            this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].selectedOptionForGuestIndex = [];
          }
          if (!this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].selectedOptionForGuestIndex.includes(null)) {
            this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].selectedOptionForGuestIndex.push(null);
          }

          const fieldRegistrationFrom = this.fieldsForRegistrationForm.filter(field=>field.id === customField.id);
          if(fieldRegistrationFrom[0].selectFormat === SelectFormat.CheckboxRadio){
            this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].isChecked = [];
            this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].selectedOptionForGuestIndex.forEach(element => {
              this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].isChecked[element] = true;
            });
          }
        });
      });

      this.invitationAttendees.guests.forEach((guest, guestIndex)=>{
      // Set selected options for guests
        guest.customFields?.forEach((customField)=>{
          customField.customFieldOptions.forEach((fieldOption)=>{

            if(!this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].selectedOptionForGuestIndex){
              this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].selectedOptionForGuestIndex = [];
            }
            if (!this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].selectedOptionForGuestIndex.includes(guestIndex)) {
              this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].selectedOptionForGuestIndex.push(guestIndex);
            }

            const fieldRegistrationFrom = this.fieldsForRegistrationForm.filter(field=>field.id === customField.id);
            if(fieldRegistrationFrom[0].selectFormat === SelectFormat.CheckboxRadio){
              this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].isChecked = [];
              this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].selectedOptionForGuestIndex.forEach(element => {
                this.customFieldOptionsMultiVenue[customField.id][fieldOption.id].isChecked[element] = true;
              });
            }
          });
        });
      });
  }

  private getCustomFieldOptionsItems(field, guestId: string){
    if (guestId === 'mainInvitee') {
        return this.getOptionsVisibleToMainInvitee(this.customFieldOptionsItems[guestId]?.[field.id]);
    } else {
        return this.getOptionsVisibleToGuests(this.customFieldOptionsItems[guestId]?.[field.id]);
    }
  }

  private refreshFieldOptions() {
    this.fieldsForRegistrationForm.forEach(f => {
      if(f.id && f.customFieldOptions.length > 0){
        this.customFieldOptionsItems['mainInvitee'][f.id] = this.refreshCustomFieldOptions(f, f.customFieldOptions, 0);
      }
    });
    this.registrationForm.value.guests.forEach((guest, guestIndex)=>{
      this.fieldsForRegistrationForm.forEach(f => {
        if(f.id && f.customFieldOptions.length > 0){
          this.customFieldOptionsItems[guest.guestId || guest.id][f.id] = this.refreshCustomFieldOptions(f, f.customFieldOptions, guestIndex + 1);
        }
      });
    });
  }

  public refreshFieldOptionsMultiVenue(){
    // Routed questions refresh mainInvitee
    this.fieldsForRegistrationForm.forEach((field)=>{
      const { isCustomFieldRouted } = this.customFieldHasRouting(field);
      if(isCustomFieldRouted && field.id && field.customFieldOptions.length > 0){
        this.customFieldOptionsItems['mainInvitee'][field.id] = this.refreshCustomFieldOptionsMultiVenuePerAttendee(field, 'mainInvitee');
      }
    });
    // Routed questions refresh additional guests
    this.registrationForm.value.guests.forEach((guest)=>{
      this.fieldsForRegistrationForm.forEach((field)=>{
        const { isCustomFieldRouted } = this.customFieldHasRouting(field);
        if(isCustomFieldRouted && field.id && field.customFieldOptions.length > 0){
          this.customFieldOptionsItems[guest.guestId || guest.id][field.id] = this.refreshCustomFieldOptionsMultiVenuePerAttendee(field, guest.guestId || guest.id);
        }
      });
    });
    // Refresh NOT routed questions
    this.registrationForm.value.guests.forEach((guest)=>{
      this.fieldsForRegistrationForm.forEach((field)=>{
        const { isCustomFieldRouted } = this.customFieldHasRouting(field);
        if(!isCustomFieldRouted && field.id && field.customFieldOptions.length > 0){
          this.customFieldOptionsItems[guest.guestId || guest.id][field.id] = this.refreshCustomFieldOptionsMultiVenuePerAttendee(field, guest.guestId || guest.id);
        }
      });
    });
  }

  private refreshCustomFieldOptionsMultiVenuePerAttendee(field, guestId?: string){
    const data = [];
    let optionDisabled;
    const fieldOptions = field.customFieldOptions;
    const { isCustomFieldRouted, locationDateTimeIdsRouted } = this.customFieldHasRouting(field);
    if(isCustomFieldRouted){
      if(this.customFieldOptionsItems[guestId]){
        this.customFieldOptionsItems[guestId][field.id].forEach(option => {
          const filteredOption = fieldOptions.filter(fieldOption => fieldOption.id === option.id);
          const currentGuest = this.multiVenueSelectionPerAttendee[guestId];
          if(currentGuest){
            const routedSelectedLocationDateTimeId = currentGuest.filter((locationDateTimeId) =>
                                          locationDateTimeIdsRouted.includes(locationDateTimeId));
            optionDisabled = this.hasMaxEntries(filteredOption[0].maxEntries) ?
              routedSelectedLocationDateTimeId.some(locationDateTimeId => filteredOption[0].entered[locationDateTimeId] >= filteredOption[0].maxEntries) : false;
          }

          const isMainInvitee = (guestId === 'mainInvitee');
          const guestForm = isMainInvitee ? this.registrationForm.value[field.id] : this.registrationForm.value.guests?.find(guest => guest.guestId || guest.id === guestId)?.[field.id];

          if (guestForm && filteredOption.length > 0) {
            const selectedOption = guestForm.includes(filteredOption[0].id);
            if (selectedOption) {
              optionDisabled = false;
            }
          }

         const optionIndex = fieldOptions.findIndex(opt => opt.id === option.id);
         let text = this.getCustomFieldOptionValue(field, optionIndex);
          if (optionDisabled) {
            text += ' ' + this.translation.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.OPTION.MAXENTRIES.FULL.LABEL');
          }
          if(!filteredOption[0].parentId && this.hasParentOptionChild(field, filteredOption[0])){
            optionDisabled = true;
          }
          data.push(
            new DropdownVenueModelSegments(option.id, text, optionDisabled, option.isVisibleToMainInvitee, option.isVisibleToGuests)
          );
        });
      }
    }else{
      const sortedFieldOptions = this.sortOptionsByParent(fieldOptions);
      sortedFieldOptions.forEach(option => {
        optionDisabled = this.maxEntriesReached(option, this.customFieldOptionsMultiVenue[field.id][option.id].entered);;


        const isMainInvitee = (guestId === 'mainInvitee');
        const guestForm = isMainInvitee ? this.registrationForm.value[field.id] : this.registrationForm.value.guests?.find(guest => guest.guestId || guest.id === guestId)?.[field.id];

        if (guestForm) {
          const selectedOption = guestForm.includes(option.id);
          if (selectedOption) {
            optionDisabled = false;
          }
        }

        const optionIndex = fieldOptions.findIndex(opt => opt.id === option.id);
        let text = this.getCustomFieldOptionValue(field, optionIndex);
        if (optionDisabled) {
          text += ' ' + this.translation.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.OPTION.MAXENTRIES.FULL.LABEL');
        }
        if(!option.parentId && this.hasParentOptionChild(field, option)){
          optionDisabled = true;
        }
        data.push(
          new DropdownVenueModelSegments(option.id, text, optionDisabled, option.isVisibleToMainInvitee, option.isVisibleToGuests)
        );
      });
    }
    return data;
  }

  private refreshCustomFieldOptions(field: any, options: any, guestIndex): DropdownVenueModelSegments[] {
    const data = [];
    const sortedOptions = this.sortOptionsByParent(options);
    sortedOptions?.forEach((option, i) => {
      let optionDisabled;
      let totalSumOptionEntered = 0;
      Object.values(option.entered).forEach((value: number) => {
        totalSumOptionEntered += value;
      });
      const { isCustomFieldRouted, locationDateTimeIdsRouted } = this.customFieldHasRouting(field);
      if(!this.isMultiVenue || (this.isMultiVenue && this.multiVenueType === MultiVenueType.Registration && this.maxVenues === 1)){
        if(isCustomFieldRouted){
          optionDisabled = this.hasMaxEntries(option.maxEntries) ?
            option.entered[this.locationDateTimeId] >= option.maxEntries && !this.isOptionSelected(this.multiDisabledOptions[guestIndex][this.locationDateTimeId][field.id], option.id) : false;
        }else{
          optionDisabled = this.hasMaxEntries(option.maxEntries) ?
            totalSumOptionEntered >= option.maxEntries && !this.isOptionSelected(this.multiDisabledOptions[guestIndex][this.locationDateTimeId][field.id], option.id) : false;
        }
      }else{
        if(isCustomFieldRouted){
          if(this.multiVenueType === MultiVenueType.Registration){
            const routedSelectedLocationDateTimeId = this.locationDateTimeIdsPerRegistration.filter((locationDateTimeId) =>
              locationDateTimeIdsRouted.includes(locationDateTimeId)
            );
            optionDisabled = this.hasMaxEntries(option.maxEntries) ?
              routedSelectedLocationDateTimeId.some(locationDateTimeId => option.entered[locationDateTimeId] >= option.maxEntries) : false;
          }
        }else{
          if(this.customFieldOptionsMultiVenue[field.id]){
            optionDisabled = this.maxEntriesReached(option, this.customFieldOptionsMultiVenue[field.id][option.id].entered);
          }
        }

        if(guestIndex === 0){
          if (this.registrationForm.value[field.id]?.includes(option.id)) {
            optionDisabled = false;
          }
        }else{
          const currentGuest = this.registrationForm.value.guests.filter((guest, index) => index === guestIndex - 1);
          if(currentGuest[0][field.id]?.includes(option.id)){
            optionDisabled = false;
          }
        }
      }
      const optionIndex = options.findIndex(opt => opt.id === option.id);
      let text = this.getCustomFieldOptionValue(field, optionIndex);
      if (optionDisabled) {
        text += ' ' + this.translation.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.OPTION.MAXENTRIES.FULL.LABEL');
      }
      if(!option.parentId && this.hasParentOptionChild(field, option)){
        optionDisabled = true;
      }
      data.push(
        new DropdownVenueModelSegments(option.id, text, optionDisabled, option.isVisibleToMainInvitee, option.isVisibleToGuests)
      );
    });
    return data;
  }

  private getCustomFieldOptions(field: any, options: any): DropdownVenueModelSegments[] {
    const dataMap = new Map<number, DropdownVenueModelSegments>();
    const parentChildMap: { [key: number]: any[] } = {};

    options.forEach(option => {
      if (option.parentId) {
        if (!parentChildMap[option.parentId]) {
          parentChildMap[option.parentId] = [];
        }
        parentChildMap[option.parentId].push(option);
      } else {
        if (!parentChildMap[option.id]) {
          parentChildMap[option.id] = [];
        }
        parentChildMap[option.id].unshift(option);
      }
      let totalSumOptionEntered = 0;

      Object.values(option.entered).forEach((value: number) => {
        totalSumOptionEntered += value;
      });

      // Define customFieldOptionsMultiVenue
      this.customFieldOptionsMultiVenue[field.id] = this.customFieldOptionsMultiVenue[field.id] || {};
      this.customFieldOptionsMultiVenue[field.id][option.id] = this.customFieldOptionsMultiVenue[field.id][option.id] || {};
      this.customFieldOptionsMultiVenue[field.id][option.id].entered = totalSumOptionEntered;
      this.customFieldOptionsMultiVenue[field.id][option.id].maxEntries = option.maxEntries;
    });

    field.customFieldOptions.forEach(option => {
      const children = parentChildMap[option.id] || [];

      const isParent = children.length > 1;
      const isFull = this.hasMaxEntries(option.maxEntries) ?
        option.entered[this.locationDateTimeId] === option.maxEntries && !this.isOptionSelected(this.multiDisabledOptions[0][this.locationDateTimeId][field.id], option.id) : false;
      const optionDisabled = isParent || isFull;

      const optionIndex = options.findIndex(opt => opt.id === option.id);

      const isVisibleToMainInvitee = option.isVisibleToMainInvitee;
      const isVisibleToGuests = option.isVisibleToGuests;

      let text = this.getCustomFieldOptionValue(field, optionIndex);

      if (isFull) {
        text += ' ' + this.translation.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.OPTION.MAXENTRIES.FULL.LABEL');
      }

      dataMap.set(option.id, new DropdownVenueModelSegments(option.id, text, optionDisabled, isVisibleToMainInvitee, isVisibleToGuests));

      if (isParent) {
        children.forEach(child => {
          if (child.id !== option.id) {
            const childFull = child.entered[this.locationDateTimeId] === child.maxEntries && child.maxEntries > 0;
            const childDisabled = childFull || (this.multiDisabledOptions[0][this.locationDateTimeId][field.id] &&
                !this.isOptionSelected(this.multiDisabledOptions[0][this.locationDateTimeId][field.id], child.id));

            const childIsVisibleToMainInvitee = option.isVisibleToMainInvitee;
            const childIsVisibleToGuests = option.isVisibleToGuests;

            let childText = this.getCustomFieldOptionValue(field, options.findIndex(opt => opt.id === child.id));
            if (childFull) {
              childText += ' ' + this.translation.instant('ROUTES.EVENTDETAIL.TIMELINE.REGISTRATIONPAGE.OPTION.MAXENTRIES.FULL.LABEL');
            }

            dataMap.set(child.id, new DropdownVenueModelSegments(child.id, childText, childDisabled, childIsVisibleToMainInvitee, childIsVisibleToGuests));
          }
        });
      }
    });
    return Array.from(dataMap.values());
  }

  private sortOptionsByParent(options) {
    const sortedOptions = [];
    const parentOptions = options.filter(option => option.parentId === null);

    parentOptions.forEach(parent => {
      sortedOptions.push(parent);

      const childOptions = options.filter(option => option.parentId === parent.id);
      sortedOptions.push(...childOptions);
    });

    return sortedOptions;
  }

  private hasParentOptionChild(f, option){
    if (option.parentId === null) {
      const field = this.fieldsForRegistrationForm.find((newField) => newField.id === f.id);

      if (field && field.customFieldOptions && field.customFieldOptions.length > 0) {
        const foundOption = field.customFieldOptions.find((customOption) => customOption.parentId === option.id);
        return foundOption ? true : false;
      }
    }
  }

  private customFieldHasRouting(field){
    const customFieldIndex = this.fieldsForRegistrationForm.findIndex(fieldForRegistrationForm =>
      fieldForRegistrationForm?.id === field.id
    );
    const locationDateTimeIdsRouted: string[] = [];
    this.locationDateTimeField.optionsGoToQuestion.some((optionGoToQuestions) => {
      if(optionGoToQuestions.goToQuestions.includes(customFieldIndex)){
        locationDateTimeIdsRouted.push(optionGoToQuestions.locationDateTimeId);
      }
    });
    const isCustomFieldRouted = locationDateTimeIdsRouted.length > 0;
    return { isCustomFieldRouted, locationDateTimeIdsRouted };
  }

  public hasMaxEntries(optionMaxEntries){
    if(optionMaxEntries || optionMaxEntries === 0){
      return true;
    }
    return false;
  }

  public maxEntriesReached(option, entered: number){
    return this.hasMaxEntries(option.maxEntries) ? entered >= option.maxEntries : false;
  }

  public validateForm(formGroup: UntypedFormGroup | UntypedFormArray) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
        return false;
      }

      if (control instanceof UntypedFormArray) {
        Object.keys(control.controls).forEach((index) => {
          if (control.get(index) instanceof UntypedFormControl) {
            control.markAsTouched({ onlySelf: true });
            return false;
          }
          this.validateForm(control.get(index) as UntypedFormGroup);
        });
      } else if (control instanceof UntypedFormGroup) {
        this.validateForm(control);
      }
    });
  }

  private initFileUpload(registrationPage: IInvitationRegistrationPage, attendeeDetails: IAttendeeDetails) {
    registrationPage.customFields.forEach(customField => {
      if (customField.show) {
        this.inviteeUploadInputs[customField.id] = new EventEmitter<UploadInput>();
      }
    });
    attendeeDetails?.customFields.forEach(f => {
      if (f.customFieldType === CustomFieldType.FileUpload) {
        this.inviteeExistingFiles[f.id] = f.customFieldFiles;
      }
    });
  }

  private initGuestFileUpload(guestId: string, guestIndex: number, registrationPage: IInvitationRegistrationPage, attendeeDetails: IAttendeeDetails) {
    registrationPage.customFields.forEach(customField => {
      if (customField.showGuest) {
        this.guestUploadInputs[guestId] = this.guestUploadInputs[guestId] || {};
        this.guestUploadInputs[guestId][customField.id] = new EventEmitter<UploadInput>();
      }
    });
    attendeeDetails?.guests[guestIndex]?.customFields.forEach(f => {
      if (f.customFieldType === CustomFieldType.FileUpload) {
        this.guestExistingFiles[guestId] = this.guestExistingFiles[guestId] || {};
        this.guestExistingFiles[guestId][f.id] = f.customFieldFiles;
      }
    });
  }

  private checkAllUploadingDone() {
    for (const fileArr of Object.values(this.inviteeFiles)) {
      for (const file of fileArr) {
        if (file.progress.status !== UploadStatus.Done && file.progress.status !== UploadStatus.Cancelled) {
          return;
        }
      }
    }

    for (const guest of Object.values(this.guestFiles)) {
      for (const fileArr of Object.values(guest)) {
        for (const file of fileArr) {
          if (file.progress.status !== UploadStatus.Done && file.progress.status !== UploadStatus.Cancelled) {
            return;
          }
        }
      }
    }

    this.uploading = false;
  }

  public onInviteeUploadOutput(output: UploadOutput, customField: ICustomField): void {
    if (output.type === 'allAddedToQueue') {
      this.uploading = true;
      const event = this.fileService.createNgxUploadEvent();
      this.inviteeUploadInputs[customField.id].emit(event);
    } else if (output.type === 'addedToQueue'  && typeof output.file !== 'undefined') { // add file to array when added
      if (output.file.size > MaxUploadedFileSizeRegistrationPage) {
        alert(this.translation.instant('UPLOAD.FILE.SIZE.ERROR'));
      } else {
        const files = this.inviteeFiles[customField.id];
        const existingFiles = this.inviteeExistingFiles[customField.id];

        // Don't add the file if a file with the same name has already been added in the "existing files".
        if (!existingFiles || existingFiles.filter(f => f.fileName === output.file.name).length === 0) {
          if (!files) {
            this.inviteeFiles[customField.id] = [output.file];
          } else {
            files.push(output.file);
          }
        }
      }
    } else if (output.type === 'rejected' && typeof output.file !== 'undefined') {
      alert(this.translation.instant('UPLOAD.FILE.TYPE.ERROR'));
    } else if (output.type === 'uploading' && typeof output.file !== 'undefined') {
      // update current data in files array for uploading file
      const files = this.inviteeFiles[customField.id];
      if (files) {
        const index = files.findIndex(file => typeof output.file !== 'undefined' && file.id === output.file.id);
        files[index] = output.file;
      }
    } else if (output.type === 'removed') {
      // remove file from array when removed
      this.inviteeFiles[customField.id] = this.inviteeFiles[customField.id]?.filter((file: UploadFile) => file !== output.file);
    } else if (output.type === 'dragOver') {
      this.inviteeDragOver[customField.id] = true;
    } else if (output.type === 'dragOut') {
      this.inviteeDragOver[customField.id] = false;
    } else if (output.type === 'drop') {
      this.inviteeDragOver[customField.id] = false;
    }

    this.checkAllUploadingDone();
    this.validateFileUpload();
  }

  public removeInviteeFile(id: string, customField: ICustomField): void {
    this.inviteeUploadInputs[customField.id].emit({ type: 'remove', id });
  }

  public removeExistingInviteeFile(file: ICustomFieldFiles, customField: ICustomField): void {
    if (this.inviteeExistingFiles[customField.id]) {
      this.inviteeExistingFiles[customField.id] = this.inviteeExistingFiles[customField.id].filter(f => f !== file);
    }
  }

  public onGuestUploadOutput(output: UploadOutput, guestId: string, customField: ICustomField): void {
    if (output.type === 'allAddedToQueue') {
      this.uploading = true;
      const event = this.fileService.createNgxUploadEvent();
      this.guestUploadInputs[guestId][customField.id].emit(event);
    } else if (output.type === 'addedToQueue'  && typeof output.file !== 'undefined') { // add file to array when added
      if (output.file.size > MaxUploadedFileSizeRegistrationPage) {
        alert(this.translation.instant('UPLOAD.FILE.SIZE.ERROR'));
      } else {
        this.guestFiles[guestId] = this.guestFiles[guestId] || {};
        const files = this.guestFiles[guestId][customField.id];
        this.guestExistingFiles[guestId] = this.guestExistingFiles[guestId] || {};
        const existingFiles = this.guestExistingFiles[guestId][customField.id];

        // Don't add the file if a file with the same name has already been added in the "existing files".
        if (!existingFiles || existingFiles.filter(f => f.fileName === output.file.name).length === 0) {
          if (!files) {
            this.guestFiles[guestId][customField.id] = [output.file];
          } else {
            files.push(output.file);
          }
        }
      }
    } else if (output.type === 'rejected' && typeof output.file !== 'undefined') {
      alert(this.translation.instant('UPLOAD.FILE.TYPE.ERROR'));
    } else if (output.type === 'uploading' && typeof output.file !== 'undefined') {
      // update current data in files array for uploading file
      const files = this.guestFiles[guestId][customField.id];
      if (files) {
        const index = files.findIndex(file => typeof output.file !== 'undefined' && file.id === output.file.id);
        files[index] = output.file;
      }
    } else if (output.type === 'removed') {
      // remove file from array when removed
      this.guestFiles[guestId][customField.id] = this.guestFiles[guestId][customField.id]?.filter((file: UploadFile) => file !== output.file);
    } else if (output.type === 'dragOver') {
      this.guestDragOver[guestId] = this.guestDragOver[guestId] ||  {};
      this.guestDragOver[guestId][customField.id] = true;
    } else if (output.type === 'dragOut') {
      this.guestDragOver[guestId] = this.guestDragOver[guestId] ||  {};
      this.guestDragOver[guestId][customField.id] = false;
    } else if (output.type === 'drop') {
      this.guestDragOver[guestId] = this.guestDragOver[guestId] ||  {};
      this.guestDragOver[guestId][customField.id] = false;
    }
    this.checkAllUploadingDone();
    this.validateFileUpload();
  }

  public removeGuestFile(guestId: string, id: string, customField: ICustomField): void {
    this.guestUploadInputs[guestId][customField.id].emit({ type: 'remove', id });
  }

  public removeExistingGuestFile(guestId: string, file: ICustomFieldFiles, customField: ICustomField): void {
    this.guestExistingFiles[guestId] = this.guestExistingFiles[guestId] || {};
    if (this.guestExistingFiles[guestId][customField.id]) {
      this.guestExistingFiles[guestId][customField.id] = this.guestExistingFiles[guestId][customField.id].filter(f => f !== file);
    }
  }

  public downloadFile(file: ICustomFieldFiles) {
    this.fileService.downloadFile(file.blobName);
  }

  private getOptionsVisibleToMainInvitee(options) {
    return options.filter(option => option.isVisibleToMainInvitee === true);
  }

  private getOptionsVisibleToGuests(options) {
    return options.filter(option => option.isVisibleToGuests === true);
  }

  public hasVisibleToMainInvitee(field): boolean {
    return field.customFieldOptions.some(option => option.isVisibleToMainInvitee === true);
  }
}
